import React, { startTransition, useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, useDeferredValue } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';
import bridgeService from '../../../services/bridgeService';
import { collectEngineersFromManagers } from '../../../utils/bridgeStaffDisplay';
import BridgeStaffReadonly from '../../../components/bridge/BridgeStaffReadonly';
import { getBridgeCoordinates } from '../../../components/bridge/BridgeMapPanel';
import sensorService from '../../../services/sensorService';
import resensysService from '../../../services/resensysService';
import videoService from '../../../services/videoService';
import Loading from '../../../components/common/Loading';
import LoaderSpinner from '../../../components/common/LoaderSpinner';
import AppSelect from '../../../components/common/AppSelect';
import AppMultiSelect from '../../../components/common/AppMultiSelect';
import Modal from '../../../components/common/Modal';
import ConfirmModal from '../../../components/common/ConfirmModal';
import VideoPlayerModal from '../../../components/video/VideoPlayerModal';
import { useSelector } from 'react-redux';
import useNotify from '../../../hooks/useNotify';
import { useAuth } from '../../../context/AuthContext';
import { filterBridgesByRole } from '../../../utils/bridgeAccess';
import { formatVideoRecordedAt, isEventVideo, isHealthVideo } from '../../../utils/videoDisplay';
import { isCriticalSeverity } from '../../../utils/thresholdAlerts';
import {
  diagnoseVideoFilters,
  logVideo,
  resolveVideoId,
  summarizeVideo,
} from '../../../utils/videoDebug';
import { selectLatestSensorDataBySensorId, selectSensorSocketConnected } from '../../../store/slices/sensorDataSlice';
import { formatTimestamp } from '../../monitoring/utils/formatSensor';
import { buildEventVideoAppearanceMap, buildSensorThresholdTimelines, eventVideoListLabel, eventVideoRowAppearance } from '../utils/videoRowSeverity';
import { saveAlertThresholds } from '../utils/alertThresholds';
import { useAcknowledgeThresholdOnChart } from '../utils/sensorThresholdStatus';
import AlertsVideoPanel from '../components/AlertsVideoPanel';
import {
  formatChartDayLabel,
  formatChartFullTime,
  formatChartHourLabel,
  formatChartSampleTime,
  getBridgeTimeZone,
  isTimestampInChartDateRange,
  toDateKeyInTimeZone,
  toUtcDate,
  zonedHour,
  zonedWallTimeToUtcMs,
} from '../utils/bridgeChartTime';
import {
  fillSingleSensorAccelGaps,
  getLatestBatteryReading,
  readingHasAccelAxes,
  readingMatchesSelectedSensors,
} from '../utils/sensorChartReadings';
import '../../dashboard/pages/DashboardPage.css';
import './SensorDataPage.css';

const formatBridgeCoordinatesDisplay = (bridge) => {
  const coords = getBridgeCoordinates(bridge);
  return coords ? `${coords.lat}, ${coords.lng}` : '—';
};

const EMPTY_SPEC_FORM = {
  axis: 'xAxis',
  normalU: '', normalL: '',
  elevU: '', elevL: '',
  sigU: '', sigL: '',
  baselineStart: '', baselineEnd: '',
};

const SPEC_AXIS_KEYS = ['xAxis', 'yAxis', 'zAxis'];
const SPEC_AXIS_LABELS = { xAxis: 'X', yAxis: 'Y', zAxis: 'Z' };

const DEFAULT_BASELINE_SCRIPT = `# Baseline calculation script
# Adjust thresholds from historical sensor data

def calculate_baseline(readings):
    mean = sum(readings) / len(readings)
    return mean
`;

const buildSpecDraftFromSensor = (targetSensor) => {
  const draft = {};
  SPEC_AXIS_KEYS.forEach((axis) => {
    const lim = targetSensor?.specLimits?.[axis] || {};
    draft[axis] = {
      normalL: lim.normal?.lower ?? '',
      normalU: lim.normal?.upper ?? '',
      elevL: lim.elevated?.lower ?? '',
      elevU: lim.elevated?.upper ?? '',
      sigL: lim.significant?.lower ?? '',
      sigU: lim.significant?.upper ?? '',
    };
  });
  return draft;
};

/** YYYY-MM-DD (input value) → MM-DD-YYYY display. Native date inputs ignore lang for display. */
const formatIsoToMmDdYyyy = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, m, d] = iso.split('-');
  return `${m}-${d}-${y}`;
};

/** Live-data / baseline dates: shows MM-DD-YYYY while keeping native calendar picker + YYYY-MM-DD value. */
function LiveDataDateInput({ value, onChange, className = '', title }) {
  const display = formatIsoToMmDdYyyy(value);
  return (
    <span className={`live-data-date-input${className ? ` ${className}` : ''}`}>
      <span className="live-data-date-input__text" aria-hidden="true">{display}</span>
      <input
        type="date"
        className="live-data-date-input__native"
        value={value}
        onChange={onChange}
        title={title || display}
        aria-label={title || display}
      />
    </span>
  );
}

const LOG_DATE_LIMIT = 5;
const SENSOR_DATA_FETCH_LIMIT = 5000;

/** Let a queued dropdown press run before the next heavy data update. */
const yieldToMain = () => new Promise((resolve) => {
  window.setTimeout(resolve, 0);
});

/** Stable local calendar key (YYYY-MM-DD) for log grouping. */
const getLogDateKey = (timestamp) => {
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Log list / table label: MM-DD-YYYY */
const formatLogDateLabel = (dateKey) => formatIsoToMmDdYyyy(dateKey) || dateKey;

const emptySpecDraftRow = () => ({
  normalL: '', normalU: '', elevL: '', elevU: '', sigL: '', sigU: '',
});

const THRESHOLD_DRAFT_KEYS = ['normalL', 'normalU', 'elevL', 'elevU', 'sigL', 'sigU'];
const NORMAL_POINT_MIN = 100;

const isFilledThresholdNumber = (value) => {
  if (value === '' || value == null) return false;
  if (typeof value === 'string' && !String(value).trim()) return false;
  return Number.isFinite(Number(value));
};

const isThresholdRowEmpty = (row) =>
  !row || THRESHOLD_DRAFT_KEYS.every((key) => !isFilledThresholdNumber(row[key]));

const isThresholdRowComplete = (row) =>
  Boolean(row) && THRESHOLD_DRAFT_KEYS.every((key) => isFilledThresholdNumber(row[key]));

const thresholdRowFromForm = (form = {}) => ({
  normalL: form.normalL,
  normalU: form.normalU,
  elevL: form.elevL,
  elevU: form.elevU,
  sigL: form.sigL,
  sigU: form.sigU,
});

/** The saved Normal data point is the same on X, Y, and Z. Elevated and Significant stay per axis. */
const draftWithSharedNormal = (draft, form) => {
  const formRow = thresholdRowFromForm(form);
  const axisKey = SPEC_AXIS_KEYS.includes(form?.axis) ? form.axis : 'xAxis';
  const next = {
    ...(draft || {}),
    [axisKey]: formRow,
  };
  SPEC_AXIS_KEYS.forEach((key) => {
    const row = { ...(next[key] || emptySpecDraftRow()) };
    next[key] = {
      ...row,
      normalL: formRow.normalL,
      normalU: formRow.normalU,
    };
  });
  return next;
};

const parseThresholdPoint = (value) => {
  if (!isFilledThresholdNumber(value)) return null;
  return Number(value);
};

const DATA_POINT_BANDS = [
  { label: 'Normal', key: 'normalU' },
  { label: 'Elevated', key: 'elevU' },
  { label: 'Significant', key: 'sigU' },
];

/**
 * Normal is required and must be 100 or above.
 * Elevated and Significant may stay blank only when they were never set.
 * A data point that already has a value can be edited, but not cleared and saved.
 */
const assertThresholdDraftSavable = (draft, formAxis = 'xAxis', existingSources = []) => {
  const axisKey = SPEC_AXIS_KEYS.includes(formAxis) ? formAxis : 'xAxis';
  const row = draft?.[axisKey];
  const axisLabel = `${SPEC_AXIS_LABELS[axisKey]}-Axis`;
  const normal = parseThresholdPoint(row?.normalU);
  const elevated = parseThresholdPoint(row?.elevU);
  const significant = parseThresholdPoint(row?.sigU);

  if (normal == null) {
    throw new Error(`Normal data point is required on ${axisLabel}.`);
  }
  if (normal < NORMAL_POINT_MIN) {
    throw new Error('Values below 100 are not allowed.');
  }
  if (elevated != null && elevated <= normal) {
    throw new Error(`Use a higher value than Normal for Elevated on ${axisLabel}.`);
  }
  const significantFloor = elevated != null ? elevated : normal;
  if (significant != null && significant <= significantFloor) {
    throw new Error(
      elevated != null
        ? `Use a higher value than Elevated for Significant on ${axisLabel}.`
        : `Use a higher value than Normal for Significant on ${axisLabel}.`
    );
  }

  const sources = Array.isArray(existingSources) ? existingSources : [existingSources];
  const axisOrder = [
    axisKey,
    ...SPEC_AXIS_KEYS.filter((key) => key !== axisKey),
  ];
  axisOrder.forEach((key) => {
    const label = `${SPEC_AXIS_LABELS[key]}-Axis`;
    DATA_POINT_BANDS.forEach(({ label: bandLabel, key: bandKey }) => {
      const hadValue = sources.some((source) => isFilledThresholdNumber(source?.[key]?.[bandKey]));
      if (hadValue && !isFilledThresholdNumber(draft?.[key]?.[bandKey])) {
        throw new Error(`${bandLabel} data point on ${label} cannot be left empty.`);
      }
    });
  });
};

/** Battery health % = (voltage from vendor API / user limit) * 100 */
const getBatteryHealthPercent = (voltage, limit) => {
  if (voltage == null || !Number.isFinite(Number(voltage))) return null;
  const lim = Number(limit);
  if (!Number.isFinite(lim) || lim <= 0) return null;
  return (Number(voltage) / lim) * 100;
};

const pickBatteryVoltage = (payload) => {
  if (payload == null) return null;
  const raw = payload.batteryVoltage
    ?? payload.battery?.batteryVoltage
    ?? payload.battery?.voltage
    ?? payload.data?.batteryVoltage;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * Arc color from battery health as a share of the limit.
 * Change these percentages to move every band. Critical is below the significant percent.
 * A reading on a line stays in the higher band.
 * 4 V example: normal 4.00–3.00, elevated down to 2.00, significant down to 1.50, critical below 1.50.
 */
const BATTERY_NORMAL_PERCENT = 75;
const BATTERY_ELEVATED_PERCENT = 50;
const BATTERY_SIGNIFICANT_PERCENT = 37.5;

const getBatteryHealthColor = (pct) => {
  if (pct == null || Number.isNaN(pct)) return '#e0e0e0';
  if (pct >= BATTERY_NORMAL_PERCENT) return '#2a9a2a';
  if (pct >= BATTERY_ELEVATED_PERCENT) return '#e0c020';
  if (pct >= BATTERY_SIGNIFICANT_PERCENT) return '#e07020';
  return '#e03030';
};

const readPositiveVolts = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
};

const SENSOR_BATTERY_LIMIT_VOLTS = 3;
const LOGGER_BATTERY_LIMIT_VOLTS = 4;
const BATTERY_NORMAL_RATIO = BATTERY_NORMAL_PERCENT / 100;
const BATTERY_ELEVATED_RATIO = BATTERY_ELEVATED_PERCENT / 100;
const BATTERY_SIGNIFICANT_RATIO = BATTERY_SIGNIFICANT_PERCENT / 100;

const formatBandVolts = (value) => (Math.round(value * 100) / 100).toFixed(2);

const percentBatteryBands = (limit) => ({
  normal: formatBandVolts(limit * BATTERY_NORMAL_RATIO),
  elevated: formatBandVolts(limit * BATTERY_ELEVATED_RATIO),
  significant: formatBandVolts(limit * BATTERY_SIGNIFICANT_RATIO),
});

const defaultBatteryLimitVolts = (isLogger, sensor) => {
  if (isLogger) return LOGGER_BATTERY_LIMIT_VOLTS;
  const type = String(sensor?.sensorType || '').toLowerCase();
  if (type.includes('accelerometer') || type.includes('tilt')) return SENSOR_BATTERY_LIMIT_VOLTS;
  return null;
};

/** User voltages replace the percent bands only when normal, elevated, and significant are saved and ordered. */
const readCustomBatteryVolts = (source) => {
  const normal = readPositiveVolts(source?.batteryNormalVolts);
  const elevated = readPositiveVolts(source?.batteryElevatedVolts);
  const significant = readPositiveVolts(source?.batterySignificantVolts);
  if (normal == null || elevated == null || significant == null) return null;
  if (!(significant < elevated && elevated < normal)) return null;
  return { normal, elevated, significant };
};

const getBatteryArcColor = (voltage, limit, source) => {
  const custom = readCustomBatteryVolts(source);
  if (!custom) return getBatteryHealthColor(getBatteryHealthPercent(voltage, limit));
  if (voltage == null || !Number.isFinite(Number(voltage))) return '#e0e0e0';
  const volts = Number(voltage);
  if (volts < custom.significant) return '#e03030';
  if (volts < custom.elevated) return '#e07020';
  if (volts < custom.normal) return '#e0c020';
  return '#2a9a2a';
};

/** Live reading keyed the same way as a sensor: Mongo id or sensorSerialNo. */
const getLiveReadingBySerial = (latestByKey, serial, sensorId) => {
  if (!latestByKey) return null;
  if (sensorId && latestByKey[String(sensorId)]) return latestByKey[String(sensorId)];
  if (serial && latestByKey[serial]) return latestByKey[serial];
  if (!serial) return null;
  return Object.values(latestByKey).find((row) => String(row?.sensorSerialNo || '') === String(serial)) || null;
};

/** Battery Voltage card: vendor voltage + user limit health arc */
function BatteryVoltageCard({
  multiSel,
  sensor,
  dataLogger,
  dataLoggerSerial,
  loggerSensor,
  sensorData,
  selectedChartSensorIds,
  selectedSensorSerials,
  liveReading,
  latestByKey,
  bridgeId,
  onLimitSaved,
  showToast,
}) {
  const [limitOpen, setLimitOpen] = useState(false);
  const [limitInput, setLimitInput] = useState('');
  const [normalInput, setNormalInput] = useState('');
  const [elevatedInput, setElevatedInput] = useState('');
  const [significantInput, setSignificantInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [batterySource, setBatterySource] = useState('did');
  const [didApiVoltage, setDidApiVoltage] = useState(null);
  const [sidApiVoltage, setSidApiVoltage] = useState(null);
  const [didLoading, setDidLoading] = useState(false);
  const [sidLoading, setSidLoading] = useState(false);

  const didLabel = sensor?.sensorSerialNo ? `DID-${sensor.sensorSerialNo}` : '';
  const sidLabel = dataLoggerSerial ? `SID-${dataLoggerSerial}` : '';
  const isLoggerSource = batterySource === 'sid';
  const activeSensor = isLoggerSource ? (dataLogger || loggerSensor) : sensor;
  const canSetLimit = isLoggerSource
    ? Boolean(bridgeId && dataLoggerSerial)
    : Boolean(!multiSel && bridgeId && sensor);

  useEffect(() => {
    setBatterySource('did');
  }, [sensor?._id, dataLogger?._id, dataLoggerSerial]);

  const fillBatteryLimitFields = (source, isLogger) => {
    const savedLimit = readPositiveVolts(source?.batteryVoltageLimit);
    const limitValue = savedLimit ?? defaultBatteryLimitVolts(isLogger, source);
    const custom = readCustomBatteryVolts(source);
    setLimitInput(limitValue != null ? String(limitValue) : '');
    if (custom) {
      setNormalInput(formatBandVolts(custom.normal));
      setElevatedInput(formatBandVolts(custom.elevated));
      setSignificantInput(formatBandVolts(custom.significant));
      return;
    }
    if (limitValue != null) {
      const bands = percentBatteryBands(limitValue);
      setNormalInput(bands.normal);
      setElevatedInput(bands.elevated);
      setSignificantInput(bands.significant);
      return;
    }
    setNormalInput('');
    setElevatedInput('');
    setSignificantInput('');
  };

  useEffect(() => {
    setLimitOpen(false);
    fillBatteryLimitFields(activeSensor, isLoggerSource);
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    batterySource,
    activeSensor?._id,
    activeSensor?.batteryVoltageLimit,
    activeSensor?.batteryNormalVolts,
    activeSensor?.batteryElevatedVolts,
    activeSensor?.batterySignificantVolts,
    activeSensor?.sensorType,
    isLoggerSource,
  ]);

  useEffect(() => {
    let cancelled = false;
    const loggerSerial = String(dataLoggerSerial || '').trim();
    const sensorSerial = String(sensor?.sensorSerialNo || '').trim();

    if (!bridgeId || multiSel || !sensor?._id) {
      setDidLoading(false);
      return undefined;
    }

    const params = {};
    if (sensorSerial) params.did = sensorSerial;
    if (loggerSerial) params.sid = loggerSerial;

    setDidApiVoltage(null);
    setDidLoading(true);
    (async () => {
      try {
        const data = await resensysService.getBattery(bridgeId, sensor._id, params);
        if (!cancelled) setDidApiVoltage(pickBatteryVoltage(data));
      } catch {
        if (!cancelled) setDidApiVoltage(null);
      } finally {
        if (!cancelled) setDidLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    bridgeId,
    multiSel,
    sensor?._id,
    sensor?.sensorSerialNo,
    dataLoggerSerial,
  ]);

  // Start the logger read with the page, not when the dropdown changes to SID.
  useEffect(() => {
    let cancelled = false;
    const loggerSerial = String(dataLoggerSerial || '').trim();
    const fetchId = loggerSensor?._id || loggerSerial;

    if (!bridgeId || !fetchId || !loggerSerial) {
      setSidLoading(false);
      return undefined;
    }

    setSidApiVoltage(null);
    setSidLoading(true);
    const readSidVoltage = async () => {
      try {
        const data = await resensysService.getBattery(bridgeId, fetchId, {
          did: loggerSerial,
          sid: loggerSerial,
        });
        return pickBatteryVoltage(data);
      } catch {
        return null;
      }
    };
    (async () => {
      let voltage = await readSidVoltage();
      if (!cancelled && voltage == null) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        if (!cancelled) voltage = await readSidVoltage();
      }
      if (!cancelled) {
        setSidApiVoltage(voltage);
        setSidLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    bridgeId,
    loggerSensor?._id,
    dataLoggerSerial,
  ]);

  const selectSource = (next) => {
    if (next === batterySource) return;
    setBatterySource(next);
    setLimitOpen(false);
  };

  const saveLimit = async () => {
    const parsed = parseFloat(limitInput);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return showToast('Enter a valid battery voltage limit greater than 0.', 'error');
    }
    const normal = parseFloat(normalInput);
    const elevated = parseFloat(elevatedInput);
    const significant = parseFloat(significantInput);
    if (![normal, elevated, significant].every((value) => Number.isFinite(value) && value > 0)) {
      return showToast('Enter normal, elevated, and significant voltages greater than 0.', 'error');
    }
    if (!(significant < elevated && elevated < normal && normal < parsed)) {
      return showToast('Limit must be greater than normal, normal greater than elevated, and elevated greater than significant.', 'error');
    }
    const bands = {
      batteryNormalVolts: normal,
      batteryElevatedVolts: elevated,
      batterySignificantVolts: significant,
    };
    if (isLoggerSource) {
      if (!bridgeId || !dataLoggerSerial) {
        return showToast('Select a data logger first.', 'error');
      }
      setSaving(true);
      try {
        const loggerId = dataLogger?._id || dataLoggerSerial;
        await bridgeService.updateDataLoggerBatteryVoltageLimit(bridgeId, loggerId, {
          batteryVoltageLimit: parsed,
          ...bands,
        });
        onLimitSaved?.(loggerId, parsed, 'logger', dataLoggerSerial, bands);
        setLimitOpen(false);
        showToast('Battery voltage limits saved.');
      } catch (e) {
        showToast(e.message || 'Failed to save battery voltage limit.', 'error');
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!bridgeId || !sensor) {
      return showToast('Select a single sensor first.', 'error');
    }
    setSaving(true);
    try {
      await bridgeService.updateBatteryVoltageLimit(bridgeId, sensor._id, {
        batteryVoltageLimit: parsed,
        ...bands,
      });
      onLimitSaved?.(sensor._id, parsed, undefined, undefined, bands);
      setLimitOpen(false);
      showToast('Battery voltage limits saved.');
    } catch (e) {
      showToast(e.message || 'Failed to save battery voltage limit.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const loggerLive = getLiveReadingBySerial(latestByKey, dataLoggerSerial, loggerSensor?._id);
  const last = isLoggerSource
    ? getLatestBatteryReading(
      sensorData,
      loggerSensor?._id ? [String(loggerSensor._id)] : [],
      dataLoggerSerial ? [dataLoggerSerial] : [],
      loggerLive,
    )
    : (!multiSel
      ? getLatestBatteryReading(
        sensorData,
        selectedChartSensorIds,
        selectedSensorSerials,
        liveReading,
      )
      : null);
  const voltage = (isLoggerSource ? sidApiVoltage : didApiVoltage) ?? last?.batteryVoltage;
  const savedLimit = readPositiveVolts(activeSensor?.batteryVoltageLimit);
  const limit = savedLimit ?? defaultBatteryLimitVolts(isLoggerSource, activeSensor);
  const healthPct = getBatteryHealthPercent(voltage, limit);
  const arcFrac = healthPct != null ? Math.min(1, Math.max(0, healthPct / 100)) : 0;
  const arcColor = getBatteryArcColor(voltage, limit, activeSensor);
  const showMultiple = multiSel && !isLoggerSource;
  // Show a number already in hand. Spinner only while that source has no voltage yet.
  const showVoltageSpinner = (isLoggerSource ? sidLoading : didLoading) && voltage == null;
  // Same semicircle path for track + value; dasharray reveals the filled portion.
  const batteryArcPath = 'M10,80 A55,55 0 0,1 120,80';
  const batteryArcLength = Math.PI * 55;

  return (
    <div className="card sensor-battery-card" style={{ marginBottom: 0, textAlign: 'center' }}>
      <div className="sensor-battery-header">
        <div className="sensor-battery-title-row">
          <div style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>Battery Voltage</div>
          {(didLabel || sidLabel) && (
            <AppSelect
              className="sensor-battery-source-select"
              aria-label="Battery source"
              value={isLoggerSource && sidLabel ? 'sid' : 'did'}
              onChange={(value) => selectSource(value)}
              options={[
                ...(didLabel ? [{ value: 'did', label: didLabel }] : []),
                ...(sidLabel ? [{ value: 'sid', label: sidLabel }] : []),
              ]}
            />
          )}
        </div>
        {canSetLimit && (
          <button
            type="button"
            className="btn btn-secondary btn-sm sensor-battery-set-limit-btn"
            onClick={() => {
              fillBatteryLimitFields(activeSensor, isLoggerSource);
              setLimitOpen((open) => !open);
            }}
            disabled={saving}
          >
            Set limit
          </button>
        )}
      </div>
      {showMultiple ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#666', fontSize: 13, fontWeight: 600 }}>Multiple</div>
      ) : (
        <>
          <svg width="130" height="90" viewBox="0 0 130 90">
            <path
              d={batteryArcPath}
              fill="none"
              stroke="#e0e0e0"
              strokeWidth="14"
              strokeLinecap="round"
            />
            {healthPct != null && arcFrac > 0 && (
              <path
                d={batteryArcPath}
                fill="none"
                stroke={arcColor}
                strokeWidth="14"
                strokeLinecap="round"
                strokeDasharray={`${batteryArcLength * arcFrac} ${batteryArcLength}`}
              />
            )}
            <text x="65" y="72" textAnchor="middle" fontSize="14" fontWeight="700" fill="#333">
              {voltage != null ? `${Number(voltage).toFixed(1)}V` : (showVoltageSpinner ? '' : '—')}
            </text>
            {showVoltageSpinner && (
              <foreignObject x="46" y="56" width="38" height="22">
                <div className="sensor-battery-voltage-loader" aria-label="Loading battery voltage">
                  <LoaderSpinner size={14} />
                </div>
              </foreignObject>
            )}
          </svg>
          {!limitOpen && limit != null && limit !== '' && (
            <div className="sensor-battery-limit-label">
              Limit: {Number(limit).toFixed(1)}V
              {healthPct != null ? ` · ${Math.round(healthPct)}%` : ''}
            </div>
          )}
          {limitOpen && (
            <div className="sensor-battery-limit-form">
              <div className="sensor-battery-limit-fields">
                <span className="sensor-battery-limit-name">Capacity :</span>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  inputMode="decimal"
                  className="sensor-battery-limit-input"
                  value={limitInput}
                  onChange={(e) => {
                    const next = e.target.value;
                    setLimitInput(next);
                    const nextLimit = parseFloat(next);
                    if (!Number.isFinite(nextLimit) || nextLimit <= 0) return;
                    const bands = percentBatteryBands(nextLimit);
                    setNormalInput(bands.normal);
                    setElevatedInput(bands.elevated);
                    setSignificantInput(bands.significant);
                  }}
                  aria-label="Battery voltage limit"
                  disabled={saving}
                />
                <span className="sensor-battery-limit-name">Elevated :</span>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  inputMode="decimal"
                  className="sensor-battery-limit-input"
                  value={normalInput}
                  onChange={(e) => setNormalInput(e.target.value)}
                  aria-label="Elevated voltage"
                  disabled={saving}
                />
                <span className="sensor-battery-limit-name">Significant :</span>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  inputMode="decimal"
                  className="sensor-battery-limit-input"
                  value={elevatedInput}
                  onChange={(e) => setElevatedInput(e.target.value)}
                  aria-label="Significant voltage"
                  disabled={saving}
                />
                <span className="sensor-battery-limit-name">Critical :</span>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  inputMode="decimal"
                  className="sensor-battery-limit-input"
                  value={significantInput}
                  onChange={(e) => setSignificantInput(e.target.value)}
                  aria-label="Critical voltage"
                  disabled={saving}
                />
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm sensor-battery-limit-save"
                onClick={saveLimit}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const mapLogEntryToField = (specLimitType, limitSide) => {
  const typeMap = { normal: 'normal', elevated: 'elev', significant: 'sig' };
  const prefix = typeMap[specLimitType];
  if (!prefix) return null;
  if (limitSide === 'lower') return `${prefix}L`;
  if (limitSide === 'upper') return `${prefix}U`;
  return null;
};

const buildLogSnapshotFromEntries = (entries) => {
  const draft = {};
  SPEC_AXIS_KEYS.forEach((axisKey) => {
    draft[axisKey] = emptySpecDraftRow();
  });
  [...entries]
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    .forEach((entry) => {
      const axis = entry.axis;
      const field = mapLogEntryToField(entry.specLimitType, entry.limitSide);
      if (!axis || !field || !draft[axis]) return;
      draft[axis][field] = entry.newValue ?? '';
    });
  return draft;
};

const round3 = (n) => Math.round(n * 1000) / 1000;

const mean = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0);

const stdDev = (values, avg) => {
  if (!values.length) return 0.1;
  const variance = values.reduce((s, v) => s + (v - avg) ** 2, 0) / values.length;
  return Math.sqrt(variance) || 0.1;
};

/** Thresholds are stored as deviation from baseline (not absolute readings). */
const computeAxisBaselineAndThresholds = (readings, axisKey) => {
  const values = readings.map((r) => r[axisKey]).filter((v) => v != null && !Number.isNaN(v));
  const baseline = mean(values);
  const sd = stdDev(values, baseline);
  return {
    baseline: round3(baseline),
    normalL: round3(-sd),
    normalU: round3(sd),
    elevL: round3(-sd * 1.4),
    elevU: round3(sd * 1.4),
    sigL: round3(-sd * 2),
    sigU: round3(sd * 2),
  };
};

const loadBaselineValuesFromSensor = (targetSensor) => ({
  xAxis: targetSensor?.specLimits?.baselineValues?.xAxis ?? '',
  yAxis: targetSensor?.specLimits?.baselineValues?.yAxis ?? '',
  zAxis: targetSensor?.specLimits?.baselineValues?.zAxis ?? '',
});

const HARDWARE_STATUS_LABELS = {
  live: 'Live',
  stale: 'Stale',
  offline: 'Offline',
  unknown: 'Offline',
};

/** Resensys SenSpots often report every few hours; treat a same-day / recent reading as live. */
const HARDWARE_LIVE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const readHardwareConnectionStatus = (payload) => {
  if (!payload || typeof payload !== 'object') return '';
  const nested = payload.data && typeof payload.data === 'object' ? payload.data : null;
  const raw =
    payload.connectionStatus
    ?? nested?.connectionStatus
    ?? payload.health?.connectionStatus
    ?? payload.deviceSummary?.connectionStatus
    ?? payload.result?.connectionStatus
    ?? (typeof payload.status === 'string' ? payload.status : '');
  return String(raw || '').toLowerCase();
};

const parseHardwareTimeMs = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
};

const resolveHardwareBadgeStatus = (payload) => {
  const latestMs = parseHardwareTimeMs(
    payload?.health?.latestTime
    || payload?.health?.latest_time
    || payload?.deviceSummary?.latestTime
    || payload?.latestTime
  );
  if (latestMs != null && (Date.now() - latestMs) <= HARDWARE_LIVE_MAX_AGE_MS) {
    return 'live';
  }
  const fromApi = readHardwareConnectionStatus(payload);
  if (fromApi === 'live' || fromApi === 'stale' || fromApi === 'offline' || fromApi === 'unknown') {
    return fromApi;
  }
  if (latestMs != null) return 'stale';
  return 'unknown';
};

const formatHardwareTimestamp = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
};

const sleepMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const EVENT_TRIGGER_THRESHOLD_RETRY_MS = 2000;

const today     = new Date().toISOString().split('T')[0];
const sevenAgo  = new Date(Date.now() - 7*24*60*60*1000).toISOString().split('T')[0];

const VIDEO_DELETE_ROLES = ['super_admin', 'bridge_admin'];
const VIDEO_PAGE_SIZE = 10;

const isAwsOrSyncNoise = (msg) =>
  /arn:aws|s3:|ListBucket|not authorized|syncWarning/i.test(msg || '');

function VideoListPagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  return (
    <div className="sensor-video-pagination">
      <button
        type="button"
        className="sensor-video-pagination-btn"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        Back
      </button>
      <span className="sensor-video-pagination-info">
        Page {currentPage} of {totalPages}
      </span>
      <button
        type="button"
        className="sensor-video-pagination-btn"
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next
      </button>
    </div>
  );
}

function paginateList(items, page, pageSize = VIDEO_PAGE_SIZE) {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const start = (currentPage - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    totalPages,
    currentPage,
  };
}

/** Hide extra Event/Health rows that share the same logger serial and recorded clock. */
function dedupeVideosByLoggerAndTime(videos) {
  const list = Array.isArray(videos) ? videos : [];
  const seen = new Set();
  return list.filter((video) => {
    const serial = String(video?.dataLoggerSerialNo || '').trim();
    const timeKey = formatVideoRecordedAt(video?.recordedAt);
    if (!serial || timeKey === '—') return true;
    const key = `${serial}|${timeKey}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
const NO_BRIDGE_MSG = 'Select a bridge to view sensor data';
const HEALTH_VIDEO_LOGGER_HINT = 'select the data logger first';
/** Resensys hardware status + single event-trigger (mg) parameter in settings. */
const SHOW_RESENSYS_HARDWARE_PANEL = true;

const DOWNLOAD_FORMATS = [
  { key: 'csv', label: 'CSV', format: 'csv', ext: 'csv' },
  { key: 'xl', label: 'XL', format: 'xlsx', ext: 'xlsx' },
  // HTML Word export must use .doc — .docx is a ZIP package and fails to open
  { key: 'word', label: 'Word', format: 'word', ext: 'doc' },
];

const THRESHOLD_LEVEL_ROWS = [
  { label: 'Normal', lowerKey: 'normalL', upperKey: 'normalU' },
  { label: 'Elevated', lowerKey: 'elevL', upperKey: 'elevU' },
  { label: 'Significant', lowerKey: 'sigL', upperKey: 'sigU' },
];

const THRESHOLD_SHEET_BY_AXIS = {
  xAxis: 'X-Axis',
  yAxis: 'Y-Axis',
  zAxis: 'Z-Axis',
};

const EXCEL_ACCEPT = '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

const cellToLimitValue = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : trimmed;
};

const buildThresholdSampleWorkbook = (draft = {}) => {
  const wb = XLSX.utils.book_new();
  SPEC_AXIS_KEYS.forEach((axisKey) => {
    const row = draft[axisKey] || emptySpecDraftRow();
    const aoa = [
      ['THRESHOLD (DEVIATION FROM BASELINE)'],
      ['Threshold', 'Lower', 'Upper'],
      ...THRESHOLD_LEVEL_ROWS.map((lvl) => {
        const lower = row[lvl.lowerKey];
        const upper = row[lvl.upperKey];
        return [
          lvl.label,
          lower === '' || lower == null ? '' : Number(lower),
          upper === '' || upper == null ? '' : Number(upper),
        ];
      }),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws, THRESHOLD_SHEET_BY_AXIS[axisKey]);
  });
  return wb;
};

const parseThresholdAxisSheet = (ws) => {
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  let headerIdx = -1;
  let lowerCol = -1;
  let upperCol = -1;
  let thrCol = -1;
  for (let i = 0; i < rows.length; i += 1) {
    const cells = (rows[i] || []).map((c) => String(c ?? '').trim().toLowerCase());
    thrCol = cells.findIndex((c) => c === 'threshold');
    lowerCol = cells.findIndex((c) => c === 'lower');
    upperCol = cells.findIndex((c) => c === 'upper');
    if (thrCol >= 0 && lowerCol >= 0 && upperCol >= 0) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx < 0) return null;
  const draft = emptySpecDraftRow();
  const labelToKeys = {
    normal: ['normalL', 'normalU'],
    elevated: ['elevL', 'elevU'],
    significant: ['sigL', 'sigU'],
  };
  for (let i = headerIdx + 1; i < rows.length; i += 1) {
    const row = rows[i] || [];
    const label = String(row[thrCol] ?? '').trim().toLowerCase();
    const keys = labelToKeys[label];
    if (!keys) continue;
    draft[keys[0]] = cellToLimitValue(row[lowerCol]);
    draft[keys[1]] = cellToLimitValue(row[upperCol]);
  }
  return draft;
};

const parseThresholdWorkbook = (arrayBuffer) => {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const draft = {};
  const missing = [];
  SPEC_AXIS_KEYS.forEach((axisKey) => {
    const expectedName = THRESHOLD_SHEET_BY_AXIS[axisKey];
    // Backend requires exact sheet names: X-Axis, Y-Axis, Z-Axis
    if (!wb.SheetNames.includes(expectedName)) {
      missing.push(expectedName);
      return;
    }
    const parsed = parseThresholdAxisSheet(wb.Sheets[expectedName]);
    if (!parsed) {
      missing.push(expectedName);
      return;
    }
    draft[axisKey] = parsed;
  });
  if (missing.length) {
    throw new Error(
      `Excel must contain sheets named exactly: X-Axis, Y-Axis, Z-Axis. Missing or incomplete: ${missing.join(', ')}`
    );
  }
  return draft;
};

const draftRowToSpecLimitsPayload = (row) => {
  const band = (upperRaw, lowerRaw) => {
    if (!isFilledThresholdNumber(upperRaw) || !isFilledThresholdNumber(lowerRaw)) return null;
    let upper = parseFloat(upperRaw);
    let lower = parseFloat(lowerRaw);
    if (upper < lower) {
      const swap = upper;
      upper = lower;
      lower = swap;
    }
    return { upper, lower };
  };
  const payload = {};
  const normal = band(row.normalU, row.normalL);
  if (normal) payload.normal = normal;
  const elevated = band(row.elevU, row.elevL);
  if (elevated) payload.elevated = elevated;
  const significant = band(row.sigU, row.sigL);
  if (significant) payload.significant = significant;
  return payload;
};

/** Keep the saved Normal (and any other bands just written) on the sensor the chart reads. */
const bridgeWithSavedThresholds = (bridgeDoc, sensorId, mergedDraft) => {
  if (!bridgeDoc?.sensors || !sensorId) return bridgeDoc;
  return {
    ...bridgeDoc,
    sensors: bridgeDoc.sensors.map((item) => {
      if (String(item._id) !== String(sensorId)) return item;
      const specLimits = { ...(item.specLimits || {}) };
      SPEC_AXIS_KEYS.forEach((axisKey) => {
        const payload = draftRowToSpecLimitsPayload(mergedDraft?.[axisKey] || {});
        if (!Object.keys(payload).length) return;
        const current = specLimits[axisKey];
        specLimits[axisKey] = {
          ...(current && typeof current === 'object' ? current : {}),
          ...payload,
        };
      });
      return { ...item, specLimits };
    }),
  };
};

const validateParsedThresholdDraft = (draft) => {
  SPEC_AXIS_KEYS.forEach((axisKey) => {
    const row = draft[axisKey];
    if (!row) throw new Error(`Missing thresholds for ${THRESHOLD_SHEET_BY_AXIS[axisKey]}.`);
    THRESHOLD_LEVEL_ROWS.forEach((lvl) => {
      const lower = Number(row[lvl.lowerKey]);
      const upper = Number(row[lvl.upperKey]);
      if (!Number.isFinite(lower) || !Number.isFinite(upper)) {
        throw new Error(
          `Fill numeric Lower and Upper for ${THRESHOLD_SHEET_BY_AXIS[axisKey]} / ${lvl.label}.`
        );
      }
      if (upper < lower) {
        throw new Error(
          `Upper must be >= Lower for ${THRESHOLD_SHEET_BY_AXIS[axisKey]} / ${lvl.label}.`
        );
      }
    });
  });
};

const EXPORT_AXIS_FIELDS = [
  { key: 'xAxis', header: 'X-Axis' },
  { key: 'yAxis', header: 'Y-Axis' },
  { key: 'zAxis', header: 'Z-Axis' },
];

const exportAxisFields = (axisKey) => (
  axisKey && axisKey !== 'all'
    ? EXPORT_AXIS_FIELDS.filter((axis) => axis.key === axisKey)
    : EXPORT_AXIS_FIELDS
);

const exportHeadersForAxis = (axisKey) => [
  'Timestamp',
  'Sensor Serial No',
  'Sensor Type',
  ...exportAxisFields(axisKey).map((axis) => axis.header),
  'Temperature',
  'Battery Voltage',
];

const escapeCsvCell = (value) => {
  const str = value == null ? '' : String(value);
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/** Same clock as the live-chart tooltip, including milliseconds. */
const exportTimestamp = (timestamp, timeZone) => {
  if (timeZone) {
    const label = formatChartSampleTime(timestamp, timeZone);
    if (label) return label;
  }
  return formatTimestamp(timestamp);
};

const exportRowCells = (reading, timeZone, axisKey) => [
  exportTimestamp(reading.timestamp, timeZone),
  reading.sensorSerialNo || '',
  reading.sensorType || '',
  ...exportAxisFields(axisKey).map((axis) => reading[axis.key] ?? ''),
  reading.temperature ?? '',
  reading.batteryVoltage ?? '',
];

const buildExportCsv = (rows, timeZone, axisKey) => {
  const lines = [
    exportHeadersForAxis(axisKey).map(escapeCsvCell).join(','),
    ...rows.map((row) => exportRowCells(row, timeZone, axisKey).map(escapeCsvCell).join(',')),
  ];
  return `\uFEFF${lines.join('\n')}`;
};

const buildExportXlsxBlob = (rows, timeZone, axisKey) => {
  const aoa = [
    exportHeadersForAxis(axisKey),
    ...rows.map((row) => exportRowCells(row, timeZone, axisKey)),
  ];
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  XLSX.utils.book_append_sheet(wb, ws, 'Sensor Data');
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

const buildExportWordHtml = (rows, timeZone, axisKey) => {
  const header = exportHeadersForAxis(axisKey).map((h) => `<th>${escapeHtml(h)}</th>`).join('');
  const body = rows
    .map((row) => `<tr>${exportRowCells(row, timeZone, axisKey).map((c) => `<td>${escapeHtml(c)}</td>`).join('')}</tr>`)
    .join('');
  // Word-compatible HTML (.doc). Do not use .docx — that format is a ZIP package.
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:w="urn:schemas-microsoft-com:office:word"
 xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<!--[if gte mso 9]><xml>
 <w:WordDocument>
  <w:View>Print</w:View>
  <w:Zoom>100</w:Zoom>
  <w:DoNotOptimizeForBrowser/>
 </w:WordDocument>
</xml><![endif]-->
<title>Sensor Data</title>
</head>
<body>
<table border="1" cellspacing="0" cellpadding="4">
<thead><tr>${header}</tr></thead>
<tbody>${body}</tbody>
</table>
</body>
</html>`;
};

const triggerFileDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const chartAxisValue = (value) =>
  (value != null && value !== '' && !Number.isNaN(Number(value)) ? Number(value) : undefined);

/** A burst sample is either a number or { value, timestamp } from the chart API. */
const axisSampleValue = (sample) =>
  chartAxisValue(sample && typeof sample === 'object' ? sample.value : sample);

const axisSampleTimestamp = (sample) => {
  const raw = sample && typeof sample === 'object' ? sample.timestamp : null;
  if (raw == null || raw === '') return null;
  const ts = new Date(raw);
  return Number.isNaN(ts.getTime()) ? null : ts.toISOString();
};

const normalizeAxisSample = (sample) => {
  const value = axisSampleValue(sample);
  if (value === undefined) return null;
  const timestamp = axisSampleTimestamp(sample);
  return timestamp ? { value, timestamp } : { value };
};

/** Distinct values stored for this minute. Repeated copies of the same value collapse. */
const samplesAroundFirstPoint = (samples) => {
  const normalized = (Array.isArray(samples) ? samples : [])
    .map((sample) => normalizeAxisSample(sample))
    .filter(Boolean);
  if (!normalized.length) return [];

  const seen = new Set();
  const unique = [];
  normalized.forEach((sample) => {
    const key = round3(sample.value);
    if (seen.has(key)) return;
    seen.add(key);
    unique.push({ ...sample, value: key });
  });
  return unique;
};

/** How many points are visible in the chart viewport (stock-chart window — pan to see the rest). */
const CHART_VIEWPORT_POINTS = 24;
/** Fixed empty slots on the right so the latest point sits inset from the edge (stock-chart style). */
const CHART_LIVE_RIGHT_PADDING_SLOTS = 4;
const CHART_MAX_EXTRA_RIGHT_SLOTS = 12;

const getChartWindowSize = (len) => {
  if (len <= 0) return 0;
  return Math.min(CHART_VIEWPORT_POINTS, len);
};

/** Live Data accel and temperature charts: show the full selected Start–End range, not only the last 24 points. */
const getLiveDataChartWindowSize = (len) => {
  if (len <= 0) return 0;
  return len;
};

const getChartLiveRightPadding = (pointCount) => {
  if (pointCount <= 0) return 0;
  return CHART_LIVE_RIGHT_PADDING_SLOTS;
};

const getWindowPointCount = (startIndex, endIndex) => Math.max(1, endIndex - startIndex + 1);

/** Past this many points, per-reading circles overlap and a spread of them can crash the page. */
const CHART_DOT_DETAIL_LIMIT = 240;

const minOf = (values) => {
  let min = Infinity;
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (value < min) min = value;
  }
  return min;
};

const maxOf = (values) => {
  let max = -Infinity;
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (value > max) max = value;
  }
  return max;
};

const chartAxisDot = (color, windowStart, windowEnd, liveTailIndex = null) => (props) => {
  const { cx, cy, payload, dataKey, index } = props;
  if (cx == null || cy == null || chartAxisValue(payload?.[dataKey]) === undefined) return null;
  const pointIndex = payload?.pointIndex ?? index ?? 0;
  const key = payload?.chartKey ?? `${dataKey}-${pointIndex}`;
  // Prefer per-series live tip index; fall back to isLiveTail mark, then window end (single-sensor).
  const isLast = liveTailIndex != null
    ? pointIndex === liveTailIndex
    : payload?.isLiveTail
      ? Boolean(payload.isLiveTail[dataKey])
      : pointIndex === windowEnd;
  const windowSpan = Math.max(0, windowEnd - windowStart);
  const ageRatio = windowSpan > 0 ? (windowEnd - pointIndex) / windowSpan : 0;
  const opacity = isLast ? 1 : 0.28 + (1 - ageRatio) * 0.32;

  if (isLast) {
    return (
      <g key={key}>
        <circle cx={cx} cy={cy} r={7} fill={color} opacity={0.18}>
          <animate attributeName="r" values="5;9;5" dur="1.35s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.18;0.42;0.18" dur="1.35s" repeatCount="indefinite" />
        </circle>
        <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={2} />
      </g>
    );
  }

  return (
    <circle
      key={key}
      cx={cx}
      cy={cy}
      r={3}
      fill={color}
      stroke={color}
      strokeWidth={1}
      opacity={opacity}
    />
  );
};

/** Pixels per data unit from the line's own points, so extra samples share the axis scale. */
const yPixelsPerUnit = (points) => {
  if (!Array.isArray(points)) return null;
  let low = null;
  let high = null;
  for (let i = 0; i < points.length; i += 1) {
    const value = Number(points[i]?.value);
    if (!Number.isFinite(value) || points[i].y == null) continue;
    if (!low || value < low.value) low = { value, y: points[i].y };
    if (!high || value > high.value) high = { value, y: points[i].y };
  }
  if (!low || !high || low.value === high.value) return null;
  const dy = high.y - low.y;
  if (dy === 0) return null;
  return dy / (high.value - low.value);
};

/**
 * Single-axis live chart: keep the connecting line, and stack every value from
 * that reading on a vertical stem.
 */
const chartAxisVerticalDot = (color, windowStart, windowEnd) => {
  let scalePoints = null;
  let scale = null;
  return (props) => {
  const { cx, cy, payload, dataKey, index, value, points } = props;
  const samples = samplesAroundFirstPoint(payload?.[`${dataKey}Samples`]);
  const plotted = samples
    .map((sample) => axisSampleValue(sample))
    .filter((sample) => sample !== undefined);
  if (plotted.length < 2) {
    return chartAxisDot(color, windowStart, windowEnd)(props);
  }

  if (cx == null || cy == null || chartAxisValue(payload?.[dataKey]) === undefined) return null;
  const primary = Number(value);
  if (points !== scalePoints) {
    scalePoints = points;
    scale = yPixelsPerUnit(points);
  }
  const yFor = (sample) => (
    scale == null || !Number.isFinite(primary) ? cy : cy + (sample - primary) * scale
  );
  const pointIndex = payload?.pointIndex ?? index ?? 0;
  const key = payload?.chartKey ?? `${dataKey}-${pointIndex}`;
  const isLast = pointIndex === windowEnd;
  const windowSpan = Math.max(0, windowEnd - windowStart);
  const ageRatio = windowSpan > 0 ? (windowEnd - pointIndex) / windowSpan : 0;
  const opacity = isLast ? 1 : 0.28 + (1 - ageRatio) * 0.32;
  const ys = plotted.map(yFor);

  return (
    <g key={key}>
      <line
        x1={cx}
        y1={minOf(ys)}
        x2={cx}
        y2={maxOf(ys)}
        stroke={color}
        strokeWidth={1.5}
        opacity={opacity}
      />
      {plotted.map((sample, sampleIndex) => {
        const sy = yFor(sample);
        const isPrimary = Number.isFinite(primary) && round3(primary) === round3(sample);
        // The stem already runs from the low sample to the high one. On a long
        // range the in-between circles sit on that stem and only slow painting.
        if ((points?.length || 0) > CHART_DOT_DETAIL_LIMIT && !isPrimary) return null;
        if (isLast && isPrimary) {
          return (
            <g key={`${key}-${sampleIndex}-${sample}`}>
              <circle cx={cx} cy={sy} r={7} fill={color} opacity={0.18}>
                <animate attributeName="r" values="5;9;5" dur="1.35s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.18;0.42;0.18" dur="1.35s" repeatCount="indefinite" />
              </circle>
              <circle cx={cx} cy={sy} r={5} fill={color} stroke="#fff" strokeWidth={2} />
            </g>
          );
        }
        return (
          <circle
            key={`${key}-${sampleIndex}-${sample}`}
            cx={cx}
            cy={sy}
            r={3}
            fill={color}
            stroke="#fff"
            strokeWidth={1}
            opacity={opacity}
          />
        );
      })}
    </g>
  );
  };
};

/** Long ranges keep every reading on the line and blink only the live tip. */
const liveTipDot = (color, windowStart, windowEnd, liveTailOrKey = null, liveTailIndex = null) => {
  const tailIndex = typeof liveTailOrKey === 'number' ? liveTailOrKey : liveTailIndex;
  return (props) => {
    const pointIndex = props.payload?.pointIndex ?? props.index ?? 0;
    const isLast = tailIndex != null
      ? pointIndex === tailIndex
      : pointIndex === windowEnd;
    if (!isLast) return null;
    return chartAxisDot(color, windowStart, windowEnd, tailIndex)(props);
  };
};

const tempChartDot = (color, windowStart, windowEnd, dataKey = 'temperature', liveTailIndex = null) => (props) => {
  const { cx, cy, payload, index } = props;
  const value = payload?.[dataKey];
  if (cx == null || cy == null || value == null || Number.isNaN(value)) return null;
  const pointIndex = payload?.pointIndex ?? index ?? 0;
  const key = payload?.chartKey ?? `${dataKey}-${pointIndex}`;
  // Prefer per-series live tip index; fall back to isLiveTail mark, then window end (single-sensor).
  const isLast = liveTailIndex != null
    ? pointIndex === liveTailIndex
    : payload?.isLiveTail
      ? Boolean(payload.isLiveTail[dataKey])
      : pointIndex === windowEnd;
  const windowSpan = Math.max(0, windowEnd - windowStart);
  const ageRatio = windowSpan > 0 ? (windowEnd - pointIndex) / windowSpan : 0;
  const opacity = isLast ? 1 : 0.28 + (1 - ageRatio) * 0.32;

  if (isLast) {
    return (
      <g key={key}>
        <circle cx={cx} cy={cy} r={7} fill={color} opacity={0.18}>
          <animate attributeName="r" values="5;9;5" dur="1.35s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.18;0.42;0.18" dur="1.35s" repeatCount="indefinite" />
        </circle>
        <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={2} />
      </g>
    );
  }

  return (
    <circle
      key={key}
      cx={cx}
      cy={cy}
      r={3}
      fill={color}
      stroke={color}
      strokeWidth={1}
      opacity={opacity}
    />
  );
};

const attachChartPanListeners = ({
  el,
  seriesLenRef,
  windowRef,
  setWindow,
  followLiveRef,
  shiftWindow,
  extraRightSlotsRef,
  setExtraRightSlots,
  onVerticalZoom,
}) => {
  const panState = { active: false, startX: 0, startWindow: null, startExtraRight: 0 };

  const onPointerDown = (e) => {
    if (seriesLenRef.current < 1) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    panState.active = true;
    panState.startX = e.clientX;
    panState.startWindow = { ...windowRef.current };
    panState.startExtraRight = extraRightSlotsRef?.current ?? 0;
    followLiveRef.current = false;
    el.classList.add('is-chart-panning');
    if (el.setPointerCapture) el.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    if (!panState.active || !panState.startWindow) return;
    const deltaX = e.clientX - panState.startX;
    const chartWidth = el.clientWidth || 400;
    const windowSize = getWindowPointCount(
      panState.startWindow.startIndex,
      panState.startWindow.endIndex,
    );
    const pointShift = Math.round((-deltaX / chartWidth) * windowSize);
    if (!pointShift) return;

    const len = seriesLenRef.current;
    const viewportSize = getChartWindowSize(len);
    const liveStartIndex = Math.max(0, len - viewportSize);
    let extraRight = panState.startExtraRight;

    // Drag left → newer data / empty space on the right (stock-chart style).
    if (pointShift > 0) {
      let newStart = panState.startWindow.startIndex + pointShift;
      let newEnd = panState.startWindow.endIndex + pointShift;
      if (newEnd >= len) {
        const overflow = newEnd - (len - 1);
        extraRight = Math.min(CHART_MAX_EXTRA_RIGHT_SLOTS, panState.startExtraRight + overflow);
        newEnd = len - 1;
        // Keep the live window anchored — only extend empty space on the right.
        newStart = liveStartIndex;
        followLiveRef.current = extraRight === 0;
        if (setExtraRightSlots) setExtraRightSlots(extraRight);
        setWindow({ startIndex: newStart, endIndex: newEnd });
        return;
      }
      if (setExtraRightSlots && extraRight > 0) {
        setExtraRightSlots(0);
        extraRight = 0;
      }
      followLiveRef.current = newEnd >= len - 1 && extraRight === 0;
      setWindow({ startIndex: newStart, endIndex: newEnd });
      return;
    }

    // Drag right → older data; collapse extra right padding first.
    let remaining = -pointShift;
    if (extraRight > 0 && setExtraRightSlots) {
      const consume = Math.min(extraRight, remaining);
      extraRight -= consume;
      remaining -= consume;
      setExtraRightSlots(extraRight);
    }
    if (remaining === 0) {
      followLiveRef.current = panState.startWindow.endIndex >= len - 1 && extraRight === 0;
      if (
        panState.startWindow.endIndex >= len - 1
        && panState.startWindow.startIndex !== liveStartIndex
      ) {
        setWindow({ startIndex: liveStartIndex, endIndex: len - 1 });
      }
      return;
    }

    let newStart = panState.startWindow.startIndex - remaining;
    let newEnd = panState.startWindow.endIndex - remaining;
    if (newStart < 0) {
      newEnd -= newStart;
      newStart = 0;
    }
    followLiveRef.current = newEnd >= len - 1 && extraRight === 0;
    setWindow({ startIndex: newStart, endIndex: newEnd });
  };

  const endPan = (e) => {
    if (!panState.active) return;
    panState.active = false;
    panState.startWindow = null;
    el.classList.remove('is-chart-panning');
    if (e?.pointerId != null && el.releasePointerCapture) {
      try { el.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    }
  };

  const onWheel = (e) => {
    const rect = el.getBoundingClientRect();
    const overYAxis = (e.clientX - rect.left) < CHART_Y_AXIS_ZOOM_GUTTER;
    if (onVerticalZoom && (e.ctrlKey || e.metaKey || overYAxis)) {
      e.preventDefault();
      onVerticalZoom(e);
      return;
    }
    if (seriesLenRef.current < 1) return;
    const len = seriesLenRef.current;
    const { startIndex, endIndex } = windowRef.current;
    const windowSize = getWindowPointCount(startIndex, endIndex);
    const atLiveEdge = endIndex >= len - 1;
    const horizontal = e.deltaX !== 0 && Math.abs(e.deltaX) > Math.abs(e.deltaY);
    const delta = horizontal ? e.deltaX : e.deltaY;
    if (!delta) return;
    // The live chart already shows every point, so a vertical wheel scrolls the page
    // instead of redrawing the chart to add empty space.
    const showingAllPoints = windowSize >= len;
    if (!horizontal && showingAllPoints && (extraRightSlotsRef?.current ?? 0) === 0) return;
    e.preventDefault();
    followLiveRef.current = false;

    // Scroll down/right at live edge → reveal more empty space on the right.
    if (delta > 0 && atLiveEdge && setExtraRightSlots) {
      const nextExtra = Math.min(
        CHART_MAX_EXTRA_RIGHT_SLOTS,
        (extraRightSlotsRef?.current ?? 0) + 1,
      );
      if (nextExtra !== (extraRightSlotsRef?.current ?? 0)) {
        setExtraRightSlots(nextExtra);
      }
      return;
    }

    // Scroll up/left at live edge → collapse extra right padding first.
    if (delta < 0 && atLiveEdge && (extraRightSlotsRef?.current ?? 0) > 0 && setExtraRightSlots) {
      setExtraRightSlots((prev) => Math.max(0, prev - 1));
      return;
    }

    if (windowSize >= len && (extraRightSlotsRef?.current ?? 0) === 0) return;
    shiftWindow(delta > 0 ? 1 : -1);
  };

  el.addEventListener('pointerdown', onPointerDown);
  el.addEventListener('pointermove', onPointerMove);
  el.addEventListener('pointerup', endPan);
  el.addEventListener('pointercancel', endPan);
  el.addEventListener('wheel', onWheel, { passive: false });

  return () => {
    el.removeEventListener('pointerdown', onPointerDown);
    el.removeEventListener('pointermove', onPointerMove);
    el.removeEventListener('pointerup', endPan);
    el.removeEventListener('pointercancel', endPan);
    el.removeEventListener('wheel', onWheel);
  };
};

const isTimestampInDateRange = (timestamp, startDate, endDate) => {
  const ts = new Date(timestamp);
  if (Number.isNaN(ts.getTime())) return false;
  const rangeStart = new Date(`${startDate}T00:00:00.000Z`);
  const rangeEnd = new Date(`${endDate}T23:59:59.999Z`);
  return ts >= rangeStart && ts <= rangeEnd;
};

const CHART_Y_AXIS_ZOOM_GUTTER = 56;

const paddedYDomainFromValues = (values) => {
  const nums = values.filter((value) => Number.isFinite(value));
  if (!nums.length) return null;
  let min = minOf(nums);
  let max = maxOf(nums);
  if (min === max) {
    const pad = Math.max(Math.abs(min) * 0.15, 1);
    return [min - pad, max + pad];
  }
  const pad = (max - min) * 0.1;
  return [min - pad, max + pad];
};

const formatAccelYAxisTick = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  if (abs >= 10000) return n.toFixed(0);
  if (abs >= 1000) return n.toFixed(1);
  if (abs >= 100) return n.toFixed(2);
  return n.toFixed(3).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};

const niceYAxisStep = (range, tickCount) => {
  const rough = range / Math.max(tickCount - 1, 1);
  if (!(rough > 0) || !Number.isFinite(rough)) return 1;
  const exp = Math.floor(Math.log10(rough));
  const frac = rough / (10 ** exp);
  const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return niceFrac * (10 ** exp);
};

/** Keep the regular scale ticks, and add each saved threshold at its own y value. */
const mergeThresholdYAxisTicks = (domainTicks, thresholdLines, domain) => {
  if (!Array.isArray(domainTicks) || !domainTicks.length) return domainTicks;
  const base = [...domainTicks];
  const [min, max] = Array.isArray(domain) ? domain : [];
  const hasDomain = Number.isFinite(min) && Number.isFinite(max);
  const seen = new Set(base.map((tick) => formatAccelYAxisTick(tick)));
  (thresholdLines || []).forEach((line) => {
    const y = Number(line?.y);
    if (!Number.isFinite(y)) return;
    if (hasDomain && (y < min || y > max)) return;
    const key = formatAccelYAxisTick(y);
    if (!key || seen.has(key)) return;
    seen.add(key);
    base.push(y);
  });
  base.sort((a, b) => a - b);
  return base;
};

const yAxisTicksFromDomain = (domain, tickCount = 5) => {
  if (!Array.isArray(domain) || domain.length < 2) return undefined;
  const min = Number(domain[0]);
  const max = Number(domain[1]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return undefined;
  if (min === max) return [min];
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  const step = niceYAxisStep(hi - lo, tickCount);
  const start = Math.ceil(lo / step) * step;
  const ticks = [];
  const seen = new Set();
  for (let v = start; v <= hi + step * 1e-9; v += step) {
    const t = Number(v.toPrecision(12));
    if (t < lo - step * 1e-6 || t > hi + step * 1e-6) continue;
    const key = formatAccelYAxisTick(t);
    if (seen.has(key)) continue;
    seen.add(key);
    ticks.push(t);
  }
  return ticks.length ? ticks : [lo, hi];
};

const accelYAxisWidthFromTicks = (domain, ticks) => {
  const samples = [
    ...(ticks || []),
    ...(Array.isArray(domain) ? domain : []),
  ].filter((value) => Number.isFinite(Number(value)));
  const labels = (samples.length ? samples : [-100]).map(formatAccelYAxisTick);
  const longest = Math.max(...labels.map((label) => String(label).length), 5);
  return Math.min(80, Math.max(52, longest * 7 + 14));
};

const zoomYDomainFromWheel = (event, el, currentDomain, fullDomain) => {
  if (!fullDomain) return currentDomain;
  const [fullMin, fullMax] = fullDomain;
  const fullRange = fullMax - fullMin;
  if (!(fullRange > 0)) return currentDomain;
  const [yMin, yMax] = currentDomain || fullDomain;
  const range = yMax - yMin;
  if (!(range > 0)) return currentDomain;
  const plotEl =
    el?.querySelector('.recharts-cartesian-grid')
    || el?.querySelector('.recharts-wrapper')
    || el;
  const rect = plotEl?.getBoundingClientRect();
  const height = rect?.height || 1;
  const rel = Math.min(1, Math.max(0, (event.clientY - (rect?.top || 0)) / height));
  const focus = yMax - rel * range;
  const zoomOut = event.deltaY > 0;
  const factor = zoomOut ? 1.2 : 1 / 1.2;
  let nextRange = range * factor;
  // Keep the 0.5 floor for wider series (accelerometer). A smaller span, such as
  // tiltmeter, uses the same 2% of the full range so zoom in and out can move.
  const minRange = fullRange < 0.5 ? fullRange * 0.02 : Math.max(fullRange * 0.02, 0.5);

  if (zoomOut) {
    if (nextRange >= fullRange * 0.99) return null;
    nextRange = Math.min(fullRange, nextRange);
    let nextMin = focus - (focus - yMin) * (nextRange / range);
    let nextMax = nextMin + nextRange;
    if (nextMin < fullMin) nextMin = fullMin;
    if (nextMax > fullMax) nextMax = fullMax;
    if (nextMax - nextMin >= fullRange * 0.99) return null;
    return [nextMin, nextMax];
  }

  nextRange = Math.min(fullRange, Math.max(minRange, nextRange));
  let nextMin = focus - (focus - yMin) * (nextRange / range);
  let nextMax = nextMin + nextRange;
  if (nextMin < fullMin) {
    nextMin = fullMin;
    nextMax = nextMin + nextRange;
  }
  if (nextMax > fullMax) {
    nextMax = fullMax;
    nextMin = nextMax - nextRange;
    if (nextMin < fullMin) nextMin = fullMin;
  }
  return [nextMin, nextMax];
};

const getVisibleChartXDomain = (startIndex, endIndex, extraRightSlots = 0) => {
  if (endIndex < startIndex) return [0, 0];
  const pointCount = endIndex - startIndex + 1;
  const rightPad = getChartLiveRightPadding(pointCount) + extraRightSlots;
  if (pointCount === 1) return [startIndex - 0.35, startIndex + 0.35 + rightPad];
  return [startIndex, endIndex + rightPad];
};

/** Horizontal space a live-axis label needs when it sits flat on the axis. */
const LIVE_AXIS_LABEL_WIDTH = { hour: 40, date: 46 };
const LIVE_AXIS_ANGLES = [0, -40, -55, -68, -78];

const liveAxisLayout = (count, plotWidth, mode) => {
  const labelWidth = LIVE_AXIS_LABEL_WIDTH[mode] || LIVE_AXIS_LABEL_WIDTH.date;
  if (count <= 1) return { angle: 0, height: 30 };
  const width = plotWidth > 0 ? plotWidth : 640;
  const slot = width / count;
  let chosen = LIVE_AXIS_ANGLES[LIVE_AXIS_ANGLES.length - 1];
  for (let i = 0; i < LIVE_AXIS_ANGLES.length; i += 1) {
    const angle = LIVE_AXIS_ANGLES[i];
    const rad = (Math.abs(angle) * Math.PI) / 180;
    const need = angle === 0 ? labelWidth : (labelWidth * Math.cos(rad)) + 4;
    if (need <= slot) {
      chosen = angle;
      break;
    }
  }
  if (chosen === 0) return { angle: 0, height: 30 };
  const rad = (Math.abs(chosen) * Math.PI) / 180;
  const vert = (labelWidth * Math.sin(rad)) + 16;
  return { angle: chosen, height: Math.max(44, Math.min(84, Math.ceil(vert))) };
};

const seriesTimePoints = (series) => (series || [])
  .map((row) => ({
    ms: toUtcDate(row?.timestamp).getTime(),
    index: Number(row?.pointIndex),
  }))
  .filter((row) => Number.isFinite(row.ms) && Number.isFinite(row.index))
  .sort((a, b) => a.ms - b.ms || a.index - b.index);

const indexAtSeriesTime = (points, targetMs) => {
  if (!points.length || !Number.isFinite(targetMs)) return null;
  if (targetMs <= points[0].ms) return points[0].index;
  const last = points[points.length - 1];
  if (targetMs >= last.ms) return last.index;
  let lo = 0;
  let hi = points.length - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid].ms <= targetMs) lo = mid;
    else hi = mid;
  }
  const a = points[lo];
  const b = points[hi];
  if (b.ms === a.ms) return a.index;
  const t = (targetMs - a.ms) / (b.ms - a.ms);
  return a.index + (b.index - a.index) * t;
};

/**
 * Live-chart horizontal ticks for the selected Start–End range.
 * One or two days (or any span whose hours still fit) use 1-hour labels (13:00).
 * Longer ranges use one label per day that has samples, on the same index scale as the line.
 */
const buildLiveChartXAxis = ({ series, startDate, endDate, timeZone, plotWidth }) => {
  const empty = { ticks: [], labelFor: () => '', angle: 0, height: 30 };
  const points = seriesTimePoints(series);
  if (!points.length) return empty;

  const selectedDays = listUtcDaysInclusive(startDate, endDate);
  const width = plotWidth > 0 ? plotWidth : 640;
  const estimatedHours = Math.max(1, selectedDays.length) * 24;
  const hoursFitFlat = estimatedHours * LIVE_AXIS_LABEL_WIDTH.hour <= width;
  const useHours = selectedDays.length > 0 && (selectedDays.length <= 2 || hoursFitFlat);
  const firstMs = points[0].ms;
  const lastMs = points[points.length - 1].ms;

  const tickLabels = [];
  const pushTick = (index, label) => {
    if (!label || !Number.isFinite(index)) return;
    const rounded = Number(index.toFixed(3));
    const prev = tickLabels[tickLabels.length - 1];
    if (prev && Math.abs(prev.index - rounded) < 0.2) return;
    tickLabels.push({ index: rounded, label });
  };

  if (useHours) {
    let dateKey = toDateKeyInTimeZone(firstMs, timeZone);
    let hour = zonedHour(firstMs, timeZone);
    const endKey = toDateKeyInTimeZone(lastMs, timeZone);
    const endHour = zonedHour(lastMs, timeZone);
    let guard = 0;
    while (dateKey && guard < 24 * 16) {
      const at = zonedWallTimeToUtcMs(dateKey, hour, 0, timeZone);
      const ms = Math.min(Math.max(at, firstMs), lastMs);
      pushTick(indexAtSeriesTime(points, ms), formatChartHourLabel(hour));
      if (dateKey === endKey && hour === endHour) break;
      if (hour >= 23) {
        dateKey = addUtcCalendarDays(dateKey, 1);
        hour = 0;
      } else {
        hour += 1;
      }
      guard += 1;
    }
  } else {
    selectedDays.forEach((dateKey) => {
      const dayStart = zonedWallTimeToUtcMs(dateKey, 0, 0, timeZone);
      const dayEnd = zonedWallTimeToUtcMs(addUtcCalendarDays(dateKey, 1), 0, 0, timeZone);
      if (!Number.isFinite(dayStart) || !Number.isFinite(dayEnd)) return;
      if (lastMs < dayStart || firstMs >= dayEnd) return;
      const ms = Math.min(Math.max(dayStart, firstMs), lastMs);
      pushTick(indexAtSeriesTime(points, ms), formatChartDayLabel(dateKey));
    });
  }

  if (!tickLabels.length) return empty;

  // Keep the first and last marks, and drop any time that would draw on the previous one.
  const indexSpan = Math.max(
    points[points.length - 1].index - points[0].index,
    1,
  );
  const minLabelPx = useHours ? LIVE_AXIS_LABEL_WIDTH.hour + 18 : LIVE_AXIS_LABEL_WIDTH.date;
  const ticksForAxis = [];
  tickLabels.forEach((tick, i) => {
    const isLast = i === tickLabels.length - 1;
    if (!ticksForAxis.length) {
      ticksForAxis.push(tick);
      return;
    }
    const prev = ticksForAxis[ticksForAxis.length - 1];
    const gapPx = (Math.abs(tick.index - prev.index) / indexSpan) * width;
    if (gapPx < minLabelPx && !isLast) return;
    if (gapPx < minLabelPx && ticksForAxis.length > 1) ticksForAxis.pop();
    ticksForAxis.push(tick);
  });

  const layout = liveAxisLayout(ticksForAxis.length, width, useHours ? 'hour' : 'date');
  const labelFor = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    let best = null;
    let bestDist = 0.51;
    ticksForAxis.forEach((tick) => {
      const dist = Math.abs(tick.index - n);
      if (dist < bestDist) {
        bestDist = dist;
        best = tick;
      }
    });
    return best ? best.label : '';
  };

  return {
    ticks: ticksForAxis.map((tick) => tick.index),
    labelFor,
    angle: layout.angle,
    height: layout.height,
  };
};

/** First date starts inside the plot so it does not sit on the y-axis corner. */
const LiveChartXTick = ({ x, y, payload, index, tickFormatter, angle, textAnchor }) => {
  const label = typeof tickFormatter === 'function' ? tickFormatter(payload?.value) : '';
  if (!label || x == null || y == null) return null;
  const px = Number(x);
  const py = Number(y);
  if (!Number.isFinite(px) || !Number.isFinite(py)) return null;
  const tilt = Number(angle) || 0;
  const isFirst = index === 0;
  const anchor = isFirst ? 'start' : (textAnchor || (tilt ? 'end' : 'middle'));
  return (
    <text
      x={isFirst && !tilt ? px + 4 : px}
      y={py}
      dy={10}
      textAnchor={anchor}
      fill="#666"
      fontSize={9}
      transform={tilt ? `rotate(${tilt}, ${px}, ${py})` : undefined}
    >
      {label}
    </text>
  );
};

const extractLatestReadings = (latestRes) => {
  if (!latestRes) return [];
  if (Array.isArray(latestRes)) return latestRes;
  if (Array.isArray(latestRes.data)) return latestRes.data;
  if (Array.isArray(latestRes.readings)) return latestRes.readings;
  if (latestRes.data && typeof latestRes.data === 'object') return [latestRes.data];
  if (latestRes.reading && typeof latestRes.reading === 'object') return [latestRes.reading];
  return [];
};

const normalizeSocketId = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    if (value._id != null) return String(value._id);
    if (typeof value.toString === 'function') {
      const asString = value.toString();
      if (asString && asString !== '[object Object]') return asString;
    }
  }
  return String(value);
};

/** Numeric fields averaged when multiple readings share a sensor + minute bucket. */
const AGGREGATE_NUMERIC_FIELDS = ['xAxis', 'yAxis', 'zAxis', 'value', 'temperature', 'batteryVoltage'];

const getReadingWeight = (row) => (row?._sampleCount > 0 ? row._sampleCount : 1);

/** One row per sensor per minute (Resensys poll is 60s; backend may store duplicate timestamps). */
const readingPointKey = (d) => {
  const ts = new Date(d.timestamp);
  const sensorId = normalizeSocketId(d.sensorId);
  if (Number.isNaN(ts.getTime())) return `${sensorId}-invalid`;
  return `${sensorId}-${Math.floor(ts.getTime() / 60000)}`;
};

/** Shared minute bucket for multi-sensor pivot charts (one row per timestamp, all sensors). */
const chartTimeBucketKey = (timestamp) => {
  const ts = new Date(timestamp);
  if (Number.isNaN(ts.getTime())) return 'invalid';
  return Math.floor(ts.getTime() / 60000);
};

const gatherAxisSamples = (rows, field) => {
  const samples = [];
  rows.forEach((row) => {
    const nested = row?._axisSamples?.[field];
    if (Array.isArray(nested) && nested.length) {
      nested.forEach((value) => {
        const sample = normalizeAxisSample(value);
        if (sample) samples.push(sample);
      });
      return;
    }
    const sample = normalizeAxisSample(row?.[field]);
    if (sample) samples.push(sample);
  });
  return samplesAroundFirstPoint(samples);
};

const aggregateReadingsGroup = (rows) => {
  const sorted = [...rows].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const last = sorted[sorted.length - 1];
  const aggregated = { ...last };
  const totalWeight = sorted.reduce((sum, row) => sum + getReadingWeight(row), 0);

  AGGREGATE_NUMERIC_FIELDS.forEach((field) => {
    let weightedSum = 0;
    let weight = 0;
    sorted.forEach((row) => {
      const value = row[field];
      if (value != null && !Number.isNaN(Number(value))) {
        const rowWeight = getReadingWeight(row);
        weightedSum += Number(value) * rowWeight;
        weight += rowWeight;
      }
    });
    if (weight > 0) aggregated[field] = round3(weightedSum / weight);
  });

  aggregated.timestamp = last.timestamp;
  aggregated._sampleCount = totalWeight;
  aggregated._axisSamples = {
    xAxis: gatherAxisSamples(sorted, 'xAxis'),
    yAxis: gatherAxisSamples(sorted, 'yAxis'),
    zAxis: gatherAxisSamples(sorted, 'zAxis'),
  };
  return aggregated;
};

/** Collapse duplicate sensor+minute readings into one averaged point per bucket. */
const dedupeReadingsPerMinute = (rows) => {
  if (!rows?.length) return [];
  const byKey = new Map();
  const sorted = [...rows].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  sorted.forEach((row) => {
    const key = readingPointKey(row);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(row);
  });
  return [...byKey.values()]
    .map((group) => aggregateReadingsGroup(group))
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
};

/**
 * A burst can cross a clock minute by a second (7:38:59.544 and 7:39:00.014).
 * That is still one sample, so the 3-hour chart spacing stays one point per interval.
 */
const BURST_SAME_POINT_MS = 5000;

/** Readings within ±2 minutes share one chart point. The later value stays on that vertical stack. */
const CHART_STACK_WINDOW_MS = 2 * 60 * 1000;

const readingTimeMs = (timestamp) => {
  const ms = new Date(timestamp).getTime();
  return Number.isFinite(ms) ? ms : null;
};

const joinBurstSpillover = (rows) => {
  if (!rows?.length) return [];
  const bySensor = new Map();
  rows.forEach((row) => {
    const sensorId = normalizeSocketId(row.sensorId) || '__none__';
    if (!bySensor.has(sensorId)) bySensor.set(sensorId, []);
    bySensor.get(sensorId).push(row);
  });
  const joined = [];
  bySensor.forEach((group) => {
    const sorted = [...group].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    let current = sorted[0];
    for (let i = 1; i < sorted.length; i += 1) {
      const prevMs = readingTimeMs(current.timestamp);
      const nextMs = readingTimeMs(sorted[i].timestamp);
      const gap = prevMs != null && nextMs != null ? nextMs - prevMs : Infinity;
      if (gap >= 0 && gap <= CHART_STACK_WINDOW_MS) {
        const keptTimestamp = current.timestamp;
        current = aggregateReadingsGroup([current, sorted[i]]);
        current.timestamp = keptTimestamp;
      } else {
        joined.push(current);
        current = sorted[i];
      }
    }
    joined.push(current);
  });
  return joined.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
};

/** Label a sub-second burst with its first time, so 7:39:00 stays on the 7:38 point. */
const burstAnchorTimestamp = (row) => {
  const rowMs = readingTimeMs(row?.timestamp);
  const times = [row?.timestamp];
  ['xAxis', 'yAxis', 'zAxis'].forEach((field) => {
    const samples = row?._axisSamples?.[field];
    if (!Array.isArray(samples)) return;
    samples.forEach((sample) => {
      if (sample?.timestamp) times.push(sample.timestamp);
    });
  });
  const ms = times.map(readingTimeMs).filter((value) => value != null);
  if (!ms.length || rowMs == null) return row?.timestamp;
  const earliest = minOf(ms);
  const latest = maxOf(ms);
  if (latest - earliest > BURST_SAME_POINT_MS) return row?.timestamp;
  if (Math.floor(earliest / 60000) === Math.floor(rowMs / 60000)) return row?.timestamp;
  return new Date(earliest).toISOString();
};

const snapSpilloverSample = (sample, anchor) => {
  if (!sample?.timestamp || anchor == null) return sample;
  const sampleMs = readingTimeMs(sample.timestamp);
  const anchorMs = readingTimeMs(anchor);
  if (sampleMs == null || anchorMs == null || sampleMs <= anchorMs) return sample;
  if (sampleMs - anchorMs > BURST_SAME_POINT_MS) return sample;
  if (Math.floor(sampleMs / 60000) === Math.floor(anchorMs / 60000)) return sample;
  return { ...sample, timestamp: anchor };
};

const mergeAveragedReading = (existing, incoming) => {
  const existingWeight = getReadingWeight(existing);
  const incomingWeight = getReadingWeight(incoming);
  const totalWeight = existingWeight + incomingWeight;
  const merged = { ...existing, ...incoming };

  AGGREGATE_NUMERIC_FIELDS.forEach((field) => {
    const existingVal = existing[field];
    const incomingVal = incoming[field];
    const existingNum = existingVal != null && !Number.isNaN(Number(existingVal)) ? Number(existingVal) : null;
    const incomingNum = incomingVal != null && !Number.isNaN(Number(incomingVal)) ? Number(incomingVal) : null;
    if (existingNum != null && incomingNum != null) {
      merged[field] = round3((existingNum * existingWeight + incomingNum * incomingWeight) / totalWeight);
    } else if (incomingNum != null) {
      merged[field] = incomingNum;
    } else if (existingNum != null) {
      merged[field] = existingNum;
    }
  });

  merged.timestamp = incoming.timestamp ?? existing.timestamp;
  merged._sampleCount = totalWeight;
  merged._axisSamples = {
    xAxis: gatherAxisSamples([existing, incoming], 'xAxis'),
    yAxis: gatherAxisSamples([existing, incoming], 'yAxis'),
    zAxis: gatherAxisSamples([existing, incoming], 'zAxis'),
  };
  return merged;
};

const combineAndAggregateReadings = (rowSets, startDate, endDate) =>
  dedupeReadingsPerMinute(
    rowSets
      .flat()
      .filter((d) => isTimestampInDateRange(d.timestamp, startDate, endDate))
  );

const getSelectedSensorMeta = (bridge, sensorIndices) => {
  const ids = [];
  const serials = [];
  if (!bridge?.sensors?.length || !sensorIndices?.length) return { ids, serials };
  sensorIndices.forEach((idx) => {
    const s = bridge.sensors[idx];
    if (!s) return;
    const id = normalizeSocketId(s._id);
    if (id) ids.push(id);
    if (s.sensorSerialNo) serials.push(s.sensorSerialNo);
  });
  return { ids, serials };
};

/** Same minute line the live chart draws. Burst samples stay on the graph only. */
const chartLineReadingsForExport = (rows, selectedSensorIds, selectedSensorSerials, startDate, endDate, timeZone) =>
  joinBurstSpillover(
    dedupeReadingsPerMinute(
      [...rows]
        .filter((d) => (
          isTimestampInChartDateRange(d.timestamp, startDate, endDate, timeZone)
          && readingMatchesSelectedSensors(d, selectedSensorIds, selectedSensorSerials)
        ))
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    )
  );

const isChartAxisNumber = (value) =>
  value != null && value !== '' && !Number.isNaN(Number(value));

const firstFiniteSample = (samples) => {
  if (!Array.isArray(samples)) return undefined;
  for (let i = 0; i < samples.length; i += 1) {
    const value = axisSampleValue(samples[i]);
    if (value !== undefined) return value;
  }
  return undefined;
};

/** A stored axis is a number, or the first burst sample when the scalar was not copied up. */
const axisOrFirstSample = (...candidates) => {
  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i];
    if (isChartAxisNumber(value)) return Number(value);
    const fromObject = axisSampleValue(value);
    if (fromObject !== undefined && value && typeof value === 'object') return fromObject;
    const fromList = firstFiniteSample(value);
    if (fromList !== undefined) return fromList;
  }
  return undefined;
};

const readingTemperatureC = (reading, vib, raw) => {
  const found = axisOrFirstSample(
    reading?.temperature,
    vib?.temperature,
    raw?.temperature,
    raw?.temp,
    raw?.['TEMPERATURE(C)'],
    raw?.TEMPERATURE,
    reading?.temperatureSamples,
    raw?.temperatureSamples,
  );
  return found === undefined ? reading?.temperature : found;
};

/** Prefer top-level axes; fill from vibration / rawPayload / burst samples when missing. */
const flattenReadingAxes = (reading) => {
  if (!reading || typeof reading !== 'object') return reading;
  const vib = reading.vibration && typeof reading.vibration === 'object' ? reading.vibration : {};
  const raw = reading.rawPayload && typeof reading.rawPayload === 'object' ? reading.rawPayload : {};
  const pick = (...vals) => vals.find(isChartAxisNumber);
  const xAxis = axisOrFirstSample(
    pick(reading.xAxis, vib.xAxis, raw.xAxis, raw['VIBRATION_X(mg)']),
    reading.xAxis,
    reading.xAxisSamples,
    vib.xAxisSamples,
    raw.xAxisSamples,
  );
  const yAxis = axisOrFirstSample(
    pick(reading.yAxis, vib.yAxis, raw.yAxis, raw['VIBRATION_Y(mg)']),
    reading.yAxis,
    reading.yAxisSamples,
    vib.yAxisSamples,
    raw.yAxisSamples,
  );
  const zAxis = axisOrFirstSample(
    pick(reading.zAxis, vib.zAxis, raw.zAxis, raw['VIBRATION_Z(mg)']),
    reading.zAxis,
    reading.zAxisSamples,
    vib.zAxisSamples,
    raw.zAxisSamples,
  );
  return {
    ...reading,
    xAxis: xAxis === undefined ? reading.xAxis : xAxis,
    yAxis: yAxis === undefined ? reading.yAxis : yAxis,
    zAxis: zAxis === undefined ? reading.zAxis : zAxis,
    temperature: readingTemperatureC(reading, vib, raw),
  };
};

const normalizeSocketReading = (reading) => {
  const flat = flattenReadingAxes(reading);
  return {
    ...flat,
    bridgeId: normalizeSocketId(flat.bridgeId),
    sensorId: normalizeSocketId(flat.sensorId || flat._id),
  };
};

/** Date inputs are YYYY-MM-DD; hybrid chart API expects full ISO range bounds. */
const toApiStartIso = (dateStr) => new Date(`${dateStr}T00:00:00.000Z`).toISOString();
const toApiEndIso = (dateStr) => new Date(`${dateStr}T23:59:59.999Z`).toISOString();

const addUtcCalendarDays = (dateStr, days) => {
  const d = new Date(`${dateStr}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/** Inclusive UTC YYYY-MM-DD list so a 5000-row cap cannot skip middle days. */
const listUtcDaysInclusive = (startStr, endStr) => {
  if (!startStr || !endStr || startStr > endStr) return [];
  const days = [];
  let cur = startStr;
  while (cur <= endStr) {
    days.push(cur);
    cur = addUtcCalendarDays(cur, 1);
    if (days.length > 370) break;
  }
  return days;
};

/** Avg of max/min for aggregated hourly buckets (>90d ranges). */
const avgBucket = (max, min, fallback = null) => {
  if (max != null && min != null && !Number.isNaN(Number(max)) && !Number.isNaN(Number(min))) {
    return (Number(max) + Number(min)) / 2;
  }
  if (max != null && !Number.isNaN(Number(max))) return Number(max);
  if (min != null && !Number.isNaN(Number(min))) return Number(min);
  return fallback;
};

/**
 * Map hybrid Mongo+S3 chart payload rows into the shape existing charts expect.
 * Backend merges sources; frontend never talks to S3.
 */
const finiteAxisSamples = (values) => samplesAroundFirstPoint(values);

/** Minute chart rows may carry every stored value for that minute. The line still uses x/y/z. */
const attachApiAxisSamples = (reading, row) => {
  const xAxis = finiteAxisSamples(row?.xAxisSamples);
  const yAxis = finiteAxisSamples(row?.yAxisSamples);
  const zAxis = finiteAxisSamples(row?.zAxisSamples);
  if (!xAxis.length && !yAxis.length && !zAxis.length) return reading;
  return {
    ...reading,
    _axisSamples: { xAxis, yAxis, zAxis },
  };
};

const mapChartApiRows = (payload) => {
  const rows = payload?.data || [];
  if (!payload?.isAggregated) {
    return rows.map((row) => attachApiAxisSamples(normalizeSocketReading(row), row));
  }
  return rows.map((row) => {
    const hourTs = row._id?.hour || row.timestamp || row.hour;
    const xAxis = avgBucket(row.maxX, row.minX, row.xAxis);
    const yAxis = avgBucket(row.maxY, row.minY, row.yAxis);
    const zAxis = avgBucket(row.maxZ, row.minZ, row.zAxis);
    const rangeSamples = (min, max, avg) => [...new Set(
      [min, max, avg].map((value) => chartAxisValue(value)).filter((value) => value !== undefined)
    )];
    return normalizeSocketReading({
      ...row,
      timestamp: hourTs,
      xAxis,
      yAxis,
      zAxis,
      value: avgBucket(row.maxValue, row.minValue, row.value),
      temperature: avgBucket(row.maxTemperature, row.minTemperature, row.temperature),
      _axisSamples: {
        xAxis: rangeSamples(row.minX, row.maxX, xAxis),
        yAxis: rangeSamples(row.minY, row.maxY, yAxis),
        zAxis: rangeSamples(row.minZ, row.maxZ, zAxis),
      },
    });
  });
};

/** Map day chunks a few at a time so a long range does not freeze the page. */
const mapChartChunks = async (chunkRes) => {
  const rows = [];
  for (let i = 0; i < chunkRes.length; i += 1) {
    const mapped = mapChartApiRows(chunkRes[i]);
    for (let j = 0; j < mapped.length; j += 1) rows.push(mapped[j]);
    if (i > 0 && i % 2 === 1) await yieldToMain();
  }
  return rows;
};

/** History for one sensor (same day-chunked getData as the live chart). */
const fetchSensorHistoryRows = (bridgeId, startDate, endDate, sensorId) =>
  Promise.all(
    listUtcDaysInclusive(startDate, endDate).map((day) =>
      sensorService.getData(bridgeId, {
        granularity: 'minute',
        limit: SENSOR_DATA_FETCH_LIMIT,
        sensorId,
        startDate: toApiStartIso(day),
        endDate: toApiEndIso(day),
      })
    )
  ).then(mapChartChunks);

const chartHistoryInflight = new Map();

/** Reuse an in-flight day-chunked history request so bridge colors overlap the video list. */
const loadChartHistoryRows = (bridgeId, startDate, endDate, sensorId) => {
  const key = `${bridgeId}|${startDate}|${endDate}|${sensorId || ''}`;
  const existing = chartHistoryInflight.get(key);
  if (existing) return existing;
  const promise = Promise.all(
    listUtcDaysInclusive(startDate, endDate).map((day) =>
      sensorService.getData(bridgeId, {
        granularity: 'minute',
        limit: SENSOR_DATA_FETCH_LIMIT,
        ...(sensorId ? { sensorId } : {}),
        startDate: toApiStartIso(day),
        endDate: toApiEndIso(day),
      })
    )
  ).then(mapChartChunks);
  chartHistoryInflight.set(key, promise);
  promise.then(
    () => {
      setTimeout(() => {
        if (chartHistoryInflight.get(key) === promise) chartHistoryInflight.delete(key);
      }, 20000);
    },
    () => {
      if (chartHistoryInflight.get(key) === promise) chartHistoryInflight.delete(key);
    },
  );
  return promise;
};

const valueFromChartY = (chartY, viewBox, domain) => {
  if (!Array.isArray(domain) || domain.length < 2) return null;
  const [yMin, yMax] = domain;
  const top = Number(viewBox?.y);
  const height = Number(viewBox?.height);
  if (!Number.isFinite(chartY) || !Number.isFinite(top) || !Number.isFinite(height) || height <= 0) return null;
  if (!Number.isFinite(yMin) || !Number.isFinite(yMax) || yMax === yMin) return null;
  const rel = (chartY - top) / height;
  return yMax - rel * (yMax - yMin);
};

/** The one vertical sample nearest the cursor. One value, not the whole burst. */
const nearestVerticalSample = (samples, chartY, viewBox, domain, lineValue) => {
  const list = samplesAroundFirstPoint(samples);
  if (list.length < 2) return null;
  const mouseValue = valueFromChartY(chartY, viewBox, domain);
  if (mouseValue == null) return null;

  const candidates = list
    .map((sample) => ({
      value: axisSampleValue(sample),
      timestamp: sample.timestamp || null,
    }))
    .filter((sample) => sample.value !== undefined);
  const line = chartAxisValue(lineValue);
  if (line !== undefined && !candidates.some((sample) => round3(sample.value) === round3(line))) {
    candidates.push({ value: line, timestamp: null });
  }
  if (!candidates.length) return null;

  return candidates.reduce((best, sample) => (
    Math.abs(sample.value - mouseValue) < Math.abs(best.value - mouseValue) ? sample : best
  ));
};

const ChartValueTooltip = ({ active, payload, timeZone, coordinate, viewBox, yDomain }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  const mouseValue = valueFromChartY(coordinate?.y, viewBox, yDomain);
  const rows = payload.map((entry) => {
    const hovered = nearestVerticalSample(
      entry.payload?.[`${entry.dataKey}Samples`],
      coordinate?.y,
      viewBox,
      yDomain,
      entry.value,
    );
    const lineValue = chartAxisValue(entry.value);
    const shown = hovered ? hovered.value : entry.value;
    const hoveredIsLine = !hovered
      || (lineValue !== undefined && round3(hovered.value) === round3(lineValue));
    const distance = mouseValue == null || shown == null || Number.isNaN(Number(shown))
      ? Infinity
      : Math.abs(Number(shown) - mouseValue);
    return {
      dataKey: entry.dataKey,
      name: entry.name,
      shown,
      distance,
      sampleTime: hovered?.timestamp && !hoveredIsLine ? hovered.timestamp : null,
    };
  });
  const nearest = rows.reduce(
    (best, row) => (row.distance < best.distance ? row : best),
    rows[0],
  );
  const timeLabel = nearest?.sampleTime
    ? formatChartSampleTime(nearest.sampleTime, timeZone)
    : (point?.fullTime || formatChartFullTime(point?.timestamp, timeZone));
  return (
    <div className="sensor-chart-value-tooltip">
      {timeLabel && (
        <div className="sensor-chart-value-tooltip-time">{timeLabel}</div>
      )}
      {rows.map((row) => (
        <div key={row.dataKey}>
          {row.name}: {row.shown != null && !Number.isNaN(Number(row.shown)) ? Number(row.shown).toFixed(3) : '—'}
        </div>
      ))}
    </div>
  );
};

const LEGEND_AXIS_ORDER = ['X', 'Y', 'Z'];

/** Group multi-sensor axis lines into one legend entry per sensor: "serial X, Y, Z". */
const GroupedMultiSensorLegend = ({ payload }) => {
  if (!payload?.length) return null;

  const groups = [];
  const bySerial = new Map();

  payload.forEach((entry) => {
    if (!entry || entry.type === 'none') return;
    const value = String(entry.value ?? entry.name ?? '');
    const match = value.match(/^(.*)\s([XYZ])$/);
    const serial = match ? match[1] : value;
    const axis = match ? match[2] : null;

    if (!bySerial.has(serial)) {
      bySerial.set(serial, { serial, color: entry.color, axes: [] });
      groups.push(serial);
    }
    const group = bySerial.get(serial);
    if (axis && !group.axes.includes(axis)) group.axes.push(axis);
  });

  return (
    <div className="sensor-data-chart-legend">
      {groups.map((serial) => {
        const group = bySerial.get(serial);
        const axes = [...group.axes].sort(
          (a, b) => LEGEND_AXIS_ORDER.indexOf(a) - LEGEND_AXIS_ORDER.indexOf(b)
        );
        const label = axes.length ? `${group.serial} ${axes.join(', ')}` : group.serial;
        return (
          <span key={serial} className="sensor-data-chart-legend-item">
            <span
              className="sensor-data-chart-legend-swatch"
              style={{ background: group.color }}
            />
            {label}
          </span>
        );
      })}
    </div>
  );
};

const CHART_AXIS_OPTIONS = [
  { value: 'all', label: 'All Axes' },
  { value: 'xAxis', label: 'X-Axis' },
  { value: 'yAxis', label: 'Y-Axis' },
  { value: 'zAxis', label: 'Z-Axis' },
];

const SENSOR_CHART_COLORS = [
  '#e03030', '#1a6fa8', '#0f6e56', '#e07020', '#7b1fa2', '#00838f', '#5e35b1', '#558b2f',
];

const MULTI_AXIS_STROKE_DASH = {
  xAxis: undefined,
  yAxis: '6 4',
  zAxis: '2 3',
};

const THRESHOLD_CHART_BANDS = [
  { key: 'normal', label: 'Normal', stroke: '#2a8a2a' },
  { key: 'elevated', label: 'Elevated', stroke: '#c09000' },
  { key: 'significant', label: 'Significant', stroke: '#e03030' },
];

const buildSingleAxisThresholdLines = (limits = {}) =>
  THRESHOLD_CHART_BANDS.flatMap((band) => {
    const lines = [];
    const upper = toFiniteNumber(limits[band.key]?.upper);
    const lower = toFiniteNumber(limits[band.key]?.lower);
    if (upper != null) {
      lines.push({
        key: `${band.key}-u`,
        y: Math.abs(upper),
        stroke: band.stroke,
        label: `${band.label} U`,
      });
    }
    if (lower != null) {
      lines.push({
        key: `${band.key}-l`,
        y: -Math.abs(lower),
        stroke: band.stroke,
        label: `${band.label} L`,
      });
    }
    return lines;
  });

const toFiniteNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeAxisLimits = (lim) => {
  if (!lim || typeof lim !== 'object') return {};
  if (lim.normal || lim.elevated || lim.significant) {
    return {
      normal: lim.normal,
      elevated: lim.elevated,
      significant: lim.significant,
    };
  }
  if (
    lim.normalU != null || lim.normalL != null
    || lim.elevU != null || lim.elevL != null
    || lim.sigU != null || lim.sigL != null
  ) {
    return {
      normal: { upper: lim.normalU, lower: lim.normalL },
      elevated: { upper: lim.elevU, lower: lim.elevL },
      significant: { upper: lim.sigU, lower: lim.sigL },
    };
  }
  return {};
};

const specHasThresholdBand = (limits) =>
  THRESHOLD_CHART_BANDS.some(
    (band) => toFiniteNumber(limits?.[band.key]?.upper) != null
      || toFiniteNumber(limits?.[band.key]?.lower) != null
  );

const axisNormalBand = (limits) => {
  const upper = toFiniteNumber(limits?.normal?.upper);
  const lower = toFiniteNumber(limits?.normal?.lower);
  if (upper == null && lower == null) return null;
  return limits.normal;
};

/** Normal is one value for every axis. Use it when the viewed axis has no normal of its own. */
const sharedNormalBand = (sensor) => {
  for (const axisKey of SPEC_AXIS_KEYS) {
    const band = axisNormalBand(resolveReferenceThresholds(sensor, axisKey).limits);
    if (band) return band;
  }
  return null;
};

const limitsForChartAxis = (sensor, axisKey, sharedNormal) => {
  const { limits } = resolveReferenceThresholds(sensor, axisKey);
  if (axisNormalBand(limits) || !sharedNormal) return limits;
  return { ...limits, normal: sharedNormal };
};

/**
 * Lines for the chart axis selection.
 * All Axes shows only the shared Normal range.
 * A specific axis (X, Y, or Z) shows Normal plus that axis's Elevated and Significant lines.
 */
const buildVisibleThresholdLines = (sensor, axisView) => {
  const axes = axisView === 'all'
    ? SPEC_AXIS_KEYS
    : (SPEC_AXIS_KEYS.includes(axisView) ? [axisView] : []);
  const sharedNormal = sharedNormalBand(sensor);
  const byBand = new Map();
  axes.forEach((axisKey) => {
    const limits = limitsForChartAxis(sensor, axisKey, sharedNormal);
    buildSingleAxisThresholdLines(limits).forEach((line) => {
      if (axisView === 'all' && !String(line.key).startsWith('normal')) return;
      const yKey = formatAccelYAxisTick(line.y);
      const bucket = byBand.get(line.key) || [];
      const match = bucket.find((entry) => formatAccelYAxisTick(entry.y) === yKey);
      if (match) {
        match.axes.push(axisKey);
        return;
      }
      bucket.push({ ...line, axes: [axisKey] });
      byBand.set(line.key, bucket);
    });
  });
  const lines = [];
  byBand.forEach((entries, bandKey) => {
    const needsAxis = axisView === 'all' && entries.length > 1;
    entries.forEach((entry) => {
      const axisPrefix = needsAxis
        ? `${entry.axes.map((axis) => SPEC_AXIS_LABELS[axis]).join('/')} `
        : '';
      lines.push({
        key: `${entry.axes.join('-')}-${bandKey}`,
        y: entry.y,
        stroke: entry.stroke,
        label: `${axisPrefix}${entry.label}`,
      });
    });
  });
  return lines;
};

/** Limits for the selected chart axis only. Empty axis → no threshold lines. */
const resolveReferenceThresholds = (sensor, axisKey) => {
  const spec = sensor?.specLimits;
  if (!spec || typeof spec !== 'object' || !axisKey) return { limits: {}, baseline: 0 };
  const limits = normalizeAxisLimits(spec[axisKey]);
  return {
    limits: specHasThresholdBand(limits) ? limits : {},
    baseline: toFiniteNumber(spec.baselineValues?.[axisKey]) ?? 0,
  };
};

const fahrenheitToCelsius = (fahrenheit) => ((fahrenheit - 32) * 5) / 9;

const toDisplayTemperature = (fahrenheit, unit) =>
  unit === 'celsius' ? fahrenheitToCelsius(fahrenheit) : fahrenheit;

const sensorChartFieldKey = (sensorId, field) => {
  const safe = normalizeSocketId(sensorId).replace(/[^a-zA-Z0-9]/g, '_');
  return `${safe}__${field}`;
};

/**
 * Multi-sensor buckets are sparse when sensors poll on different minutes.
 * Carry each sensor's last value forward so every series is continuous and
 * shares the same live tip (fixes blink-only-one + left gap before blink).
 */
const fillMultiSensorPivotGaps = (points, sensorIds, fields) => {
  if (!points.length || sensorIds.length < 2) return points;
  const filled = points.map((point) => ({ ...point }));
  sensorIds.forEach((sid) => {
    fields.forEach((field) => {
      const key = sensorChartFieldKey(sid, field);
      let lastVal;
      for (let i = 0; i < filled.length; i += 1) {
        if (chartAxisValue(filled[i][key]) !== undefined) {
          lastVal = filled[i][key];
        } else if (lastVal !== undefined) {
          filled[i][key] = lastVal;
        }
      }
    });
  });
  return filled;
};

/** Mark each field's last defined point so every sensor series can blink its own live tip. */
const markPivotLiveTails = (points, fieldKeys) => {
  if (!points.length) return points;
  const lastIndexByField = {};
  points.forEach((point, i) => {
    fieldKeys.forEach((key) => {
      if (chartAxisValue(point[key]) !== undefined) lastIndexByField[key] = i;
    });
  });
  return points.map((point, i) => {
    const isLiveTail = {};
    fieldKeys.forEach((key) => {
      if (lastIndexByField[key] === i) isLiveTail[key] = true;
    });
    return { ...point, isLiveTail };
  });
};

const getLastDefinedPointIndex = (series, dataKey) => {
  if (!series?.length || !dataKey) return null;
  for (let i = series.length - 1; i >= 0; i -= 1) {
    if (chartAxisValue(series[i][dataKey]) !== undefined) {
      return series[i].pointIndex ?? i;
    }
  }
  return null;
};

const buildPivotAccelChartSeries = (rows, sensorIds, startDate, endDate, timeZone) => {
  const fields = ['xAxis', 'yAxis', 'zAxis'];
  const filtered = dedupeReadingsPerMinute(
    rows.filter((d) => (
      isTimestampInChartDateRange(d.timestamp, startDate, endDate, timeZone)
      && sensorIds.includes(normalizeSocketId(d.sensorId))
    ))
  );
  const bucketMap = new Map();
  filtered.forEach((row) => {
    const key = chartTimeBucketKey(row.timestamp);
    if (!bucketMap.has(key)) {
      bucketMap.set(key, { timestamp: row.timestamp, sensors: new Map() });
    }
    bucketMap.get(key).sensors.set(normalizeSocketId(row.sensorId), row);
  });
  const points = [...bucketMap.entries()]
    .sort((a, b) => new Date(a[1].timestamp) - new Date(b[1].timestamp))
    .map(([key, bucket], i) => {
      const point = {
        pointIndex: i,
        chartKey: `accel-${key}`,
        timestamp: bucket.timestamp,
      };
      sensorIds.forEach((sid) => {
        const row = bucket.sensors.get(sid);
        point[sensorChartFieldKey(sid, 'xAxis')] = chartAxisValue(row?.xAxis) ?? firstFiniteSample(row?._axisSamples?.xAxis);
        point[sensorChartFieldKey(sid, 'yAxis')] = chartAxisValue(row?.yAxis) ?? firstFiniteSample(row?._axisSamples?.yAxis);
        point[sensorChartFieldKey(sid, 'zAxis')] = chartAxisValue(row?.zAxis) ?? firstFiniteSample(row?._axisSamples?.zAxis);
      });
      return point;
    });
  const fieldKeys = sensorIds.flatMap((sid) => fields.map((f) => sensorChartFieldKey(sid, f)));
  return markPivotLiveTails(
    fillMultiSensorPivotGaps(points, sensorIds, fields),
    fieldKeys,
  );
};

const buildPivotTemperatureChartSeries = (rows, sensorIds, startDate, endDate, temperatureUnit, timeZone) => {
  const fields = ['temperature'];
  const matched = rows.filter((d) => (
    d.temperature != null
    && isTimestampInChartDateRange(d.timestamp, startDate, endDate, timeZone)
    && sensorIds.includes(normalizeSocketId(d.sensorId))
  ));
  const exact = matched.filter((d) => d.temperatureSample === true);
  const filtered = exact.length ? exact : dedupeReadingsPerMinute(matched);
  const bucketMap = new Map();
  filtered.forEach((row) => {
    const key = chartTimeBucketKey(row.timestamp);
    if (!bucketMap.has(key)) {
      bucketMap.set(key, { timestamp: row.timestamp, sensors: new Map() });
    }
    bucketMap.get(key).sensors.set(normalizeSocketId(row.sensorId), row);
  });
  const points = [...bucketMap.entries()]
    .sort((a, b) => new Date(a[1].timestamp) - new Date(b[1].timestamp))
    .map(([key, bucket], i) => {
      const point = {
        pointIndex: i,
        chartKey: `temp-${key}`,
        timestamp: bucket.timestamp,
      };
      sensorIds.forEach((sid) => {
        const row = bucket.sensors.get(sid);
        point[sensorChartFieldKey(sid, 'temperature')] = row?.temperature != null
          ? toDisplayTemperature(row.temperature, temperatureUnit)
          : undefined;
      });
      return point;
    });
  const fieldKeys = sensorIds.map((sid) => sensorChartFieldKey(sid, 'temperature'));
  const seriesPoints = exact.length
    ? points
    : fillMultiSensorPivotGaps(points, sensorIds, fields);
  return markPivotLiveTails(seriesPoints, fieldKeys);
};

const chartsPlaceholderStyle = {
  padding: 48,
  textAlign: 'center',
  color: '#999',
  background: '#f8f9fa',
  border: '1px solid #ddd',
  borderRadius: 4,
  minHeight: 320,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  marginBottom: 12,
};

export default function SensorDataPage({
  embedded = false,
  fixedBridges = null,
  externalBridgeId = null,
  hideBridgeDetails = false,
} = {}) {
  const [bridges,        setBridges]        = useState(() => (embedded && fixedBridges ? fixedBridges : []));
  const [selectedBridge, setSelectedBridge] = useState(null);
  const [selectedDataLoggerId, setSelectedDataLoggerId] = useState('');
  const [selectedSensorType, setSelectedSensorType] = useState('');
  const [selectedSensors,setSelectedSensors]= useState([]);
  const [sensorData,     setSensorData]     = useState([]);
  const [loggerVideoReadings, setLoggerVideoReadings] = useState([]);
  const [loading,        setLoading]        = useState(() => !embedded);
  const [dataLoading,    setDataLoading]    = useState(false);
  const [videoLoading,   setVideoLoading]   = useState(false);
  const [videoTab,       setVideoTab]       = useState('event');
  const [eventVideos,    setEventVideos]    = useState([]);
  const [healthVideos,   setHealthVideos]   = useState([]);
  const [eventVideoPage, setEventVideoPage] = useState(1);
  const [healthVideoPage, setHealthVideoPage] = useState(1);
  const [settingsOpen,   setSettingsOpen]   = useState(false);
  const [startDate,      setStartDate]      = useState(sevenAgo);
  const [endDate,        setEndDate]        = useState(today);
  const [logbook,        setLogbook]        = useState([]);
  const [sensorThresholdLogs, setSensorThresholdLogs] = useState({});
  const [videoModal,     setVideoModal]     = useState(null);
  const [confirmModal,   setConfirmModal]   = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [specForm,       setSpecForm]       = useState(EMPTY_SPEC_FORM);
  const [specDraft,      setSpecDraft]      = useState({});
  const [logDates,       setLogDates]       = useState([]);
  const [selectedLogDate, setSelectedLogDate] = useState(null);
  const [baselineScript, setBaselineScript] = useState(DEFAULT_BASELINE_SCRIPT);
  const [baselineValues, setBaselineValues] = useState({ xAxis: '', yAxis: '', zAxis: '' });
  const [scriptMenuOpen, setScriptMenuOpen] = useState(false);
  const [scriptEditorOpen, setScriptEditorOpen] = useState(false);
  const [runningBaseline, setRunningBaseline] = useState(false);
  const [hardwareStatus, setHardwareStatus] = useState(null);
  const [, setHardwareThresholds] = useState(null);
  const [hardwareLoading, setHardwareLoading] = useState(false);
  const [, setHardwareError] = useState(null);
  /** Device-level Resensys event trigger (mg) — one number for all axes; DID=sensorSerialNo, SID=dataLoggerSerialNo */
  const [hardwareTriggerMg, setHardwareTriggerMg] = useState('');
  const [, setHardwareTriggerSaving] = useState(false);
  const [eventTriggerThreshold, setEventTriggerThreshold] = useState(null);
  const [eventTriggerThresholdLoading, setEventTriggerThresholdLoading] = useState(false);
  const scriptMenuRef = useRef(null);
  const baselineScriptInputRef = useRef(null);
  const [chartAxisView,  setChartAxisView]  = useState('all');
  const [accelChartYDomain, setAccelChartYDomain] = useState(null);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [thresholdUploadOpen, setThresholdUploadOpen] = useState(false);
  const [thresholdUploading, setThresholdUploading] = useState(false);
  const [temperatureUnit, setTemperatureUnit] = useState('fahrenheit');
  const [bridgeDetailsExpanded, setBridgeDetailsExpanded] = useState(false);
  const downloadMenuRef = useRef(null);
  const thresholdUploadInputRef = useRef(null);
  const chartFollowLiveRef = useRef(true);
  const chartBlockRef = useRef(null);
  const [liveChartAxisWidth, setLiveChartAxisWidth] = useState(0);
  const [tempChartAxisWidth, setTempChartAxisWidth] = useState(0);
  const chartWindowRef = useRef({ startIndex: 0, endIndex: 0 });
  const chartSeriesLenRef = useRef(0);
  const selectedBridgeRef = useRef(null);
  const selectedSensorsRef = useRef([]);
  const selectedSensorIdsRef = useRef([]);
  const selectedSensorSerialsRef = useRef([]);
  const hasSensorSelectionRef = useRef(false);
  const latestSensorDataRef = useRef({});
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  const [chartWindow, setChartWindow] = useState({ startIndex: 0, endIndex: 0 });
  const [chartExtraRightSlots, setChartExtraRightSlots] = useState(0);
  const chartExtraRightSlotsRef = useRef(0);
  const tempChartFollowLiveRef = useRef(true);
  const tempChartBlockRef = useRef(null);
  const tempChartWindowRef = useRef({ startIndex: 0, endIndex: 0 });
  const tempChartSeriesLenRef = useRef(0);
  const [tempChartWindow, setTempChartWindow] = useState({ startIndex: 0, endIndex: 0 });
  const [tempChartExtraRightSlots, setTempChartExtraRightSlots] = useState(0);
  const tempChartExtraRightSlotsRef = useRef(0);
  const lastExternalBridgeIdRef = useRef(null);
  const bridgesRef = useRef(bridges);
  const selectSeqRef = useRef(0);
  const skipNextVideosFetchRef = useRef(false);
  const autoSelectedFromStateRef = useRef(null);
  const pendingMapSensorRef = useRef(null);
  const videoColorReadingsRef = useRef([]);
  const [pendingBridgeId, setPendingBridgeId] = useState('');
  const [bridgeSwitchLoading, setBridgeSwitchLoading] = useState(false);

  const { user } = useAuth();
  const { showToast } = useNotify();
  const latestSensorData = useSelector(selectLatestSensorDataBySensorId);
  const socketConnected = useSelector(selectSensorSocketConnected);
  const location = useLocation();
  const multiSel  = selectedSensors.length > 1;
  const sensor    = selectedSensors.length === 1 && selectedBridge
    ? selectedBridge.sensors[selectedSensors[0]] : null;
  const liveReadingSeverity = sensor?._id
    ? latestSensorData[String(sensor._id)]?.severity
    : null;
  const showCriticalLiveBanner = !multiSel && isCriticalSeverity(liveReadingSeverity);
  const hasSensorSelection = Boolean(selectedDataLoggerId && selectedSensorType && selectedSensors.length > 0);
  // Checkbox updates immediately. Chart mount and history prep follow after paint,
  // so a click on a serial number is not stuck behind the loading render.
  const deferredSelectedSensors = useDeferredValue(selectedSensors);
  const showSensorCharts = Boolean(
    selectedDataLoggerId && selectedSensorType && deferredSelectedSensors.length > 0
  );
  const selectedSensorMeta = useMemo(
    () => getSelectedSensorMeta(selectedBridge, selectedSensors),
    [selectedBridge, selectedSensors]
  );
  const ackChartSensors = useMemo(
    () => (selectedBridge?.sensors && selectedSensors.length
      ? selectedSensors.map((idx) => selectedBridge.sensors[idx]).filter(Boolean)
      : []),
    [selectedBridge, selectedSensors]
  );
  const acknowledgeThresholdOnChart = useAcknowledgeThresholdOnChart({
    sensors: ackChartSensors,
    latestByKey: latestSensorData,
  });
  selectedBridgeRef.current = selectedBridge;
  bridgesRef.current = bridges;
  selectedSensorsRef.current = selectedSensors;
  selectedSensorIdsRef.current = selectedSensorMeta.ids;
  selectedSensorSerialsRef.current = selectedSensorMeta.serials;
  hasSensorSelectionRef.current = hasSensorSelection;
  latestSensorDataRef.current = latestSensorData;
  startDateRef.current = startDate;
  endDateRef.current = endDate;
  const sensorLocationValue = !hasSensorSelection
    ? ''
    : multiSel
      ? 'Multiple'
      : (sensor?.manualLocation || '');

  const resetBridgeDependentState = useCallback(() => {
    setSelectedDataLoggerId('');
    setSelectedSensorType('');
    setSelectedSensors([]);
    setSensorData([]);
    videoColorReadingsRef.current = [];
    setSettingsOpen(false);
    setSpecForm(EMPTY_SPEC_FORM);
    setSpecDraft({});
    setLogDates([]);
    setSelectedLogDate(null);
    setBaselineScript(DEFAULT_BASELINE_SCRIPT);
    setBaselineValues({ xAxis: '', yAxis: '', zAxis: '' });
    setScriptMenuOpen(false);
    setScriptEditorOpen(false);
    setRunningBaseline(false);
    setHardwareStatus(null);
    setHardwareThresholds(null);
    setHardwareLoading(false);
    setHardwareError(null);
    setHardwareTriggerMg('');
    setHardwareTriggerSaving(false);
    setChartAxisView('all');
    setAccelChartYDomain(null);
    setLogbook([]);
    setVideoModal(null);
    setVideoTab('event');
    setEventVideos([]);
    setHealthVideos([]);
    setEventVideoPage(1);
    setHealthVideoPage(1);
    setDownloadMenuOpen(false);
    setThresholdUploadOpen(false);
  }, []);

  const selectBridge = useCallback(async (id) => {
    const seq = ++selectSeqRef.current;
    resetBridgeDependentState();
    if (!id) {
      skipNextVideosFetchRef.current = false;
      setSelectedBridge(null);
      setPendingBridgeId('');
      setBridgeSwitchLoading(false);
      return;
    }
    const idStr = String(id);
    const fallback = bridgesRef.current.find((b) => String(b._id) === idStr) || null;
    setPendingBridgeId(idStr);
    setSelectedBridge(fallback);
    setBridgeSwitchLoading(true);
    loadChartHistoryRows(idStr, startDateRef.current, endDateRef.current).catch(() => {});

    const eventParams = {
      bridgeId: idStr,
    };

    try {
      const [detailResult, eventResult] = await Promise.allSettled([
        bridgeService.fetchDetail(idStr),
        videoService.listEventVideos(eventParams),
      ]);
      if (seq !== selectSeqRef.current) return;

      const full = (detailResult.status === 'fulfilled' && detailResult.value)
        ? detailResult.value
        : fallback;

      let nextEventVideos = [];
      if (eventResult.status === 'fulfilled') {
        nextEventVideos = eventResult.value.videos || [];
        logVideo('fetchVideos — event API OK', {
          count: eventResult.value.count,
          videosLength: nextEventVideos.length,
          syncWarning: eventResult.value.syncWarning || null,
          sample: nextEventVideos.slice(0, 3).map(summarizeVideo),
        });
      } else {
        const errMsg = eventResult.reason?.message;
        logVideo('fetchVideos — event API FAILED', {
          message: errMsg,
          isAwsNoise: isAwsOrSyncNoise(errMsg),
        });
        if (/too many requests|rate limit/i.test(errMsg || '')) {
          showToast('Too many requests. Video list will retry when you refresh.', 'error');
        }
        if (!isAwsOrSyncNoise(errMsg) && !/timeout/i.test(errMsg || '')) {
          nextEventVideos = [];
        }
      }

      if (!full) {
        skipNextVideosFetchRef.current = false;
        setSelectedBridge(null);
        setPendingBridgeId('');
        setBridgeSwitchLoading(false);
        return;
      }

      skipNextVideosFetchRef.current = true;
      setSelectedBridge(full);
      setBridges((prev) => prev.map((b) => (String(b._id) === idStr ? full : b)));
      setEventVideos(nextEventVideos);
      setHealthVideos([]);
      setPendingBridgeId('');
      setBridgeSwitchLoading(false);

      if (nextEventVideos.length === 0) {
        window.setTimeout(() => {
          if (seq !== selectSeqRef.current) return;
          videoService.listEventVideos(eventParams).then((retry) => {
            if (seq !== selectSeqRef.current) return;
            const retried = retry.videos || [];
            if (retried.length) setEventVideos(retried);
          }).catch(() => {});
        }, 2500);
      }
    } catch (err) {
      if (seq !== selectSeqRef.current) return;
      logVideo('fetchVideos — unexpected error', { message: err?.message });
      if (fallback) {
        skipNextVideosFetchRef.current = true;
        setSelectedBridge(fallback);
        setEventVideos([]);
        setHealthVideos([]);
      } else {
        skipNextVideosFetchRef.current = false;
        setSelectedBridge(null);
      }
      setPendingBridgeId('');
      setBridgeSwitchLoading(false);
    }
  }, [resetBridgeDependentState, showToast]);

  useEffect(() => {
    if (!embedded || !fixedBridges) return;
    setBridges(fixedBridges);
    setLoading(false);
  }, [embedded, fixedBridges]);

  // Load bridges (initial) — no bridge pre-selected
  useEffect(() => {
    if (embedded) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const d = await bridgeService.getAll();
        const list = filterBridgesByRole(d.bridges || [], user);
        if (cancelled) return;
        setBridges(list);
      } catch (e) {
        if (!cancelled) showToast(e.message, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [embedded, user, showToast]);

  useEffect(() => {
    if (embedded) return;
    const bridgeId = location.state?.bridgeId;
    if (!bridgeId || loading || !bridges.length) return;
    if (!bridges.some((b) => String(b._id) === String(bridgeId))) return;
    const sensorId = location.state?.sensorId || '';
    const sensorSerialNo = location.state?.sensorSerialNo || '';
    const sensorType = location.state?.sensorType || '';
    const dataLoggerId = location.state?.dataLoggerId || '';
    const dataLoggerSerialNo = location.state?.dataLoggerSerialNo || '';
    const token = `${bridgeId}|${sensorId}|${sensorSerialNo}`;
    if (autoSelectedFromStateRef.current === token) return;
    autoSelectedFromStateRef.current = token;
    pendingMapSensorRef.current = (sensorId || sensorSerialNo)
      ? { bridgeId, sensorId, sensorSerialNo, sensorType, dataLoggerId, dataLoggerSerialNo }
      : null;
    selectBridge(bridgeId);
  }, [
    embedded,
    location.state?.bridgeId,
    location.state?.sensorId,
    location.state?.sensorSerialNo,
    location.state?.sensorType,
    location.state?.dataLoggerId,
    location.state?.dataLoggerSerialNo,
    loading,
    bridges,
    selectBridge,
  ]);

  useEffect(() => {
    if (embedded) return;
    const pending = pendingMapSensorRef.current;
    if (!pending || bridgeSwitchLoading || !selectedBridge?.sensors) return;
    if (String(selectedBridge._id) !== String(pending.bridgeId)) return;

    const idx = selectedBridge.sensors.findIndex((s) => {
      if (pending.sensorId && String(s._id) === String(pending.sensorId)) return true;
      if (pending.sensorSerialNo && String(s.sensorSerialNo) === String(pending.sensorSerialNo)) return true;
      return false;
    });
    if (idx < 0) {
      pendingMapSensorRef.current = null;
      return;
    }

    const target = selectedBridge.sensors[idx];
    const rawLoggerId = pending.dataLoggerId || target.dataLoggerId;
    let loggerId = String(
      rawLoggerId && typeof rawLoggerId === 'object'
        ? (rawLoggerId._id || '')
        : (rawLoggerId || '')
    );
    const loggers = selectedBridge.dataLoggers || [];
    if (loggerId && !loggers.some((dl) => String(dl._id) === loggerId)) {
      const bySerial = loggers.find((dl) => String(dl.serialNo) === loggerId);
      if (bySerial) loggerId = String(bySerial._id);
    }
    if (!loggerId && pending.dataLoggerSerialNo) {
      const bySerial = loggers.find((dl) => String(dl.serialNo) === String(pending.dataLoggerSerialNo));
      if (bySerial) loggerId = String(bySerial._id);
    }
    const sensorType = String(pending.sensorType || target.sensorType || '');
    const loggerReady = String(selectedDataLoggerId) === loggerId;
    const typeReady = selectedSensorType === sensorType;
    const sensorReady = selectedSensors.length === 1 && selectedSensors[0] === idx;

    if (loggerReady && typeReady && sensorReady) {
      // Keep pending one extra pass so the data-logger reset effect does not
      // clear type/sensor after this apply.
      if (pending.settled) {
        pendingMapSensorRef.current = null;
      } else {
        pendingMapSensorRef.current = { ...pending, settled: true };
      }
      return;
    }

    if (!loggerReady) setSelectedDataLoggerId(loggerId);
    if (!typeReady) setSelectedSensorType(sensorType);
    if (!sensorReady) setSelectedSensors([idx]);
  }, [
    embedded,
    selectedBridge,
    bridgeSwitchLoading,
    selectedDataLoggerId,
    selectedSensorType,
    selectedSensors,
  ]);

  useEffect(() => {
    if (!embedded || !externalBridgeId) return;
    const id = String(externalBridgeId);
    if (lastExternalBridgeIdRef.current === id) return;
    const bridgeList = fixedBridges || bridges;
    if (!bridgeList.some((b) => String(b._id) === id)) return;
    lastExternalBridgeIdRef.current = id;
    selectBridge(id);
  }, [embedded, externalBridgeId, fixedBridges, bridges, selectBridge]);

  const chartFetchActiveRef = useRef(false);

  // Load sensor history for the live chart (after sensor pick) and Event Video colors (after bridge pick)
  const fetchData = useCallback(async () => {
    if (!selectedBridge) return;
    const forChart = hasSensorSelection;
    if (forChart) {
      chartFetchActiveRef.current = true;
      setDataLoading(true);
    }
    try {
      const { ids: selectedIds, serials: selectedSerials } = getSelectedSensorMeta(selectedBridge, selectedSensors);
      const sId = forChart && selectedIds.length === 1 ? selectedIds[0] : undefined;
      const historyParams = {
        granularity: 'minute',
        limit: SENSOR_DATA_FETCH_LIMIT,
        ...(sId && { sensorId: sId }),
      };
      const chunkByDay = !forChart || Boolean(sId);
      if (forChart && sId) {
        await sensorService.getData(selectedBridge._id, {
          ...historyParams,
          startDate: toApiStartIso(startDate),
          endDate: toApiEndIso(endDate),
          ensureBursts: '1',
        }, { timeout: 120000 }).catch(() => null);
      }
      const historyPromise = chunkByDay
        ? loadChartHistoryRows(selectedBridge._id, startDate, endDate, sId)
        : sensorService.getData(selectedBridge._id, {
          ...historyParams,
          startDate: toApiStartIso(startDate),
          endDate: toApiEndIso(endDate),
        }).then(mapChartApiRows);
      const [apiRows, latestRes] = await Promise.all([
        historyPromise,
        // Live companion only — Mongo latest, never archive
        sensorService.getLatest(selectedBridge._id).catch(() => null),
      ]);
      const latestRows = extractLatestReadings(latestRes)
        .map(normalizeSocketReading)
        .filter((reading) => {
          if (!forChart || !selectedIds.length) return true;
          const readingSensorId = normalizeSocketId(reading.sensorId || reading._id);
          return selectedIds.includes(readingSensorId)
            || (reading.sensorSerialNo && selectedSerials.includes(reading.sensorSerialNo));
        });
      await yieldToMain();
      startTransition(() => {
        setSensorData((prev) => {
          const cachedLive = prev.filter((d) => isTimestampInDateRange(d.timestamp, startDate, endDate));
          return combineAndAggregateReadings([apiRows, latestRows, cachedLive], startDate, endDate);
        });
        if (forChart) setDataLoading(false);
      });
    } catch (e) {
      showToast(e.message, 'error');
      if (forChart) setDataLoading(false);
    }
    finally {
      if (forChart) chartFetchActiveRef.current = false;
    }
  }, [selectedBridge, hasSensorSelection, selectedSensors, startDate, endDate, showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const thresholdLogSensorKey = (selectedBridge?.sensors || [])
    .map((sensor) => normalizeSocketId(sensor._id))
    .filter(Boolean)
    .sort()
    .join('|');

  useEffect(() => {
    const bridgeId = selectedBridge?._id ? String(selectedBridge._id) : '';
    const sensors = selectedBridge?.sensors || [];
    if (!bridgeId || !thresholdLogSensorKey) {
      setSensorThresholdLogs({});
      return undefined;
    }
    let cancelled = false;
    const ids = thresholdLogSensorKey.split('|');
    Promise.all(ids.map(async (sensorId) => {
      try {
        const data = await bridgeService.getLogbook(bridgeId, sensorId);
        const logs = data?.logs || data?.data?.logs || [];
        return [sensorId, Array.isArray(logs) ? logs : []];
      } catch {
        return [sensorId, []];
      }
    })).then((pairs) => {
      if (cancelled) return;
      const next = {};
      pairs.forEach(([sensorId, logs]) => {
        next[sensorId] = logs;
      });
      sensors.forEach((sensor) => {
        const id = normalizeSocketId(sensor._id);
        const serial = sensor?.sensorSerialNo != null ? String(sensor.sensorSerialNo) : '';
        if (id && serial && next[id]) next[serial] = next[id];
      });
      setSensorThresholdLogs(next);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedBridge?._id, selectedBridge?.sensors, thresholdLogSensorKey]);

  const loggerVideoFetchSeqRef = useRef(0);

  // Event Video colors: other sensors on this logger, plus every other logger,
  // so one spike can paint the same ±2 minute event everywhere.
  // Chart fetch stays the selected sensor. Wait until it finishes so the two do not compete.
  const fetchLoggerVideoReadings = useCallback(async () => {
    const seq = ++loggerVideoFetchSeqRef.current;
    if (dataLoading || chartFetchActiveRef.current) {
      return;
    }
    if (!selectedBridge?._id || !selectedDataLoggerId) {
      setLoggerVideoReadings([]);
      return;
    }
    const selectedIds = selectedSensorMeta.ids;
    const chartIsSingleSensor = hasSensorSelection && selectedIds.length === 1;
    if (!chartIsSingleSensor) {
      setLoggerVideoReadings([]);
      return;
    }
    const colorSensors = (selectedBridge.sensors || []).filter((s) => {
      const id = normalizeSocketId(s._id);
      return id && !selectedIds.includes(id);
    });
    if (!colorSensors.length) {
      setLoggerVideoReadings([]);
      return;
    }
    try {
      const historyRows = (
        await Promise.all(
          colorSensors.map((s) => fetchSensorHistoryRows(
            selectedBridge._id,
            startDate,
            endDate,
            normalizeSocketId(s._id),
          ))
        )
      ).flat();
      const latestRes = await sensorService.getLatest(selectedBridge._id).catch(() => null);
      const colorSensorIds = colorSensors.map((s) => normalizeSocketId(s._id)).filter(Boolean);
      const colorSensorSerials = colorSensors
        .map((s) => s.sensorSerialNo)
        .filter(Boolean);
      const latestRows = extractLatestReadings(latestRes)
        .map(normalizeSocketReading)
        .filter((reading) => readingMatchesSelectedSensors(reading, colorSensorIds, colorSensorSerials));
      if (seq !== loggerVideoFetchSeqRef.current) return;
      const combinedLoggerReadings = combineAndAggregateReadings(
        [historyRows, latestRows],
        startDate,
        endDate,
      );
      await yieldToMain();
      if (seq !== loggerVideoFetchSeqRef.current) return;
      setLoggerVideoReadings(combinedLoggerReadings);
    } catch {
      if (seq !== loggerVideoFetchSeqRef.current) return;
      setLoggerVideoReadings([]);
    }
  }, [
    selectedBridge,
    selectedDataLoggerId,
    hasSensorSelection,
    selectedSensorMeta.ids,
    startDate,
    endDate,
    dataLoading,
  ]);

  useEffect(() => { fetchLoggerVideoReadings(); }, [fetchLoggerVideoReadings]);

  const fetchVideos = useCallback(async () => {
    if (!selectedBridge?._id) return;
    setVideoLoading(true);
    const bridge = selectedBridge;
    const loggerSerial = (bridge.dataLoggers || [])
      .find((dl) => dl.isActive !== false && String(dl._id) === String(selectedDataLoggerId))
      ?.serialNo;

    const eventParams = { bridgeId: bridge._id };
    const healthParams = {
      bridgeId: bridge._id,
    };

    logVideo('fetchVideos — request', {
      eventParams,
      healthParams,
      bridgeName: bridge.name,
      dataLoggersOnBridge: (bridge.dataLoggers || []).map((dl) => dl.serialNo),
    });

    let nextEventVideos = [];
    let nextHealthVideos = [];

    try {
      const [eventResult, healthResult] = await Promise.allSettled([
        videoService.listEventVideos(eventParams),
        selectedDataLoggerId
          ? videoService.listHealthVideos(healthParams)
          : Promise.resolve({ videos: [] }),
      ]);

      await yieldToMain();

      if (eventResult.status === 'fulfilled') {
        nextEventVideos = eventResult.value.videos || [];
        setEventVideos(nextEventVideos);
        logVideo('fetchVideos — event API OK', {
          count: eventResult.value.count,
          videosLength: nextEventVideos.length,
          syncWarning: eventResult.value.syncWarning || null,
          sample: nextEventVideos.slice(0, 3).map(summarizeVideo),
        });
      } else {
        logVideo('fetchVideos — event API FAILED', {
          message: eventResult.reason?.message,
          isAwsNoise: isAwsOrSyncNoise(eventResult.reason?.message),
        });
        if (!isAwsOrSyncNoise(eventResult.reason?.message) && !/timeout/i.test(eventResult.reason?.message || '')) {
          setEventVideos([]);
        }
      }

      if (!selectedDataLoggerId) {
        nextHealthVideos = [];
        setHealthVideos([]);
      } else if (healthResult.status === 'fulfilled') {
        nextHealthVideos = healthResult.value.videos || [];
        setHealthVideos(nextHealthVideos);
        logVideo('fetchVideos — health API OK', {
          count: healthResult.value.count,
          videosLength: nextHealthVideos.length,
          syncWarning: healthResult.value.syncWarning || null,
          sample: nextHealthVideos.slice(0, 3).map(summarizeVideo),
        });
      } else {
        logVideo('fetchVideos — health API FAILED', {
          message: healthResult.reason?.message,
          isAwsNoise: isAwsOrSyncNoise(healthResult.reason?.message),
        });
        if (!isAwsOrSyncNoise(healthResult.reason?.message) && !/timeout/i.test(healthResult.reason?.message || '')) {
          setHealthVideos([]);
        }
      }

      const diagnosis = diagnoseVideoFilters({
        rawEventVideos: nextEventVideos,
        rawHealthVideos: nextHealthVideos,
        selectedDataLoggerSerial: loggerSerial,
      });
      logVideo('fetchVideos — filter diagnosis', diagnosis);

      const filteredEvent = nextEventVideos
        .filter((v) => !loggerSerial || String(v.dataLoggerSerialNo || '').trim() === String(loggerSerial).trim())
        .filter(isEventVideo);
      const filteredHealth = nextHealthVideos
        .filter((v) => !loggerSerial || String(v.dataLoggerSerialNo || '').trim() === String(loggerSerial).trim())
        .filter(isHealthVideo);

      logVideo('fetchVideos — after UI filters', {
        eventShown: filteredEvent.length,
        healthShown: filteredHealth.length,
        eventRows: filteredEvent.slice(0, 5).map(summarizeVideo),
        healthRows: filteredHealth.slice(0, 5).map(summarizeVideo),
      });

      if (nextEventVideos.length > 0 && filteredEvent.length === 0) {
        console.warn(
          '[SHM Video] Backend returned event videos but UI shows none. Check dataLoggerSerialNo match and type/videoLabel.',
          diagnosis
        );
      }
      if (nextHealthVideos.length > 0 && filteredHealth.some((v) => !resolveVideoId(v))) {
        console.warn(
          '[SHM Video] Health videos missing _id — playback will fail. Backend must return MongoDB _id on each video.',
          nextHealthVideos.filter((v) => !resolveVideoId(v)).map(summarizeVideo)
        );
      }
    } catch (err) {
      logVideo('fetchVideos — unexpected error', { message: err?.message });
      if (/too many requests|rate limit/i.test(err?.message || '')) {
        showToast('Too many requests. Video list will retry when you refresh.', 'error');
      }
    } finally {
      setVideoLoading(false);
    }
  }, [selectedBridge, selectedDataLoggerId, showToast]);

  useEffect(() => {
    skipNextVideosFetchRef.current = false;
  }, [selectedDataLoggerId]);

  useEffect(() => {
    if (bridgeSwitchLoading) return;
    if (!selectedBridge?._id) return;
    if (skipNextVideosFetchRef.current) {
      skipNextVideosFetchRef.current = false;
      // Event videos were already loaded with the bridge. Still fetch when a
      // logger is selected so Health Video is not left empty.
      if (!selectedDataLoggerId) return;
    }
    fetchVideos();
  }, [fetchVideos, bridgeSwitchLoading, selectedBridge?._id, selectedDataLoggerId]);

  const prefetchVideoOnHover = useCallback((video) => {
    const videoId = resolveVideoId(video);
    if (!videoId) return;
    videoService.prefetchStream(videoId, video);
  }, []);

  useEffect(() => {
    setEventVideoPage(1);
    setHealthVideoPage(1);
  }, [selectedDataLoggerId, selectedBridge?._id]);

  useEffect(() => {
    setEventVideoPage(1);
  }, [startDate, endDate]);

  useEffect(() => {
    if (!selectedDataLoggerId && videoTab === 'health') {
      setVideoTab('event');
    }
  }, [selectedDataLoggerId, videoTab]);

  const syncCachedLiveReadings = useCallback(() => {
    if (!hasSensorSelectionRef.current) return;
    selectedSensorIdsRef.current.forEach((sensorId) => {
      const reading = latestSensorDataRef.current[sensorId];
      if (reading) mergeLiveReadingRef.current(reading);
    });
  }, []);

  const readingMatchesSelection = useCallback((reading) => {
    const bridge = selectedBridgeRef.current;
    if (!bridge || !hasSensorSelectionRef.current) return false;
    if (normalizeSocketId(reading.bridgeId) !== normalizeSocketId(bridge._id)) return false;

    const readingSensorId = normalizeSocketId(reading.sensorId || reading._id);
    if (readingSensorId && selectedSensorIdsRef.current.includes(readingSensorId)) return true;

    return Boolean(
      reading.sensorSerialNo
      && selectedSensorSerialsRef.current.includes(reading.sensorSerialNo)
    );
  }, []);

  const mergeLiveReading = useCallback((reading) => {
    if (!reading || !readingMatchesSelection(reading)) return;

    const ts = new Date(reading.timestamp);
    if (Number.isNaN(ts.getTime())) return;
    if (!isTimestampInDateRange(reading.timestamp, startDateRef.current, endDateRef.current)) return;

    const normalized = normalizeSocketReading(reading);
    const incomingKey = readingPointKey(normalized);
    setSensorData((prev) => {
      const samples = prev.filter((d) => d.temperatureSample === true);
      const rest = samples.length ? prev.filter((d) => d.temperatureSample !== true) : prev;
      const existingIdx = rest.findIndex((d) => readingPointKey(d) === incomingKey);
      const structural = existingIdx >= 0
        ? rest.map((row, index) => (
          index === existingIdx ? mergeAveragedReading(row, normalized) : row
        ))
        : dedupeReadingsPerMinute([...rest, normalized]);
      return samples.length ? [...samples, ...structural] : structural;
    });
  }, [readingMatchesSelection]);

  const mergeLiveReadingRef = useRef(mergeLiveReading);
  mergeLiveReadingRef.current = mergeLiveReading;
  const syncCachedLiveReadingsRef = useRef(syncCachedLiveReadings);
  syncCachedLiveReadingsRef.current = syncCachedLiveReadings;

  // Bridge changes drop the previous bridge's readings. Sensor and logger
  // picks keep the rows already loaded so the chart can draw immediately.
  useEffect(() => {
    setSensorData([]);
  }, [selectedBridge?._id]);

  // Apply cached live readings when selection is complete or socket cache updates
  useEffect(() => {
    if (!hasSensorSelection || !selectedBridge?._id) return;
    syncCachedLiveReadingsRef.current();
  }, [
    hasSensorSelection,
    selectedBridge?._id,
    selectedDataLoggerId,
    selectedSensorType,
    selectedSensors,
    latestSensorData,
  ]);

  useEffect(() => {
    if (!socketConnected || !hasSensorSelection) return;
    syncCachedLiveReadingsRef.current();
  }, [socketConnected, hasSensorSelection]);

  // Reset sensor filters when data logger changes
  useEffect(() => {
    if (pendingMapSensorRef.current) return;
    setSelectedSensorType('');
    setSelectedSensors([]);
    setSettingsOpen(false);
  }, [selectedDataLoggerId]);

  // Drop stale sensor indices when sensor type / logger / bridge changes
  useEffect(() => {
    if (pendingMapSensorRef.current) return;
    if (!selectedSensorType || !selectedDataLoggerId) {
      setSelectedSensors([]);
      return;
    }
    setSelectedSensors((prev) =>
      prev.filter((idx) => {
        const s = selectedBridge?.sensors?.[idx];
        return s
          && s.sensorType === selectedSensorType
          && String(s.dataLoggerId) === String(selectedDataLoggerId);
      })
    );
  }, [selectedSensorType, selectedDataLoggerId, selectedBridge]);

  useEffect(() => {
    const closeMenus = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setDownloadMenuOpen(false);
      }
      if (scriptMenuRef.current && !scriptMenuRef.current.contains(e.target)) {
        setScriptMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', closeMenus);
    return () => document.removeEventListener('mousedown', closeMenus);
  }, []);

  const selectedChartSensorIds = useMemo(
    () => selectedSensorMeta.ids,
    [selectedSensorMeta]
  );
  const selectedChartSensorSerials = useMemo(
    () => selectedSensorMeta.serials,
    [selectedSensorMeta]
  );
  const chartTimeZone = useMemo(
    () => getBridgeTimeZone(selectedBridge),
    [selectedBridge]
  );

  const chartSensorDescriptors = useMemo(
    () => selectedSensors
      .map((idx, i) => {
        const s = selectedBridge?.sensors?.[idx];
        if (!s) return null;
        const id = normalizeSocketId(s._id);
        return {
          id,
          serial: s.sensorSerialNo || `Sensor ${i + 1}`,
          color: SENSOR_CHART_COLORS[i % SENSOR_CHART_COLORS.length],
        };
      })
      .filter(Boolean),
    [selectedBridge, selectedSensors]
  );

  // Chart prep is hidden until a sensor is picked. Building it for every
  // bridge reading (an empty filter matches all rows) freezes the dropdowns.
  const deferredSensorData = useDeferredValue(sensorData);
  const deferredLoggerVideoReadings = useDeferredValue(loggerVideoReadings);
  const deferredEventVideos = useDeferredValue(eventVideos);
  const deferredSensorThresholdLogs = useDeferredValue(sensorThresholdLogs);

  const deferredChartSensorMeta = useMemo(
    () => getSelectedSensorMeta(selectedBridge, deferredSelectedSensors),
    [selectedBridge, deferredSelectedSensors]
  );

  const accelSourceRows = useMemo(
    () => deferredSensorData.filter((d) => d.temperatureSample !== true || readingHasAccelAxes(d)),
    [deferredSensorData]
  );

  const chartSeries = useMemo(() => {
    if (!showSensorCharts || !deferredChartSensorMeta.ids.length) return [];
    if (multiSel) {
      return buildPivotAccelChartSeries(
        accelSourceRows,
        deferredChartSensorMeta.ids,
        startDate,
        endDate,
        chartTimeZone,
      );
    }
    return fillSingleSensorAccelGaps(
      joinBurstSpillover(
        dedupeReadingsPerMinute(
          accelSourceRows.filter((d) => (
            isTimestampInChartDateRange(d.timestamp, startDate, endDate, chartTimeZone)
            && readingHasAccelAxes(d)
            && readingMatchesSelectedSensors(
              d,
              deferredChartSensorMeta.ids,
              deferredChartSensorMeta.serials,
            )
          ))
        )
      ).map((d, i) => {
        const stamp = burstAnchorTimestamp(d);
        const samplesFor = (field) => gatherAxisSamples([d], field)
          .map((sample) => snapSpilloverSample(sample, stamp));
        const axisPoint = (field) => chartAxisValue(d[field]) ?? firstFiniteSample(samplesFor(field));
        return {
          pointIndex: i,
          chartKey: readingPointKey({ ...d, timestamp: stamp }),
          timestamp: stamp,
          xAxis: axisPoint('xAxis'),
          yAxis: axisPoint('yAxis'),
          zAxis: axisPoint('zAxis'),
          xAxisSamples: samplesFor('xAxis'),
          yAxisSamples: samplesFor('yAxis'),
          zAxisSamples: samplesFor('zAxis'),
          value: chartAxisValue(d.value),
        };
      })
    );
  }, [
    showSensorCharts,
    accelSourceRows,
    deferredChartSensorMeta,
    startDate,
    endDate,
    multiSel,
    chartTimeZone,
  ]);

  const chartLiveTailKey = chartSeries[chartSeries.length - 1]?.chartKey ?? '';

  chartSeriesLenRef.current = chartSeries.length;
  chartWindowRef.current = chartWindow;
  chartExtraRightSlotsRef.current = chartExtraRightSlots;

  const visibleChartSeries = useMemo(() => {
    if (!chartSeries.length) return [];
    const start = Math.min(chartWindow.startIndex, chartSeries.length - 1);
    const end = Math.min(Math.max(chartWindow.endIndex, start), chartSeries.length - 1);
    return chartSeries.slice(start, end + 1);
  }, [chartSeries, chartWindow]);

  const accelChartWindowStart = visibleChartSeries[0]?.pointIndex ?? 0;
  const accelChartWindowEnd = visibleChartSeries[visibleChartSeries.length - 1]?.pointIndex ?? 0;

  const temperatureSeries = useMemo(() => {
    if (!showSensorCharts || !deferredChartSensorMeta.ids.length) return [];
    const matched = deferredSensorData.filter((d) => (
      d.temperature != null
      && isTimestampInChartDateRange(d.timestamp, startDate, endDate, chartTimeZone)
      && readingMatchesSelectedSensors(
        d,
        deferredChartSensorMeta.ids,
        deferredChartSensorMeta.serials,
      )
    ));
    const exact = matched.filter((d) => d.temperatureSample === true);
    if (multiSel) {
      return buildPivotTemperatureChartSeries(
        exact.length ? exact : deferredSensorData,
        deferredChartSensorMeta.ids,
        startDate,
        endDate,
        temperatureUnit,
        chartTimeZone,
      );
    }
    const source = exact.length
      ? [...exact].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      : dedupeReadingsPerMinute(matched);
    return source.map((d, i) => ({
        pointIndex: i,
        chartKey: readingPointKey(d),
        timestamp: d.timestamp,
        temperature: toDisplayTemperature(d.temperature, temperatureUnit),
      }));
  }, [
    showSensorCharts,
    deferredSensorData,
    deferredChartSensorMeta,
    temperatureUnit,
    startDate,
    endDate,
    multiSel,
    chartTimeZone,
  ]);

  const tempChartLiveTailKey = temperatureSeries[temperatureSeries.length - 1]?.chartKey ?? '';

  tempChartSeriesLenRef.current = temperatureSeries.length;
  tempChartWindowRef.current = tempChartWindow;
  tempChartExtraRightSlotsRef.current = tempChartExtraRightSlots;

  const visibleTemperatureSeries = useMemo(() => {
    if (!temperatureSeries.length) return [];
    const start = Math.min(tempChartWindow.startIndex, temperatureSeries.length - 1);
    const end = Math.min(Math.max(tempChartWindow.endIndex, start), temperatureSeries.length - 1);
    return temperatureSeries.slice(start, end + 1);
  }, [temperatureSeries, tempChartWindow]);

  const tempChartWindowStart = visibleTemperatureSeries[0]?.pointIndex ?? 0;
  const tempChartWindowEnd = visibleTemperatureSeries[visibleTemperatureSeries.length - 1]?.pointIndex ?? 0;

  useEffect(() => {
    chartFollowLiveRef.current = true;
    tempChartFollowLiveRef.current = true;
    setChartExtraRightSlots(0);
    setTempChartExtraRightSlots(0);
  }, [sensor?._id, selectedBridge?._id, startDate, endDate, selectedSensors]);

  useEffect(() => {
    const len = chartSeries.length;
    if (!len) {
      setChartWindow({ startIndex: 0, endIndex: 0 });
      return;
    }
    if (chartFollowLiveRef.current && chartExtraRightSlotsRef.current !== 0) {
      setChartExtraRightSlots(0);
    }
    setChartWindow((prev) => {
      const windowSize = getLiveDataChartWindowSize(len);
      if (chartFollowLiveRef.current) {
        return {
          startIndex: Math.max(0, len - windowSize),
          endIndex: len - 1,
        };
      }
      const viewportSize = getLiveDataChartWindowSize(len);
      const prevWindowSize = Math.min(
        getWindowPointCount(prev.startIndex, prev.endIndex),
        viewportSize,
      );
      const endIndex = Math.min(prev.endIndex, len - 1);
      return {
        startIndex: Math.max(0, endIndex - prevWindowSize + 1),
        endIndex,
      };
    });
  }, [chartSeries.length, chartLiveTailKey, selectedBridge?._id, startDate, endDate, selectedSensors]);

  useEffect(() => {
    const len = temperatureSeries.length;
    if (!len) {
      setTempChartWindow({ startIndex: 0, endIndex: 0 });
      return;
    }
    if (tempChartFollowLiveRef.current && tempChartExtraRightSlotsRef.current !== 0) {
      setTempChartExtraRightSlots(0);
    }
    setTempChartWindow((prev) => {
      const windowSize = getLiveDataChartWindowSize(len);
      if (tempChartFollowLiveRef.current) {
        return {
          startIndex: Math.max(0, len - windowSize),
          endIndex: len - 1,
        };
      }
      const viewportSize = getLiveDataChartWindowSize(len);
      const prevWindowSize = Math.min(
        getWindowPointCount(prev.startIndex, prev.endIndex),
        viewportSize,
      );
      const endIndex = Math.min(prev.endIndex, len - 1);
      return {
        startIndex: Math.max(0, endIndex - prevWindowSize + 1),
        endIndex,
      };
    });
  }, [temperatureSeries.length, tempChartLiveTailKey, selectedBridge?._id, temperatureUnit, startDate, endDate, selectedSensors]);

  const shiftChartWindow = useCallback((pointShift) => {
    const len = chartSeriesLenRef.current;
    if (len <= 1 || !pointShift) return;
    const viewportSize = getLiveDataChartWindowSize(len);
    setChartWindow((prev) => {
      let newStart = prev.startIndex + pointShift;
      let newEnd = prev.endIndex + pointShift;
      if (newStart < 0) {
        newEnd -= newStart;
        newStart = 0;
      }
      if (newEnd >= len) {
        newEnd = len - 1;
        newStart = Math.max(0, len - viewportSize);
      }
      if (newStart === prev.startIndex && newEnd === prev.endIndex) return prev;
      chartFollowLiveRef.current = newEnd >= len - 1 && chartExtraRightSlotsRef.current === 0;
      return { startIndex: newStart, endIndex: newEnd };
    });
  }, []);

  const shiftTempChartWindow = useCallback((pointShift) => {
    const len = tempChartSeriesLenRef.current;
    if (len <= 1 || !pointShift) return;
    const viewportSize = getLiveDataChartWindowSize(len);
    setTempChartWindow((prev) => {
      let newStart = prev.startIndex + pointShift;
      let newEnd = prev.endIndex + pointShift;
      if (newStart < 0) {
        newEnd -= newStart;
        newStart = 0;
      }
      if (newEnd >= len) {
        newEnd = len - 1;
        newStart = Math.max(0, len - viewportSize);
      }
      if (newStart === prev.startIndex && newEnd === prev.endIndex) return prev;
      tempChartFollowLiveRef.current = newEnd >= len - 1 && tempChartExtraRightSlotsRef.current === 0;
      return { startIndex: newStart, endIndex: newEnd };
    });
  }, []);

  useEffect(() => {
    const el = tempChartBlockRef.current;
    if (!el || !hasSensorSelection || temperatureSeries.length < 1) return undefined;
    return attachChartPanListeners({
      el,
      seriesLenRef: tempChartSeriesLenRef,
      windowRef: tempChartWindowRef,
      setWindow: setTempChartWindow,
      followLiveRef: tempChartFollowLiveRef,
      shiftWindow: shiftTempChartWindow,
      extraRightSlotsRef: tempChartExtraRightSlotsRef,
      setExtraRightSlots: setTempChartExtraRightSlots,
    });
  }, [hasSensorSelection, showSensorCharts, shiftTempChartWindow, temperatureSeries.length]);

  const chartAxisKey = chartAxisView === 'all' ? 'xAxis' : chartAxisView;
  const chartBaseline = toFiniteNumber(sensor?.specLimits?.baselineValues?.[chartAxisKey]) ?? 0;
  const isAccelerometerChart = (sensor?.sensorType || selectedSensorType) === 'Accelerometer';
  const accelSeriesDense = visibleChartSeries.length > CHART_DOT_DETAIL_LIMIT;
  const tempSeriesDense = visibleTemperatureSeries.length > CHART_DOT_DETAIL_LIMIT;
  const liveChartSensorType = sensor?.sensorType || selectedSensorType || '';
  const liveChartYUnit = liveChartSensorType === 'Accelerometer'
    ? '(mg)'
    : liveChartSensorType === 'Tiltmeter'
      ? '(deg)'
      : '';

  const showChartLine = (axis) => chartAxisView === 'all' || chartAxisView === axis;
  const showAxisThresholdLines = !multiSel;
  const axisThresholdLines = useMemo(
    () => (showAxisThresholdLines ? buildVisibleThresholdLines(sensor, chartAxisView) : []),
    [showAxisThresholdLines, sensor, chartAxisView],
  );

  const accelChartYFullDomain = useMemo(() => {
    const values = [];
    let min = Infinity;
    let max = -Infinity;
    const consider = (value) => {
      if (!Number.isFinite(value)) return;
      if (value < min) min = value;
      if (value > max) max = value;
    };
    axisThresholdLines.forEach((line) => consider(line.y));
    if (!multiSel && chartBaseline !== 0) consider(chartBaseline);
    const axes = chartAxisView === 'all' ? ['xAxis', 'yAxis', 'zAxis'] : [chartAxisView];
    const keys = multiSel
      ? chartSensorDescriptors.flatMap((desc) => axes.map((axis) => sensorChartFieldKey(desc.id, axis)))
      : axes;
    visibleChartSeries.forEach((point) => {
      keys.forEach((key) => {
        consider(chartAxisValue(point[key]));
        if (
          !multiSel
          && (chartAxisView !== 'all' || isAccelerometerChart)
          && Array.isArray(point[`${key}Samples`])
        ) {
          point[`${key}Samples`].forEach((sample) => consider(axisSampleValue(sample)));
        }
      });
    });
    if (min !== Infinity) values.push(min, max);
    return paddedYDomainFromValues(values);
  }, [
    axisThresholdLines,
    chartBaseline,
    chartAxisView,
    chartSensorDescriptors,
    isAccelerometerChart,
    multiSel,
    visibleChartSeries,
  ]);

  const accelChartYFullDomainRef = useRef(null);
  accelChartYFullDomainRef.current = accelChartYFullDomain;

  const activeAccelYDomain = accelChartYDomain || accelChartYFullDomain;
  const liveAccelThresholdLines = showAxisThresholdLines ? axisThresholdLines : [];
  const liveAccelYTicks = mergeThresholdYAxisTicks(
    yAxisTicksFromDomain(activeAccelYDomain),
    liveAccelThresholdLines,
    activeAccelYDomain,
  );

  useLayoutEffect(() => {
    const el = tempChartBlockRef.current;
    if (!el) return undefined;
    const update = () => {
      const next = el.clientWidth || 0;
      setTempChartAxisWidth((prev) => (prev === next ? prev : next));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasSensorSelection, visibleTemperatureSeries.length]);

  useLayoutEffect(() => {
    const el = chartBlockRef.current;
    if (!el) return undefined;
    const update = () => {
      const next = el.clientWidth || 0;
      setLiveChartAxisWidth((prev) => (prev === next ? prev : next));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasSensorSelection, dataLoading, visibleChartSeries.length]);

  const tempChartXAxis = useMemo(() => {
    const plotWidth = tempChartAxisWidth > 0
      ? Math.max(180, tempChartAxisWidth - 48 - 40)
      : 0;
    return buildLiveChartXAxis({
      series: visibleTemperatureSeries,
      startDate,
      endDate,
      timeZone: chartTimeZone,
      plotWidth,
    });
  }, [
    visibleTemperatureSeries,
    startDate,
    endDate,
    chartTimeZone,
    tempChartAxisWidth,
  ]);

  const liveChartXAxis = useMemo(() => {
    const yWidth = accelYAxisWidthFromTicks(activeAccelYDomain, liveAccelYTicks);
    const plotWidth = liveChartAxisWidth > 0
      ? Math.max(180, liveChartAxisWidth - yWidth - 40)
      : 0;
    return buildLiveChartXAxis({
      series: visibleChartSeries,
      startDate,
      endDate,
      timeZone: chartTimeZone,
      plotWidth,
    });
  }, [
    visibleChartSeries,
    startDate,
    endDate,
    chartTimeZone,
    liveChartAxisWidth,
    activeAccelYDomain,
    liveAccelYTicks,
  ]);
  const visibleAxisThresholdLines = !activeAccelYDomain
    ? axisThresholdLines
    : axisThresholdLines.filter((line) => line.y >= activeAccelYDomain[0] && line.y <= activeAccelYDomain[1]);
  const showBaselineLine = !multiSel
    && chartBaseline !== 0
    && (!activeAccelYDomain || (chartBaseline >= activeAccelYDomain[0] && chartBaseline <= activeAccelYDomain[1]));

  const onAccelVerticalZoom = useCallback((event) => {
    setAccelChartYDomain((prev) => zoomYDomainFromWheel(
      event,
      chartBlockRef.current,
      prev,
      accelChartYFullDomainRef.current,
    ));
  }, []);

  useEffect(() => {
    setAccelChartYDomain(null);
  }, [chartAxisView, selectedBridge?._id, selectedSensors, startDate, endDate]);

  useEffect(() => {
    const el = chartBlockRef.current;
    if (!el || !showSensorCharts) return undefined;
    return attachChartPanListeners({
      el,
      seriesLenRef: chartSeriesLenRef,
      windowRef: chartWindowRef,
      setWindow: setChartWindow,
      followLiveRef: chartFollowLiveRef,
      shiftWindow: shiftChartWindow,
      extraRightSlotsRef: chartExtraRightSlotsRef,
      setExtraRightSlots: setChartExtraRightSlots,
      onVerticalZoom: onAccelVerticalZoom,
    });
  }, [showSensorCharts, shiftChartWindow, onAccelVerticalZoom]);

  const loadSettingsLogbook = async (bridgeId, sensorId) => {
    try {
      const data = await bridgeService.getLogbook(bridgeId, sensorId);
      const logs = data.logs || [];
      setLogbook(logs);
      const dates = [...new Set(logs.map((l) => getLogDateKey(l.timestamp)))]
        .sort((a, b) => new Date(b) - new Date(a))
        .slice(0, LOG_DATE_LIMIT);
      setLogDates(dates);
      setSelectedLogDate(null);
      return logs;
    } catch {
      setLogbook([]);
      setLogDates([]);
      setSelectedLogDate(null);
      return [];
    }
  };

  const resolveSensorDataLoggerSerial = (bridge, targetSensor) => {
    if (!bridge || !targetSensor) return '';
    if (targetSensor.dataLoggerSerialNo) return targetSensor.dataLoggerSerialNo;
    const logger = (bridge.dataLoggers || []).find(
      (dl) => dl._id && targetSensor.dataLoggerId && String(dl._id) === String(targetSensor.dataLoggerId)
    );
    return logger?.serialNo || '';
  };

  const loadHardwareThresholds = async (bridgeId, sensorId, axis) => {
    if (!SHOW_RESENSYS_HARDWARE_PANEL) return null;
    try {
      const data = await resensysService.getHardwareThresholds(bridgeId, sensorId, axis);
      setHardwareThresholds(data);
      return data;
    } catch (err) {
      if (err.status === 429 || /rate limit/i.test(err?.message || '')) {
        showToast('Resensys rate limit — try again shortly.', 'error');
      }
      setHardwareThresholds(null);
      if (/no threshold records found/i.test(err?.message || '')) {
        return null;
      }
      throw err;
    }
  };

  const loadHardwareData = async (bridgeId, sensorId, axis) => {
    if (!SHOW_RESENSYS_HARDWARE_PANEL) return;
    setHardwareLoading(true);
    setHardwareError(null);
    try {
      const status = await resensysService.getHardwareStatus(bridgeId, sensorId);
      setHardwareStatus(status);
      const storedMg = status?.hardwareEventTriggerMg
        ?? status?.hardwareEventTrigger?.threshold
        ?? null;
      if (storedMg != null && storedMg !== '') {
        setHardwareTriggerMg(String(storedMg));
      }
      if (SPEC_AXIS_KEYS.includes(axis)) {
        await loadHardwareThresholds(bridgeId, sensorId, axis);
      }
    } catch (err) {
      if (err.status === 429 || /rate limit/i.test(err?.message || '')) {
        showToast('Resensys rate limit — try again shortly.', 'error');
      }
      setHardwareError(err?.message || 'Hardware status unavailable');
      setHardwareStatus(null);
    } finally {
      setHardwareLoading(false);
    }
  };

  const loadEventTriggerThreshold = async (bridgeId, sensorId, targetSensor) => {
    if (!SHOW_RESENSYS_HARDWARE_PANEL) return null;
    setEventTriggerThresholdLoading(true);
    const did = String(targetSensor?.sensorSerialNo || '').trim();
    const loggerSerial = resolveSensorDataLoggerSerial(selectedBridge, targetSensor);
    const loggerParts = String(loggerSerial || '').split(/[-_]/).filter(Boolean);
    const sid = loggerParts.length >= 2
      ? `${loggerParts[loggerParts.length - 2]}-${loggerParts[loggerParts.length - 1]}`
      : String(loggerSerial || '').trim();
    const params = {
      ...(did ? { did } : {}),
      ...(sid ? { sid } : {}),
    };

    const fetchOnce = () =>
      resensysService.getEventTriggerThreshold(bridgeId, sensorId, params);

    try {
      let data;
      try {
        data = await fetchOnce();
      } catch (err) {
        if (err.status === 429 || /rate limit/i.test(err?.message || '')) {
          await sleepMs(EVENT_TRIGGER_THRESHOLD_RETRY_MS);
          data = await fetchOnce();
        } else {
          throw err;
        }
      }
      const value = data?.success === false ? null : (data?.threshold ?? null);
      setEventTriggerThreshold(value);
      return data;
    } catch {
      setEventTriggerThreshold(null);
      return null;
    } finally {
      setEventTriggerThresholdLoading(false);
    }
  };

  const openSettings = async () => {
    if (multiSel) return showToast('Single select a sensor to adjust settings.', 'error');
    if (!sensor) return showToast('Select a single sensor first.', 'error');
    const toDateInput = (d) => (d ? new Date(d).toISOString().split('T')[0] : '');
    const sl = sensor?.specLimits || {};
    setSpecDraft(buildSpecDraftFromSensor(sensor));
    setSpecForm({
      axis: '',
      normalU: '',
      normalL: '',
      elevU: '',
      elevL: '',
      sigU: '',
      sigL: '',
      baselineStart: toDateInput(sl.baselineStartDate),
      baselineEnd: toDateInput(sl.baselineEndDate),
    });
    setBaselineValues(loadBaselineValuesFromSensor(sensor));
    setBaselineScript(sensor?.specLimits?.baselineScript || DEFAULT_BASELINE_SCRIPT);
    setScriptEditorOpen(false);
    setHardwareStatus(null);
    setHardwareThresholds(null);
    setHardwareError(null);
    setEventTriggerThreshold(null);
    setEventTriggerThresholdLoading(SHOW_RESENSYS_HARDWARE_PANEL);
    setHardwareTriggerMg(
      sensor?.hardwareEventTriggerMg != null && sensor.hardwareEventTriggerMg !== ''
        ? String(sensor.hardwareEventTriggerMg)
        : ''
    );
    setSettingsOpen(true);
    await loadSettingsLogbook(selectedBridge._id, sensor._id);
    if (SHOW_RESENSYS_HARDWARE_PANEL) {
      loadHardwareData(selectedBridge._id, sensor._id, '');
      loadEventTriggerThreshold(selectedBridge._id, sensor._id, sensor);
    }
  };

  const onSpecAxisChange = (axisKey) => {
    if (!SPEC_AXIS_KEYS.includes(axisKey)) {
      setSpecForm((prev) => ({
        ...prev,
        axis: '',
        normalU: '',
        normalL: '',
        elevU: '',
        elevL: '',
        sigU: '',
        sigL: '',
      }));
      return;
    }

    const comingFromAxis = SPEC_AXIS_KEYS.includes(specForm.axis);
    const formRow = thresholdRowFromForm(specForm);
    const hasSharedNormal = comingFromAxis && (
      isFilledThresholdNumber(formRow.normalU) || isFilledThresholdNumber(formRow.normalL)
    );
    let updatedDraft = { ...specDraft };
    if (comingFromAxis) {
      SPEC_AXIS_KEYS.forEach((key) => {
        const row = key === specForm.axis
          ? formRow
          : { ...(updatedDraft[key] || emptySpecDraftRow()) };
        updatedDraft[key] = hasSharedNormal
          ? { ...row, normalL: formRow.normalL, normalU: formRow.normalU }
          : row;
      });
      setSpecDraft(updatedDraft);
    }

    const row = updatedDraft[axisKey] || buildSpecDraftFromSensor(sensor)[axisKey] || {};
    setSpecForm((prev) => ({
      ...prev,
      axis: axisKey,
      normalU: hasSharedNormal ? (formRow.normalU ?? '') : (row.normalU ?? ''),
      normalL: hasSharedNormal ? (formRow.normalL ?? '') : (row.normalL ?? ''),
      elevU: row.elevU ?? '',
      elevL: row.elevL ?? '',
      sigU: row.sigU ?? '',
      sigL: row.sigL ?? '',
    }));
    if (row.normalU != null && row.normalU !== '') {
      setHardwareTriggerMg(String(row.normalU));
    }
    if (SHOW_RESENSYS_HARDWARE_PANEL && selectedBridge && sensor) {
      setHardwareLoading(true);
      setHardwareError(null);
      loadHardwareThresholds(selectedBridge._id, sensor._id, axisKey)
        .catch((err) => {
          setHardwareError(err?.message || 'Hardware thresholds unavailable');
        })
        .finally(() => setHardwareLoading(false));
    }
  };

  const buildHardwareTriggerPayload = (pushToDevice) => {
    const mg = parseFloat(hardwareTriggerMg);
    if (!Number.isFinite(mg) || mg <= 0) {
      throw new Error('Enter a valid hardware event trigger (mg) greater than 0.');
    }
    const did = sensor?.sensorSerialNo?.trim();
    const sid = resolveSensorDataLoggerSerial(selectedBridge, sensor);
    if (!did) throw new Error('Sensor serial number (DID) is missing.');
    if (!sid) throw new Error('Data logger serial number (SID) is missing. Link this sensor to a data logger.');
    return {
      hardwareEventTriggerMg: mg,
      pushToDevice: Boolean(pushToDevice),
      did,
      sid,
      threshold: mg,
    };
  };

  const executeSaveHardwareTrigger = async (pushToDevice) => {
    if (!sensor || !selectedBridge) return;
    setHardwareTriggerSaving(true);
    try {
      const payload = buildHardwareTriggerPayload(pushToDevice);
      const res = await resensysService.saveHardwareEventTrigger(
        selectedBridge._id,
        sensor._id,
        payload
      );
      const savedMg = res?.hardwareEventTriggerMg ?? payload.hardwareEventTriggerMg;
      setHardwareTriggerMg(String(savedMg));
      setSelectedBridge((prev) => {
        if (!prev?.sensors) return prev;
        return {
          ...prev,
          sensors: prev.sensors.map((s) =>
            String(s._id) === String(sensor._id)
              ? {
                  ...s,
                  hardwareEventTriggerMg: savedMg,
                  lastHardwarePush: res?.lastHardwarePush ?? s.lastHardwarePush,
                }
              : s
          ),
        };
      });
      if (pushToDevice) {
        showToast(
          res?.note
            || `Hardware trigger ${savedMg} mg pushed. Applies on next gateway contact.`
        );
      } else {
        showToast(`Hardware trigger ${savedMg} mg saved.`);
      }
      if (SHOW_RESENSYS_HARDWARE_PANEL) {
        loadHardwareData(selectedBridge._id, sensor._id, specForm.axis).catch(() => {});
        if (pushToDevice) {
          loadEventTriggerThreshold(selectedBridge._id, sensor._id, sensor).catch(() => {});
        }
      }
    } catch (e) {
      if (e.status === 429 || /rate limit/i.test(e?.message || '')) {
        showToast('Resensys rate limit — try again shortly.', 'error');
      } else {
        showToast(e.message || 'Failed to save hardware trigger.', 'error');
      }
      throw e;
    } finally {
      setHardwareTriggerSaving(false);
    }
  };

  const applyFormToDraft = () => {
    setSpecDraft((prev) => ({
      ...prev,
      [specForm.axis]: thresholdRowFromForm(specForm),
    }));
  };

  /** UI Data point N → upper N, lower -N. Save payload still uses lower/upper. */
  const handleThresholdDataPointChange = (lowerKey, upperKey, value) => {
    const lowerValue = value === '' || value == null
      ? ''
      : (Number.isFinite(Number(value)) ? String(-Number(value)) : value);
    setSpecForm((p) => ({ ...p, [upperKey]: value, [lowerKey]: lowerValue }));
    if (upperKey === 'normalU') setHardwareTriggerMg(value);
  };

  const handleThresholdDataPointBlur = (lowerKey, upperKey, rawValue) => {
    const current = rawValue != null ? rawValue : specForm[upperKey];
    const normal = parseThresholdPoint(upperKey === 'normalU' ? current : specForm.normalU);
    const elevated = parseThresholdPoint(upperKey === 'elevU' ? current : specForm.elevU);
    const significant = parseThresholdPoint(upperKey === 'sigU' ? current : specForm.sigU);
    if (!isFilledThresholdNumber(current)) return;
    if (upperKey === 'normalU' && normal != null && normal < NORMAL_POINT_MIN) {
      showToast('Values below 100 are not allowed.', 'error');
      return;
    }
    if (upperKey === 'elevU' && elevated != null && normal != null && elevated <= normal) {
      showToast('Use a higher value than Normal for Elevated.', 'error');
      return;
    }
    if (upperKey === 'sigU' && significant != null) {
      if (elevated != null && significant <= elevated) {
        showToast('Use a higher value than Elevated for Significant.', 'error');
        return;
      }
      if (elevated == null && normal != null && significant <= normal) {
        showToast('Use a higher value than Normal for Significant.', 'error');
      }
    }
  };

  const handleRunBaseline = async () => {
    if (!sensor || !selectedBridge) return;
    if (!specForm.baselineStart || !specForm.baselineEnd) {
      return showToast('Select baseline start and end dates.', 'error');
    }
    setRunningBaseline(true);
    try {
      const res = await sensorService.getData(selectedBridge._id, {
        sensorId: sensor._id,
        startDate: toApiStartIso(specForm.baselineStart),
        endDate: toApiEndIso(specForm.baselineEnd),
        granularity: 'minute',
        limit: 5000,
      });
      const readings = dedupeReadingsPerMinute(mapChartApiRows(res));
      if (!readings.length) {
        return showToast('No sensor data found for the selected baseline period.', 'error');
      }

      const nextDraft = {};
      const nextBaselines = {};
      SPEC_AXIS_KEYS.forEach((axisKey) => {
        const computed = computeAxisBaselineAndThresholds(readings, axisKey);
        nextBaselines[axisKey] = computed.baseline;
        nextDraft[axisKey] = {
          normalL: computed.normalL,
          normalU: computed.normalU,
          elevL: computed.elevL,
          elevU: computed.elevU,
          sigL: computed.sigL,
          sigU: computed.sigU,
        };
      });

      setBaselineValues(nextBaselines);
      setSpecDraft(nextDraft);
      const row = nextDraft[specForm.axis];
      if (row) {
        setSpecForm((prev) => ({
          ...prev,
          normalL: row.normalL,
          normalU: row.normalU,
          elevL: row.elevL,
          elevU: row.elevU,
          sigL: row.sigL,
          sigU: row.sigU,
        }));
        if (row.normalU != null && row.normalU !== '') {
          setHardwareTriggerMg(String(row.normalU));
        }
      }
      showToast('Baseline run complete. Thresholds recalculated as deviation from baseline for all axes.');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setRunningBaseline(false);
    }
  };

  const executeApplyLogToThreshold = () => {
    const entries = logbook.filter((l) => getLogDateKey(l.timestamp) === selectedLogDate);
    const snapshot = buildLogSnapshotFromEntries(entries);
    setSpecDraft((prev) => ({ ...prev, ...snapshot }));
    const row = snapshot[specForm.axis] || emptySpecDraftRow();
    setSpecForm((prev) => ({
      ...prev,
      normalL: row.normalL ?? '',
      normalU: row.normalU ?? '',
      elevL: row.elevL ?? '',
      elevU: row.elevU ?? '',
      sigL: row.sigL ?? '',
      sigU: row.sigU ?? '',
    }));
    if (row.normalU != null && row.normalU !== '') {
      setHardwareTriggerMg(String(row.normalU));
    }
    showToast('Log thresholds applied to chart settings.');
  };

  const handleApplyLogToThreshold = () => {
    if (!selectedLogDate) return;
    setConfirmModal({ type: 'applyLog' });
  };

  const requestApplySpec = (closeAfter = false) => {
    if (!sensor) return showToast('Select a single sensor first.', 'error');
    const mergedDraft = draftWithSharedNormal(specDraft, specForm);
    try {
      assertThresholdDraftSavable(mergedDraft, specForm.axis, [
        buildSpecDraftFromSensor(sensor),
        specDraft,
      ]);
    } catch (e) {
      return showToast(e.message, 'error');
    }
    setConfirmModal({ type: 'applySpec', closeAfter });
  };

  const executeApplySpec = async (closeAfter = false) => {
    applyFormToDraft();
    const mergedDraft = draftWithSharedNormal(specDraft, specForm);

    const parsedBaselines = {};
    SPEC_AXIS_KEYS.forEach((k) => {
      const v = baselineValues[k];
      if (v !== '' && v != null) parsedBaselines[k] = parseFloat(v);
    });

    try {
      assertThresholdDraftSavable(mergedDraft, specForm.axis, [
        buildSpecDraftFromSensor(sensor),
        specDraft,
      ]);
      for (const axisKey of SPEC_AXIS_KEYS) {
        const row = mergedDraft[axisKey];
        if (isThresholdRowEmpty(row)) continue;
        await bridgeService.updateSpecLimits(selectedBridge._id, sensor._id, {
          axis: axisKey,
          specLimits: draftRowToSpecLimitsPayload(row),
          ...(specForm.baselineStart && { baselineStartDate: specForm.baselineStart }),
          ...(specForm.baselineEnd && { baselineEndDate: specForm.baselineEnd }),
          ...(Object.keys(parsedBaselines).length && { baselineValues: parsedBaselines }),
          baselineScript,
        });
      }
      const did = String(sensor?.sensorSerialNo || '').trim();
      const sid = resolveSensorDataLoggerSerial(selectedBridge, sensor);
      const axisRow = mergedDraft[specForm.axis] || {};
      const signedThreshold = Number(axisRow.normalU ?? hardwareTriggerMg);
      const thresholdValue = Number.isFinite(signedThreshold) ? Math.abs(signedThreshold) : signedThreshold;
      const alertPayload = {
        ...(did ? { did } : {}),
        ...(sid ? { sid } : {}),
        ...(Number.isFinite(thresholdValue) ? { threshold: thresholdValue } : {}),
      };
      if (Object.keys(alertPayload).length) {
        const alertRes = await saveAlertThresholds(
          selectedBridge._id,
          sensor._id,
          alertPayload
        );
        if (alertRes?.success === false) {
          throw new Error(alertRes.message || 'Alert threshold update failed.');
        }
      }
      showToast('Thresholds and baseline settings saved. Live alerts updated.');
      setSpecDraft(mergedDraft);
      setSelectedBridge((prev) => bridgeWithSavedThresholds(prev, sensor._id, mergedDraft));
      const updated = await bridgeService.fetchDetail(selectedBridge._id);
      if (updated) {
        const nextBridge = bridgeWithSavedThresholds(updated, sensor._id, mergedDraft);
        setSelectedBridge(nextBridge);
        const updatedSensor = (nextBridge.sensors || []).find(
          (s) => String(s._id) === String(sensor._id)
        );
        if (updatedSensor) {
          const fromServer = buildSpecDraftFromSensor(updatedSensor);
          const nextDraft = { ...mergedDraft };
          SPEC_AXIS_KEYS.forEach((axisKey) => {
            if (isThresholdRowComplete(fromServer[axisKey])) {
              nextDraft[axisKey] = fromServer[axisKey];
            }
          });
          setSpecDraft(nextDraft);
        }
      }
      await loadSettingsLogbook(selectedBridge._id, sensor._id);
      if (SHOW_RESENSYS_HARDWARE_PANEL) {
        await loadEventTriggerThreshold(selectedBridge._id, sensor._id, sensor);
      }
      if (closeAfter) setSettingsOpen(false);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleConfirmModal = async () => {
    if (!confirmModal || confirmLoading) return;
    if (confirmModal.type === 'applyLog') {
      executeApplyLogToThreshold();
      setConfirmModal(null);
      return;
    }
    if (confirmModal.type === 'applySpec') {
      setConfirmLoading(true);
      try {
        await executeApplySpec(confirmModal.closeAfter);
        setConfirmModal(null);
      } finally {
        setConfirmLoading(false);
      }
      return;
    }
    if (confirmModal.type === 'pushHardwareTrigger') {
      setConfirmLoading(true);
      try {
        await executeSaveHardwareTrigger(true);
        setConfirmModal(null);
      } catch {
        /* toast already shown */
      } finally {
        setConfirmLoading(false);
      }
      return;
    }
    if (confirmModal.type === 'deleteVideo') {
      setConfirmLoading(true);
      try {
        await videoService.deleteVideo(confirmModal.videoId);
        setVideoModal(null);
        showToast('Video deleted.');
        await fetchVideos();
        setConfirmModal(null);
      } catch (e) {
        showToast(e.message || 'Failed to delete video.', 'error');
      } finally {
        setConfirmLoading(false);
      }
    }
  };

  const downloadBaselineScript = () => {
    const blob = new Blob([baselineScript], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `baseline_${sensor?.sensorSerialNo || 'script'}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
    setScriptMenuOpen(false);
  };

  const uploadBaselineScript = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.py,text/plain';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        setBaselineScript(String(reader.result || ''));
        showToast('Baseline script loaded.');
      };
      reader.readAsText(file);
    };
    input.click();
    setScriptMenuOpen(false);
  };

  const focusBaselineScript = () => {
    setScriptEditorOpen(true);
    setScriptMenuOpen(false);
    setTimeout(() => baselineScriptInputRef.current?.focus(), 50);
  };

  const handleDownload = (fmt) => {
    if (!selectedBridge || !hasSensorSelection) return;
    const cfg = DOWNLOAD_FORMATS.find((f) => f.key === fmt) || DOWNLOAD_FORMATS[0];
    setDownloadMenuOpen(false);
    const axisKey = chartAxisView === 'all' ? null : chartAxisView;
    const rows = chartLineReadingsForExport(
      sensorData,
      selectedChartSensorIds,
      selectedChartSensorSerials,
      startDate,
      endDate,
      chartTimeZone,
    ).map((reading) => ({
      ...reading,
      timestamp: burstAnchorTimestamp(reading),
    }));
    if (!rows.length) {
      showToast('No sensor data available to download for the selected filters.', 'error');
      return;
    }
    const filename = `sensor_data_${selectedBridge.name}_${today}.${cfg.ext}`;
    try {
      if (cfg.key === 'csv') {
        triggerFileDownload(
          new Blob([buildExportCsv(rows, chartTimeZone, axisKey)], { type: 'text/csv;charset=utf-8;' }),
          filename,
        );
        return;
      }
      if (cfg.key === 'xl') {
        // Real .xlsx (ZIP) — SpreadsheetML XML with .xlsx extension is rejected by Excel
        triggerFileDownload(buildExportXlsxBlob(rows, chartTimeZone, axisKey), filename);
        return;
      }
      // Word: HTML Word document with .doc extension (not .docx)
      triggerFileDownload(
        new Blob([`\ufeff${buildExportWordHtml(rows, chartTimeZone, axisKey)}`], { type: 'application/msword;charset=utf-8' }),
        filename,
      );
    } catch (e) {
      showToast(e.message || 'Download failed.', 'error');
    }
  };

  const getSelectedSensorObjects = () => {
    if (!selectedBridge?.sensors?.length || !selectedSensors.length) return [];
    return selectedSensors
      .map((idx) => selectedBridge.sensors[idx])
      .filter((s) => s && s._id);
  };

  const openThresholdUploadModal = () => {
    setDownloadMenuOpen(false);
    if (!selectedBridge || !selectedSensors.length) {
      return showToast('Select at least one sensor first.', 'error');
    }
    setThresholdUploadOpen(true);
  };

  const downloadThresholdSampleExcel = () => {
    const targets = getSelectedSensorObjects();
    if (!targets.length) return showToast('Select at least one sensor first.', 'error');
    const sampleSensor = sensor || targets[0];
    const draft = Object.keys(specDraft).length
      ? specDraft
      : buildSpecDraftFromSensor(sampleSensor);
    const wb = buildThresholdSampleWorkbook(draft);
    const filename = targets.length > 1
      ? `threshold_sample_${targets.length}_sensors.xlsx`
      : `threshold_sample_${sampleSensor.sensorSerialNo || 'sensor'}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const applyThresholdDraftFromExcel = async (parsedDraft, file) => {
    const targets = getSelectedSensorObjects();
    if (!selectedBridge || !targets.length) return;
    validateParsedThresholdDraft(parsedDraft);

    // Local settings form is single-sensor only — update it when one sensor is selected
    if (sensor) {
      setSpecDraft(parsedDraft);
      const axisKey = specForm.axis || 'xAxis';
      const row = parsedDraft[axisKey] || emptySpecDraftRow();
      setSpecForm((prev) => ({
        ...prev,
        normalL: row.normalL ?? '',
        normalU: row.normalU ?? '',
        elevL: row.elevL ?? '',
        elevU: row.elevU ?? '',
        sigL: row.sigL ?? '',
        sigU: row.sigU ?? '',
      }));
      if (row.normalU != null && row.normalU !== '') {
        setHardwareTriggerMg(String(row.normalU));
      }
    }

    let savedViaExcelApi = false;
    if (file) {
      try {
        if (targets.length > 1) {
          // Prefer bulk endpoint when multiple sensors are selected
          const fd = new FormData();
          fd.append('file', file);
          targets.forEach((s) => fd.append('sensorIds', s._id));
          await bridgeService.uploadSpecLimitsExcelBulk(selectedBridge._id, fd);
          savedViaExcelApi = true;
        } else {
          const fd = new FormData();
          fd.append('file', file);
          await bridgeService.uploadSpecLimitsExcel(selectedBridge._id, targets[0]._id, fd);
          savedViaExcelApi = true;
        }
      } catch (err) {
        const status = err?.status;
        if (targets.length > 1 && status === 404) {
          // Bulk route missing — fall back to existing per-sensor Excel upload
          try {
            for (const target of targets) {
              const fd = new FormData();
              fd.append('file', file);
              await bridgeService.uploadSpecLimitsExcel(selectedBridge._id, target._id, fd);
            }
            savedViaExcelApi = true;
          } catch (perSensorErr) {
            const perStatus = perSensorErr?.status;
            if (perStatus && perStatus !== 404) throw perSensorErr;
            savedViaExcelApi = false;
          }
        } else if (status && status !== 404) {
          throw err;
        } else {
          savedViaExcelApi = false;
        }
      }
    }

    if (!savedViaExcelApi) {
      for (const target of targets) {
        for (const axis of SPEC_AXIS_KEYS) {
          const axisRow = parsedDraft[axis];
          if (!axisRow) continue;
          await bridgeService.updateSpecLimits(selectedBridge._id, target._id, {
            axis,
            specLimits: draftRowToSpecLimitsPayload(axisRow),
          });
        }
      }
    }

    const updated = await bridgeService.fetchDetail(selectedBridge._id);
    if (updated) setSelectedBridge(updated);
    if (sensor) await loadSettingsLogbook(selectedBridge._id, sensor._id);
  };

  const handleThresholdExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const targets = getSelectedSensorObjects();
    if (!file || !selectedBridge || !targets.length) return;

    const name = (file.name || '').toLowerCase();
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls')
      || /spreadsheet|excel/i.test(file.type || '');
    if (!isExcel) {
      showToast('Please upload an Excel file (.xlsx or .xls) only.', 'error');
      return;
    }

    setThresholdUploading(true);
    try {
      const buffer = await file.arrayBuffer();
      const parsedDraft = parseThresholdWorkbook(buffer);
      validateParsedThresholdDraft(parsedDraft);
      await applyThresholdDraftFromExcel(parsedDraft, file);
      const countLabel = targets.length > 1 ? ` for ${targets.length} sensors` : '';
      showToast(`Thresholds updated from Excel for X, Y, and Z axes${countLabel}.`);
      setThresholdUploadOpen(false);
    } catch (err) {
      showToast(err.message || 'Failed to upload threshold Excel.', 'error');
    } finally {
      setThresholdUploading(false);
    }
  };

  const eventVideoColorCache = useMemo(() => {
    const bridgeSensors = selectedBridge?.sensors || [];
    // Logger selection only filters which rows are shown. Colors stay on the
    // full bridge live data, including a spike shared across loggers.
    const liveReadings = deferredLoggerVideoReadings.length
      ? combineAndAggregateReadings([deferredSensorData, deferredLoggerVideoReadings], startDate, endDate)
      : deferredSensorData;
    const countReadingSensors = (rows) => {
      const ids = new Set();
      (rows || []).forEach((row) => {
        const id = normalizeSocketId(row?.sensorId || row?._id) || String(row?.sensorSerialNo || '').trim();
        if (id) ids.add(id);
      });
      return ids.size;
    };
    // A sensor pick reloads that sensor for the chart. Do not recolor Event
    // Videos from that narrower set while the rest of the bridge is still known.
    const storedReadings = videoColorReadingsRef.current;
    const liveCount = countReadingSensors(liveReadings);
    const storedCount = countReadingSensors(storedReadings);
    const readings = liveCount === 0 || (storedCount > liveCount)
      ? storedReadings
      : liveReadings;
    if (liveCount > 0 && liveCount >= storedCount) {
      videoColorReadingsRef.current = liveReadings;
    }
    const bridgeLoggers = selectedBridge?.dataLoggers || [];
    const thresholdTimelines = buildSensorThresholdTimelines(
      bridgeSensors,
      deferredSensorThresholdLogs,
    );
    let appearances = new Map();
    if (deferredEventVideos.length) {
      try {
        appearances = buildEventVideoAppearanceMap(
          deferredEventVideos,
          readings,
          bridgeSensors,
          bridgeLoggers,
          chartAxisView,
          chartTimeZone,
          thresholdTimelines,
        );
      } catch {
        appearances = new Map();
      }
    }
    return {
      readings,
      sensors: bridgeSensors,
      loggers: bridgeLoggers,
      thresholdTimelines,
      appearances,
    };
  }, [
    selectedBridge,
    deferredSensorData,
    deferredLoggerVideoReadings,
    startDate,
    endDate,
    deferredEventVideos,
    chartAxisView,
    chartTimeZone,
    deferredSensorThresholdLogs,
  ]);

  if (loading) return <Loading message={embedded ? 'Loading sensor data...' : undefined} />;

  const sensors = selectedBridge?.sensors || [];
  const dataLoggers = (selectedBridge?.dataLoggers || []).filter((dl) => dl.isActive !== false);
  const loggerSensors = selectedDataLoggerId
    ? sensors.filter((s) => s.dataLoggerId && String(s.dataLoggerId) === String(selectedDataLoggerId))
    : [];
  const selectedDataLogger = dataLoggers.find(
    (dl) => String(dl._id) === String(selectedDataLoggerId)
  );
  const selectedDataLoggerSerial = selectedDataLogger?.serialNo;
  const loggerSensor = selectedDataLoggerSerial
    ? (sensors.find((s) => String(s.sensorSerialNo) === String(selectedDataLoggerSerial))
      || {
        _id: selectedDataLoggerSerial,
        sensorSerialNo: selectedDataLoggerSerial,
        batteryVoltageLimit: selectedDataLogger?.batteryVoltageLimit,
        batteryNormalVolts: selectedDataLogger?.batteryNormalVolts,
        batteryElevatedVolts: selectedDataLogger?.batteryElevatedVolts,
        batterySignificantVolts: selectedDataLogger?.batterySignificantVolts,
        batteryCriticalVolts: selectedDataLogger?.batteryCriticalVolts,
      })
    : null;
  const filterByLogger = (videos) => (
    selectedDataLoggerSerial
      ? videos.filter((v) => String(v.dataLoggerSerialNo || '').trim() === String(selectedDataLoggerSerial).trim())
      : videos
  );
  // Logger only → all videos for that logger.
  // Logger + sensor (live data) → also filter by Start/End date range.
  // No logger → all bridge event videos.
  const filteredEventVideos = dedupeVideosByLoggerAndTime(
    filterByLogger(eventVideos)
      .filter(isEventVideo)
      .filter((video) => (
        !hasSensorSelection
        || isTimestampInDateRange(video.recordedAt, startDate, endDate)
      )),
  );
  const filteredHealthVideos = dedupeVideosByLoggerAndTime(
    filterByLogger(healthVideos),
  );
  const videoSeverityLoggers = eventVideoColorCache.loggers;
  const videoSeveritySensors = eventVideoColorCache.sensors;
  const videoSeverityReadings = eventVideoColorCache.readings;
  const eventVideoAppearances = eventVideoColorCache.appearances;
  const eventVideoPagination = paginateList(filteredEventVideos, eventVideoPage);
  const healthVideoPagination = paginateList(filteredHealthVideos, healthVideoPage);
  const canDeleteVideo = VIDEO_DELETE_ROLES.includes(user?.role);
  const healthVideoLocked = Boolean(selectedBridge) && !selectedDataLoggerId;
  const liveDataTitle = sensor?.sensorType || selectedSensorType
    ? `${sensor?.sensorType || selectedSensorType} — Live Data`
    : 'Live Data';
  const tempUnitSuffix = temperatureUnit === 'fahrenheit' ? '°F' : '°C';

  return (
    <div className={embedded ? 'sensor-data-embedded' : undefined}>
      {/* Bridge selector — same layout as Dashboard */}
      <div className="dashboard-bridge-toolbar">
        <label>Select Bridge:</label>
        <AppSelect
          value={pendingBridgeId || selectedBridge?._id || ''}
          onChange={(value) => selectBridge(value)}
          placeholder="Select Bridge"
          options={[
            { value: '', label: 'Select Bridge' },
            ...bridges.map((b) => ({ value: b._id, label: b.name })),
          ]}
        />
      </div>

      {selectedBridge && !hideBridgeDetails ? (
        <div className={`dashboard-bridge-details${bridgeDetailsExpanded ? '' : ' is-collapsed'}`}>
          <button
            type="button"
            className="dashboard-bridge-details-title"
            onClick={() => setBridgeDetailsExpanded((prev) => !prev)}
            aria-expanded={bridgeDetailsExpanded}
          >
            <span className="dashboard-bridge-details-title-label">
              <i className="ti ti-info-circle" /> Bridge details — {selectedBridge.name}
            </span>
            <i
              className={`ti ti-chevron-down dashboard-bridge-details-toggle${bridgeDetailsExpanded ? ' is-expanded' : ''}`}
              aria-hidden="true"
            />
          </button>
          {bridgeDetailsExpanded && (
            <>
              <div className="dashboard-location-grid">
                {[
                  ['Country', selectedBridge.country],
                  ['State', selectedBridge.state],
                  ['City', selectedBridge.city],
                  ['Division', selectedBridge.division],
                  ['Bridge Coordinates', formatBridgeCoordinatesDisplay(selectedBridge)],
                  ['ZIP Code', selectedBridge.zipCode],
                ].map(([label, value]) => (
                  <div key={label} className="dashboard-location-item">
                    <label>{label}</label>
                    <span>{value || '—'}</span>
                  </div>
                ))}
              </div>
              <div className="dashboard-staff-grid">
                <BridgeStaffReadonly label="Bridge Admin" people={selectedBridge.admins} />
                <BridgeStaffReadonly label="Bridge Manager" people={selectedBridge.managers} />
                <BridgeStaffReadonly
                  label="Bridge Engineer"
                  people={collectEngineersFromManagers(selectedBridge.managers)}
                />
                <BridgeStaffReadonly label="Bridge Owner" people={selectedBridge.owners} />
              </div>
            </>
          )}
        </div>
      ) : null}

      {/* ── Sensor selection (always visible) ── */}
      <div className="card sensor-data-filters" style={{ marginBottom: 12 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
          <div className="form-group">
            <label>Select Data Logger</label>
            <AppSelect
              value={selectedDataLoggerId}
              disabled={!selectedBridge}
              placeholder="Select Data Logger"
              onChange={(value) => {
                setSelectedDataLoggerId(value);
                setSelectedSensorType('');
                setSelectedSensors([]);
                setSettingsOpen(false);
              }}
              options={[
                { value: '', label: 'Select Data Logger' },
                ...dataLoggers.map((dl) => ({ value: dl._id, label: dl.serialNo })),
              ]}
            />
          </div>
          <div className="form-group">
            <label>Sensor Type</label>
            <AppSelect
              value={selectedSensorType}
              disabled={!selectedBridge || !selectedDataLoggerId}
              placeholder="Select Sensor Type"
              onChange={(value) => {
                setSelectedSensorType(value);
                setSelectedSensors([]);
                setSettingsOpen(false);
              }}
              options={[
                { value: '', label: 'Select Sensor Type' },
                ...[...new Set(loggerSensors.map((s) => s.sensorType).filter(Boolean))]
                  .sort((a, b) => {
                    const order = ['Accelerometer', 'Strain Gauge', 'Deflection Gauge', 'Camera', 'Tiltmeter', 'Pressure gauge'];
                    const ai = order.indexOf(a);
                    const bi = order.indexOf(b);
                    return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
                  })
                  .map((t) => ({
                    value: t,
                    label: t,
                  })),
              ]}
            />
          </div>
          <div className="form-group">
            <label>Select Sensor</label>
            <AppMultiSelect
              disabled={!selectedBridge || !selectedDataLoggerId || !selectedSensorType}
              placeholder="Select sensor(s)"
              values={selectedSensors}
              onChange={(next) => setSelectedSensors(next.map((v) => parseInt(v, 10)))}
              options={sensors
                .map((s, i) => ({ s, i }))
                .filter(({ s }) =>
                  selectedDataLoggerId
                  && selectedSensorType
                  && s.sensorType === selectedSensorType
                  && String(s.dataLoggerId) === String(selectedDataLoggerId)
                )
                .map(({ s, i }) => ({ value: i, label: s.sensorSerialNo }))}
            />
          </div>
          <div className="form-group">
            <label>Sensor Location</label>
            <input
              key={`sensor-loc-${selectedSensorType}-${selectedSensors.join('-')}`}
              type="text"
              readOnly
              value={sensorLocationValue}
              placeholder="Sensor location will appear"
            />
          </div>
        </div>
      </div>

      {/* ── Charts / videos: shown after a bridge is selected ── */}
      {!selectedBridge ? (
        <div style={chartsPlaceholderStyle}>{NO_BRIDGE_MSG}</div>
      ) : (
      <>
      {/* ── Top row: videos after bridge; Live Data after sensor selection ── */}
      <div className={`sensor-data-top-row${hasSensorSelection || showSensorCharts ? '' : ' is-videos-only'}`}>
        {/* Live data — left; only after data logger + sensor type + sensor */}
        {(hasSensorSelection || showSensorCharts) && (
        <div className="sensor-data-split-col">
          <div className="card sensor-data-panel-card">
          <div className="sensor-data-panel-toolbar">
            <span className="sensor-data-panel-title">
              {liveDataTitle}
            </span>
            <div className="sensor-data-panel-actions">
              <div className="sensor-icon-menu" ref={downloadMenuRef}>
                <button
                  type="button"
                  className="btn btn-gray btn-sm sensor-icon-btn"
                  title="Download"
                  onClick={() => { setThresholdUploadOpen(false); setDownloadMenuOpen((o) => !o); }}
                >
                  <i className="ti ti-download" />
                </button>
                {downloadMenuOpen && (
                  <div className="sensor-icon-menu-dropdown">
                    {DOWNLOAD_FORMATS.map((f) => (
                      <button key={f.key} type="button" onClick={() => handleDownload(f.key)}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn btn-gray btn-sm sensor-icon-btn"
                title="Upload threshold Excel"
                onClick={openThresholdUploadModal}
              >
                <i className="ti ti-upload" />
              </button>
              <input
                ref={thresholdUploadInputRef}
                type="file"
                className="sensor-file-input-hidden"
                accept={EXCEL_ACCEPT}
                onChange={handleThresholdExcelUpload}
              />
              <button
                type="button"
                className={`sensor-settings-btn${multiSel ? ' is-frozen' : ''}`}
                title={multiSel ? 'Thresholds (Select a single sensor)' : 'Thresholds'}
                onClick={openSettings}
              >
                <i className="ti ti-settings" />
              </button>
            </div>
          </div>
          {multiSel && (
            <div className="sensor-data-panel-notice">
              <i className="ti ti-info-circle" /> Select a single sensor to adjust thresholds
            </div>
          )}
          {showCriticalLiveBanner && (
            <div className="sensor-data-panel-notice sensor-data-panel-notice--critical">
              <i className="ti ti-alert-triangle" />
              Live severity: <strong>{liveReadingSeverity}</strong>
              {' '}(Significant / critical). Backend notifies bridge Owner, Admins, Managers, and creator via email, SMS, and voice.
            </div>
          )}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:8 }}>
            <label style={{ margin:0, fontSize:11 }}>Start:</label>
            <LiveDataDateInput value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <label style={{ margin:0, fontSize:11 }}>End:</label>
            <LiveDataDateInput value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            <label style={{ margin:0, fontSize:11, marginLeft:4 }}>Select Axis:</label>
            <select
              value={chartAxisView}
              onChange={(e) => setChartAxisView(e.target.value)}
              style={{ fontSize:11, minWidth:100 }}
            >
              {CHART_AXIS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div
            className="sensor-data-chart-block sensor-data-chart-pan"
            ref={chartBlockRef}
            onClick={acknowledgeThresholdOnChart}
            title="Click chart to acknowledge yellow / orange / red threshold alerts"
          >
          {(dataLoading || (hasSensorSelection && !showSensorCharts) || deferredSensorData !== sensorData) && visibleChartSeries.length === 0 ? <Loading message="Fetching sensor data..." /> : (
            visibleChartSeries.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={visibleChartSeries}
                  margin={{ top: liveChartYUnit ? 16 : 5, right:24, bottom: liveChartXAxis.angle ? 8 : 5, left:10 }}
                  onClick={acknowledgeThresholdOnChart}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis
                    dataKey="pointIndex"
                    type="number"
                    scale="linear"
                    allowDecimals
                    domain={getVisibleChartXDomain(
                      accelChartWindowStart,
                      accelChartWindowEnd,
                      chartExtraRightSlots,
                    )}
                    ticks={liveChartXAxis.ticks}
                    interval={0}
                    tick={LiveChartXTick}
                    tickFormatter={liveChartXAxis.labelFor}
                    angle={liveChartXAxis.angle}
                    textAnchor={liveChartXAxis.angle ? 'end' : 'middle'}
                    height={liveChartXAxis.height}
                    tickMargin={liveChartXAxis.angle ? 4 : 2}
                  />
                  <YAxis
                    tick={{ fontSize: 9 }}
                    domain={accelChartYDomain || accelChartYFullDomain || ['auto', 'auto']}
                    ticks={liveAccelYTicks}
                    interval={0}
                    tickFormatter={formatAccelYAxisTick}
                    allowDataOverflow
                    width={accelYAxisWidthFromTicks(activeAccelYDomain, liveAccelYTicks)}
                    tickMargin={4}
                    label={liveChartYUnit ? {
                      value: liveChartYUnit,
                      position: 'top',
                      offset: 2,
                      fontSize: 10,
                      fill: '#555',
                    } : undefined}
                  />
                  <Tooltip content={(tipProps) => (
                    <ChartValueTooltip
                      {...tipProps}
                      timeZone={chartTimeZone}
                      yDomain={activeAccelYDomain}
                    />
                  )} />
                  <Legend
                    wrapperStyle={{ fontSize: 11 }}
                    content={multiSel ? <GroupedMultiSensorLegend /> : undefined}
                  />
                  {visibleAxisThresholdLines.map((line) => (
                    <ReferenceLine
                      key={line.key}
                      y={line.y}
                      stroke={line.stroke}
                      strokeDasharray="5 3"
                      ifOverflow="discard"
                      label={{ value: line.label, fontSize: 9, fill: line.stroke, position: 'center' }}
                    />
                  ))}
                  {showBaselineLine && (
                    <ReferenceLine
                      y={chartBaseline}
                      stroke="#78909c"
                      strokeDasharray="4 4"
                      ifOverflow="discard"
                      label={{ value:'Baseline', fontSize:9, fill:'#78909c' }}
                    />
                  )}
                  {multiSel ? (
                    chartSensorDescriptors.flatMap((sensorDesc) => {
                      const axes = chartAxisView === 'all'
                        ? ['xAxis', 'yAxis', 'zAxis']
                        : [chartAxisView];
                      return axes.map((axis) => {
                        const axisKey = sensorChartFieldKey(sensorDesc.id, axis);
                        return (
                        <Line
                          key={`${sensorDesc.id}-${axis}`}
                          type="monotone"
                          dataKey={axisKey}
                          stroke={sensorDesc.color}
                          strokeDasharray={chartAxisView === 'all' ? MULTI_AXIS_STROKE_DASH[axis] : undefined}
                          dot={(accelSeriesDense ? liveTipDot : chartAxisDot)(
                            sensorDesc.color,
                            accelChartWindowStart,
                            accelChartWindowEnd,
                            getLastDefinedPointIndex(chartSeries, axisKey),
                          )}
                          activeDot={{ r: 4 }}
                          connectNulls
                          strokeWidth={1.5}
                          name={`${sensorDesc.serial} ${SPEC_AXIS_LABELS[axis]}`}
                          isAnimationActive={false}
                        />
                        );
                      });
                    })
                  ) : (
                    <>
                  {showChartLine('xAxis') && (
                    <Line
                      type="monotone"
                      dataKey="xAxis"
                      stroke="#e03030"
                      dot={(accelSeriesDense
                        ? liveTipDot
                        : (chartAxisView === 'all' && !isAccelerometerChart ? chartAxisDot : chartAxisVerticalDot))(
                        '#e03030',
                        accelChartWindowStart,
                        accelChartWindowEnd,
                      )}
                      activeDot={{ r: 4 }}
                      connectNulls
                      strokeWidth={1.5}
                      name="X-Axis"
                      isAnimationActive={false}
                    />
                  )}
                  {showChartLine('yAxis') && (
                    <Line
                      type="monotone"
                      dataKey="yAxis"
                      stroke="#1a6fa8"
                      dot={(accelSeriesDense
                        ? liveTipDot
                        : (chartAxisView === 'all' && !isAccelerometerChart ? chartAxisDot : chartAxisVerticalDot))(
                        '#1a6fa8',
                        accelChartWindowStart,
                        accelChartWindowEnd,
                      )}
                      activeDot={{ r: 4 }}
                      connectNulls
                      strokeWidth={1.5}
                      name="Y-Axis"
                      isAnimationActive={false}
                    />
                  )}
                  {showChartLine('zAxis') && (
                    <Line
                      type="monotone"
                      dataKey="zAxis"
                      stroke="#0f6e56"
                      dot={(accelSeriesDense
                        ? liveTipDot
                        : (chartAxisView === 'all' && !isAccelerometerChart ? chartAxisDot : chartAxisVerticalDot))(
                        '#0f6e56',
                        accelChartWindowStart,
                        accelChartWindowEnd,
                      )}
                      activeDot={{ r: 4 }}
                      connectNulls
                      strokeWidth={1.5}
                      name="Z-Axis"
                      isAnimationActive={false}
                    />
                  )}
                    </>
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="sensor-data-chart-empty">
                No sensor data for selected period.<br />
                <small>Start the simulator or connect hardware to see live data.</small>
              </div>
            )
          )}
          </div>
          </div>
        </div>
        )}

        {/* Event / Health / Alerts video — left until sensor selected, then right */}
        <div className="sensor-data-split-col">
          <div className={`card sensor-data-panel-card${videoTab === 'health' ? ' sensor-data-panel-card--fill' : ''}`}>
            <div className="sensor-data-panel-tabs">
              <button type="button" className={`vtab ${videoTab==='event'?'active':''}`} onClick={()=>setVideoTab('event')}>Event Video</button>
              <span className={`sensor-data-health-tab${healthVideoLocked ? ' is-locked' : ''}`}>
                <button
                  type="button"
                  className={`vtab ${videoTab==='health'?'active':''}`}
                  aria-disabled={healthVideoLocked}
                  onClick={() => {
                    if (healthVideoLocked) return;
                    setVideoTab('health');
                  }}
                >
                  Health Video
                </button>
                {healthVideoLocked && (
                  <span className="sensor-data-health-tab-hint" role="tooltip">
                    {HEALTH_VIDEO_LOGGER_HINT}
                  </span>
                )}
              </span>
              <button type="button" className={`vtab ${videoTab==='alerts'?'active':''}`} onClick={()=>setVideoTab('alerts')}>Field Highlights</button>
            </div>
            <div className="sensor-data-panel-body">
              {videoTab === 'alerts' ? (
                <AlertsVideoPanel
                  bridge={selectedBridge}
                  dataLogger={selectedDataLogger}
                />
              ) : videoLoading ? (
                <Loading message="Loading videos..." />
              ) : videoTab === 'event' ? (
                <>
                  <table className="sensor-video-severity-table" style={{ fontSize:11 }}>
                    <tbody>
                      {eventVideoPagination.items.map((video) => {
                        let rowAppearance = eventVideoAppearances.get(String(video._id));
                        if (!rowAppearance) {
                          try {
                            rowAppearance = eventVideoRowAppearance(
                              video,
                              videoSeverityReadings,
                              videoSeveritySensors,
                              videoSeverityLoggers,
                              chartAxisView,
                              chartTimeZone,
                              eventVideoColorCache.thresholdTimelines,
                            );
                          } catch {
                            rowAppearance = { className: 'sev-yellow' };
                          }
                        }
                        return (
                          <tr
                            key={video._id}
                            className={rowAppearance.className}
                          >
                            <td>
                              <a
                                href="#!"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setVideoModal(video);
                                }}
                                onMouseEnter={() => prefetchVideoOnHover(video)}
                              >
                                {eventVideoListLabel(video)}
                              </a>
                            </td>
                            <td>
                              {formatVideoRecordedAt(video.recordedAt, chartTimeZone)}
                            </td>
                          </tr>
                        );
                      })}
                      {filteredEventVideos.length === 0 && (
                        <tr>
                          <td colSpan="2" style={{ textAlign:'center', color:'#aaa', padding:16 }}>
                            No events in selected period
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <VideoListPagination
                    page={eventVideoPage}
                    totalPages={eventVideoPagination.totalPages}
                    onPageChange={setEventVideoPage}
                  />
                </>
              ) : healthVideoLocked ? (
                <div style={{ textAlign:'center', color:'#aaa', padding:16, fontSize:12 }}>
                  {HEALTH_VIDEO_LOGGER_HINT}
                </div>
              ) : (
                <div className="sensor-data-health-list">
                  <div className="sensor-video-table-wrap">
                    <table
                      className={`sensor-video-table${healthVideoPagination.items.length < VIDEO_PAGE_SIZE ? ' sensor-video-table--natural' : ''}`}
                      style={{ fontSize:11 }}
                    >
                      <tbody>
                        {healthVideoPagination.items.map((video) => (
                          <tr key={video._id}>
                            <td>
                              <a
                                href="#!"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setVideoModal(video);
                                }}
                                onMouseEnter={() => prefetchVideoOnHover(video)}
                              >
                                {video.dataLoggerSerialNo}_health
                              </a>
                            </td>
                            <td>
                              {formatVideoRecordedAt(video.recordedAt, chartTimeZone)}
                            </td>
                          </tr>
                        ))}
                        {filteredHealthVideos.length === 0 && (
                          <tr>
                            <td colSpan="2" style={{ textAlign:'center', color:'#aaa', padding:16 }}>
                              No health videos for selected sensors
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <VideoListPagination
                    page={healthVideoPage}
                    totalPages={healthVideoPagination.totalPages}
                    onPageChange={setHealthVideoPage}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {!(hasSensorSelection || showSensorCharts) && (
          <div className="sensor-data-split-col sensor-data-size-mirror" aria-hidden="true">
            <div className="card sensor-data-panel-card">
              <div className="sensor-data-panel-toolbar">
                <span className="sensor-data-panel-title">Live Data</span>
                <div className="sensor-data-panel-actions">
                  <span className="btn btn-gray btn-sm sensor-icon-btn">
                    <i className="ti ti-download" />
                  </span>
                  <span className="btn btn-gray btn-sm sensor-icon-btn">
                    <i className="ti ti-upload" />
                  </span>
                  <span className="sensor-settings-btn">
                    <i className="ti ti-settings" />
                  </span>
                </div>
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:8 }}>
                <label style={{ margin:0, fontSize:11 }}>Start:</label>
                <LiveDataDateInput value={startDate} onChange={() => {}} />
                <label style={{ margin:0, fontSize:11 }}>End:</label>
                <LiveDataDateInput value={endDate} onChange={() => {}} />
                <label style={{ margin:0, fontSize:11, marginLeft:4 }}>Select Axis:</label>
                <select value={chartAxisView} tabIndex={-1} onChange={() => {}} style={{ fontSize:11, minWidth:100 }}>
                  {CHART_AXIS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="sensor-data-chart-block" />
            </div>
          </div>
        )}
      </div>

      {/* ── Bottom row: Temperature | Humidity | Battery (after sensor selection) ── */}
      {showSensorCharts && (
      <div style={{ display:'grid', gridTemplateColumns:'1fr minmax(120px, 0.5fr) minmax(300px, 340px)', gap:12 }}>
        <div className="card sensor-temp-chart-card" style={{ marginBottom:0 }}>
          <div className="sensor-temp-chart-header">
            <span className="sensor-temp-chart-title">Temperature</span>
            <div className="sensor-temp-unit-toggle" role="group" aria-label="Temperature unit">
              <label
                className={temperatureUnit === 'fahrenheit' ? 'is-active' : ''}
                title="Degree Fahrenheit"
                aria-label="Degree Fahrenheit"
              >
                <input
                  type="radio"
                  name="temperatureUnit"
                  value="fahrenheit"
                  checked={temperatureUnit === 'fahrenheit'}
                  onChange={() => setTemperatureUnit('fahrenheit')}
                />
                °F
              </label>
              <label
                className={temperatureUnit === 'celsius' ? 'is-active' : ''}
                title="Degree Celsius"
                aria-label="Degree Celsius"
              >
                <input
                  type="radio"
                  name="temperatureUnit"
                  value="celsius"
                  checked={temperatureUnit === 'celsius'}
                  onChange={() => setTemperatureUnit('celsius')}
                />
                °C
              </label>
            </div>
          </div>
          <div
            className="sensor-data-chart-block sensor-data-chart-pan sensor-temp-chart-block"
            ref={tempChartBlockRef}
          >
          {visibleTemperatureSeries.length === 0 && (dataLoading || deferredSensorData !== sensorData) ? (
            <Loading message="Fetching sensor data..." />
          ) : visibleTemperatureSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={visibleTemperatureSeries}
                margin={{ top: 14, right: 24, bottom: multiSel ? 52 : 40, left: 14 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="pointIndex"
                  type="number"
                  scale="linear"
                  allowDecimals
                  domain={getVisibleChartXDomain(
                    tempChartWindowStart,
                    tempChartWindowEnd,
                    tempChartExtraRightSlots,
                  )}
                  ticks={tempChartXAxis.ticks}
                  interval={0}
                  tick={LiveChartXTick}
                  tickFormatter={tempChartXAxis.labelFor}
                  angle={tempChartXAxis.angle}
                  textAnchor={tempChartXAxis.angle ? 'end' : 'middle'}
                  height={tempChartXAxis.height + 14}
                  tickMargin={tempChartXAxis.angle ? 4 : 2}
                  axisLine={{ stroke: '#bbb' }}
                  tickLine={{ stroke: '#bbb' }}
                  label={{ value: 'Time', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#666' }}
                />
                <YAxis
                  tick={{ fontSize: 9 }}
                  domain={['auto', 'auto']}
                  axisLine={{ stroke: '#bbb' }}
                  tickLine={{ stroke: '#bbb' }}
                  width={48}
                  label={{
                    value: 'Temperature',
                    angle: -90,
                    position: 'insideLeft',
                    offset: 4,
                    dy: 28,
                    fontSize: 10,
                    fill: '#666',
                  }}
                />
                <Tooltip
                  contentStyle={{ fontSize: 10 }}
                  formatter={(value, name) => [`${Number(value).toFixed(1)} ${tempUnitSuffix}`, name]}
                  labelFormatter={(_, payload) => {
                    const point = payload?.[0]?.payload;
                    return point?.fullTime || formatChartFullTime(point?.timestamp, chartTimeZone) || '';
                  }}
                />
                {multiSel && (
                  <Legend wrapperStyle={{ fontSize: 9, paddingTop: 14 }} />
                )}
                {multiSel ? (
                  chartSensorDescriptors.map((sensorDesc) => {
                    const tempKey = sensorChartFieldKey(sensorDesc.id, 'temperature');
                    return (
                      <Line
                        key={sensorDesc.id}
                        type="monotone"
                        dataKey={tempKey}
                        stroke={sensorDesc.color}
                        dot={(tempSeriesDense ? liveTipDot : tempChartDot)(
                          sensorDesc.color,
                          tempChartWindowStart,
                          tempChartWindowEnd,
                          tempKey,
                          getLastDefinedPointIndex(temperatureSeries, tempKey),
                        )}
                        activeDot={{ r: 4 }}
                        connectNulls
                        strokeWidth={1.5}
                        name={sensorDesc.serial}
                        isAnimationActive={false}
                      />
                    );
                  })
                ) : (
                <Line
                  type="monotone"
                  dataKey="temperature"
                  stroke="#e07020"
                  dot={(tempSeriesDense ? liveTipDot : tempChartDot)(
                    '#e07020',
                    tempChartWindowStart,
                    tempChartWindowEnd,
                  )}
                  activeDot={{ r: 4 }}
                  connectNulls={false}
                  strokeWidth={1.5}
                  name="temperature"
                  isAnimationActive={false}
                />
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="sensor-temp-chart-empty">No temperature data yet</div>
          )}
          </div>
        </div>
        <div className="card" style={{ marginBottom:0 }}>
          <div style={{ fontWeight:700, fontSize:12, marginBottom:8, color:'#1a6fa8' }}>Humidity</div>
          <div style={{ fontSize:11, color:'#aaa', padding:16, textAlign:'center' }}>* No data available</div>
        </div>
        <BatteryVoltageCard
          multiSel={multiSel}
          sensor={sensor}
          dataLogger={selectedDataLogger}
          dataLoggerSerial={selectedDataLoggerSerial}
          loggerSensor={loggerSensor}
          sensorData={sensorData}
          selectedChartSensorIds={selectedChartSensorIds}
          selectedSensorSerials={selectedChartSensorSerials}
          liveReading={
            sensor
              ? (latestSensorData[String(sensor._id)]
                || (sensor.sensorSerialNo ? latestSensorData[sensor.sensorSerialNo] : null))
              : null
          }
          latestByKey={latestSensorData}
          bridgeId={selectedBridge?._id}
          showToast={showToast}
          onLimitSaved={(id, parsed, target, serialNo, bands) => {
            const savedBands = bands || {};
            setSelectedBridge((prev) => {
              if (!prev) return prev;
              if (target === 'logger') {
                const loggerSerial = serialNo || selectedDataLoggerSerial;
                return {
                  ...prev,
                  dataLoggers: (prev.dataLoggers || []).map((dl) => (
                    String(dl.serialNo) === String(loggerSerial)
                    || String(dl._id) === String(id)
                      ? { ...dl, batteryVoltageLimit: parsed, ...savedBands }
                      : dl
                  )),
                  sensors: (prev.sensors || []).map((s) => (
                    String(s.sensorSerialNo) === String(loggerSerial)
                    || String(s._id) === String(id)
                      ? { ...s, batteryVoltageLimit: parsed, ...savedBands }
                      : s
                  )),
                };
              }
              if (!prev.sensors) return prev;
              return {
                ...prev,
                sensors: prev.sensors.map((s) => (
                  String(s._id) === String(id)
                    ? { ...s, batteryVoltageLimit: parsed, ...savedBands }
                    : s
                )),
              };
            });
          }}
        />
      </div>
      )}
      </>
      )}

      {/* Threshold Excel upload modal */}
      {thresholdUploadOpen && selectedSensors.length > 0 && (
        <Modal
          title="Upload Threshold Excel"
          onClose={() => !thresholdUploading && setThresholdUploadOpen(false)}
          width={480}
        >
          <div className="threshold-upload-modal">
            <div className="threshold-upload-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={downloadThresholdSampleExcel}
                disabled={thresholdUploading}
              >
                <i className="ti ti-download" /> Sample Excel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => thresholdUploadInputRef.current?.click()}
                disabled={thresholdUploading}
              >
                <i className="ti ti-upload" />
                {thresholdUploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
            <p className="threshold-upload-hint">
              Accepted formats: .xlsx, .xls only
              {selectedSensors.length > 1
                ? ` — thresholds will apply to all ${selectedSensors.length} selected sensors`
                : ''}
            </p>
          </div>
        </Modal>
      )}

      {/* Video player modal */}
      {videoModal && (
        <VideoPlayerModal
          video={videoModal}
          bridgeId={selectedBridge?._id}
          timeZone={chartTimeZone}
          canDelete={canDeleteVideo}
          onClose={() => setVideoModal(null)}
          onDeleted={(video) => setConfirmModal({ type: 'deleteVideo', videoId: video._id })}
        />
      )}

      {/* Threshold / spec settings modal */}
      {settingsOpen && sensor && (() => {
        const logEntriesForDate = selectedLogDate
          ? logbook.filter((l) => getLogDateKey(l.timestamp) === selectedLogDate)
          : [];
        const logSnapshot = buildLogSnapshotFromEntries(logEntriesForDate);
        const connectionStatus = resolveHardwareBadgeStatus(hardwareStatus);
        const showHardwareStatusLoader = hardwareLoading && !hardwareStatus;
        const latestTime = hardwareStatus?.health?.latestTime
          || hardwareStatus?.health?.latest_time
          || hardwareStatus?.deviceSummary?.latestTime;
        const lastTriggered = hardwareStatus?.lastTriggered
          || hardwareStatus?.last_triggered;
        const currentThresholdFromNormal = SPEC_AXIS_KEYS.includes(specForm.axis)
          ? (isFilledThresholdNumber(specForm.normalU) ? specForm.normalU : null)
          : (SPEC_AXIS_KEYS.map((key) => specDraft?.[key]?.normalU).find(isFilledThresholdNumber) ?? null);
        const headingThreshold = currentThresholdFromNormal != null && currentThresholdFromNormal !== ''
          ? currentThresholdFromNormal
          : eventTriggerThreshold;

        return (
          <Modal
            title={(
              <span className="spec-settings-title">
                <span>{`${sensor.sensorType} — ${sensor.sensorSerialNo}`}</span>
                {SHOW_RESENSYS_HARDWARE_PANEL && (
                  <>
                    <span className="spec-title-current-threshold" aria-live="polite">
                      current threshold-
                      {eventTriggerThresholdLoading && (currentThresholdFromNormal == null || currentThresholdFromNormal === '') ? (
                        <span className="spec-hardware-badge spec-hardware-badge--loading" aria-busy="true">
                          <LoaderSpinner size={12} />
                        </span>
                      ) : (
                        <span className="spec-title-current-threshold-value">
                          {headingThreshold == null || headingThreshold === ''
                            ? '—'
                            : `${headingThreshold} mg`}
                        </span>
                      )}
                    </span>
                    <span className="spec-title-status">
                      {showHardwareStatusLoader ? (
                        <span className="spec-hardware-badge spec-hardware-badge--loading" aria-busy="true">
                          <LoaderSpinner size={12} />
                        </span>
                      ) : (
                        <>
                          <span className={`spec-hardware-badge spec-hardware-badge--${connectionStatus}`}>
                            <span className="spec-hardware-badge-dot" />
                            {HARDWARE_STATUS_LABELS[connectionStatus] || 'Unknown'}
                          </span>
                          <span className="spec-title-status-tooltip" role="tooltip">
                            Latest reading: {formatHardwareTimestamp(latestTime)}
                            <br />
                            Last alert triggered: {formatHardwareTimestamp(lastTriggered)}
                          </span>
                        </>
                      )}
                    </span>
                  </>
                )}
              </span>
            )}
            onClose={() => setSettingsOpen(false)}
            width={920}
            className={[
              'modal--spec-settings',
              SPEC_AXIS_KEYS.includes(specForm.axis) ? '' : 'modal--axis-select',
            ].filter(Boolean).join(' ')}
          >
            <div className="spec-settings-axis-row">
              <label>Select Axis:</label>
              <AppSelect
                value={specForm.axis}
                onChange={onSpecAxisChange}
                placeholder="Select"
                options={[
                  ...(SPEC_AXIS_KEYS.includes(specForm.axis)
                    ? [{ value: '', label: 'Select' }]
                    : []),
                  { value: 'xAxis', label: 'X-Axis' },
                  { value: 'yAxis', label: 'Y-Axis' },
                  { value: 'zAxis', label: 'Z-Axis' },
                ]}
              />
            </div>

            {SPEC_AXIS_KEYS.includes(specForm.axis) && (
            <>
            <div className="spec-settings-layout is-log-unselected">
              <div className="spec-settings-form-col">
                {/* Threshold table — top */}
                <div className="spec-section">
                  <div className="spec-section-title">Threshold (deviation from baseline)</div>
                  <div className="spec-threshold-panel">
                    <div className="spec-threshold-grid spec-threshold-grid--datapoint">
                      <div className="spec-th-cell spec-th-header">Threshold</div>
                      <div className="spec-th-cell spec-th-header spec-th-header--datapoint" aria-label="Data point, positive and negative">
                        <span>Data point</span>
                        <span className="spec-th-plusminus" aria-hidden="true">
                          <span>(</span>
                          <span className="spec-th-plusminus-signs">
                            <span>+</span>
                            <span>−</span>
                          </span>
                          <span>)</span>
                        </span>
                      </div>

                      {[
                        ['Normal', 'normalL', 'normalU', true],
                        ['Elevated', 'elevL', 'elevU', false],
                        ['Significant', 'sigL', 'sigU', false],
                      ].map(([label, lowerKey, upperKey, mandatory]) => (
                        <React.Fragment key={label}>
                          <div className="spec-th-cell spec-th-label">{label}</div>
                          <input
                            className="spec-th-input"
                            type="number"
                            step="any"
                            required={mandatory}
                            aria-required={mandatory}
                            value={specForm[upperKey]}
                            onChange={(e) => handleThresholdDataPointChange(lowerKey, upperKey, e.target.value)}
                            onBlur={(e) => handleThresholdDataPointBlur(lowerKey, upperKey, e.target.value)}
                          />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Baseline — second, separate from threshold table */}
                <div className="spec-section">
                  <div className="spec-section-title">Baseline</div>
                  <div className="spec-baseline-row">
                    <LiveDataDateInput
                      className="spec-baseline-date-wrap"
                      value={specForm.baselineStart}
                      onChange={(e) => setSpecForm((p) => ({ ...p, baselineStart: e.target.value }))}
                      title="Start Date"
                    />
                    <LiveDataDateInput
                      className="spec-baseline-date-wrap"
                      value={specForm.baselineEnd}
                      onChange={(e) => setSpecForm((p) => ({ ...p, baselineEnd: e.target.value }))}
                      title="End Date"
                    />
                    <button
                      type="button"
                      className="spec-run-btn"
                      title="Click here to run"
                      onClick={handleRunBaseline}
                      disabled={runningBaseline}
                    >
                      {runningBaseline ? 'Running...' : 'Run'}
                    </button>
                    <div className="spec-script-menu" ref={scriptMenuRef}>
                      <button
                        type="button"
                        className="spec-script-icon-btn"
                        title="Baseline script and Click for additional functions"
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setScriptMenuOpen(true);
                        }}
                        onClick={() => setScriptMenuOpen((o) => !o)}
                      >
                        <i className="ti ti-file-text" />
                      </button>
                      {scriptMenuOpen && (
                        <div className="spec-script-dropdown">
                          <button type="button" onClick={downloadBaselineScript}>Download</button>
                          <button type="button" onClick={uploadBaselineScript}>Upload</button>
                          <button type="button" onClick={focusBaselineScript}>Modify</button>
                        </div>
                      )}
                    </div>
                  </div>
                  {scriptEditorOpen && (
                    <textarea
                      ref={baselineScriptInputRef}
                      className="spec-script-textarea is-editing"
                      value={baselineScript}
                      onChange={(e) => setBaselineScript(e.target.value)}
                      spellCheck={false}
                      placeholder="Baseline calculation script..."
                    />
                  )}
                </div>

                <div className="spec-log-section">
                  <div className="spec-section-title">Log</div>
                  <div className="spec-log-row">
                    <ul className="spec-log-date-list">
                      {logDates.length > 0 ? logDates.map((d) => (
                        <li key={d}>
                          <button
                            type="button"
                            className={selectedLogDate === d ? 'is-active' : ''}
                            onClick={() => setSelectedLogDate(d)}
                          >
                            {formatLogDateLabel(d)}
                          </button>
                        </li>
                      )) : (
                        <li className="spec-log-empty">No log dates yet</li>
                      )}
                    </ul>
                    {selectedLogDate && (
                    <div className="spec-settings-table-col">
                      <div className="spec-summary-table-wrap">
                        <table className="spec-summary-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Axis</th>
                              <th>N_L</th>
                              <th>N_U</th>
                              <th>E_L</th>
                              <th>E_U</th>
                              <th>S_L</th>
                              <th>S_U</th>
                            </tr>
                          </thead>
                          <tbody>
                            {SPEC_AXIS_KEYS.map((axisKey, idx) => {
                              const row = logSnapshot[axisKey] || emptySpecDraftRow();
                              return (
                                <tr key={axisKey} className={specForm.axis === axisKey ? 'is-active-row' : ''}>
                                  {idx === 0 && (
                                    <td rowSpan={3} className="spec-date-cell">{formatLogDateLabel(selectedLogDate)}</td>
                                  )}
                                  <td>{SPEC_AXIS_LABELS[axisKey]}</td>
                                  <td>{row.normalL !== '' && row.normalL != null ? row.normalL : '—'}</td>
                                  <td>{row.normalU !== '' && row.normalU != null ? row.normalU : '—'}</td>
                                  <td>{row.elevL !== '' && row.elevL != null ? row.elevL : '—'}</td>
                                  <td>{row.elevU !== '' && row.elevU != null ? row.elevU : '—'}</td>
                                  <td>{row.sigL !== '' && row.sigL != null ? row.sigL : '—'}</td>
                                  <td>{row.sigU !== '' && row.sigU != null ? row.sigU : '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <button
                        type="button"
                        className="spec-apply-btn"
                        onClick={handleApplyLogToThreshold}
                        title="Apply log thresholds to chart settings"
                      >
                        Apply
                      </button>
                      <p className="spec-apply-hint">
                        Apply loads the selected log into Threshold (deviation from baseline).
                      </p>
                    </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="spec-settings-footer">
              <button
                type="button"
                className="spec-save-continue-btn"
                onClick={() => requestApplySpec(true)}
              >
                Save and Continue
              </button>
            </div>
            </>
            )}
          </Modal>
        );
      })()}

      {confirmModal && (
        <ConfirmModal
          title={
            confirmModal.type === 'applySpec'
              ? 'Save Thresholds'
              : confirmModal.type === 'applyLog'
                ? 'Apply to Chart'
                : confirmModal.type === 'pushHardwareTrigger'
                  ? 'Push Hardware Trigger'
                  : 'Delete Video'
          }
          message={
            confirmModal.type === 'applySpec' ? (
              <>
                <strong>Warning:</strong> This will permanently update live alert thresholds for this sensor.
                <br /><br />
                Thresholds are evaluated as deviation from the baseline (not from zero).
                <br /><br />
                Exceeding the <strong>Significant</strong> band triggers critical alerts (email, SMS, and voice)
                to the bridge Owner, Admins, Managers, and creator.
                <br /><br />
                Do you want to continue?
              </>
            ) : confirmModal.type === 'applyLog' ? (
              'Changes will apply on the current chart. Are you sure you want to continue?'
            ) : confirmModal.type === 'pushHardwareTrigger' ? (
              <>
                This updates the physical sensor event trigger to{' '}
                <strong>{hardwareTriggerMg} mg</strong> via Resensys
                {sensor?.sensorSerialNo ? (
                  <> (DID <code>{sensor.sensorSerialNo}</code>)</>
                ) : null}
                .
                <br /><br />
                Applies on the next gateway contact. Per-axis SHM bands are not sent to the device.
                <br /><br />
                Do you want to continue?
              </>
            ) : (
              'Deleting will remove the video permanently!'
            )
          }
          confirmLabel={
            confirmModal.type === 'deleteVideo'
              ? 'Delete'
              : confirmModal.type === 'pushHardwareTrigger'
                ? 'Push to device'
                : 'Continue'
          }
          cancelLabel="Cancel"
          variant={confirmModal.type === 'deleteVideo' ? 'danger' : 'primary'}
          loading={confirmLoading}
          onConfirm={handleConfirmModal}
          onCancel={() => !confirmLoading && setConfirmModal(null)}
        />
      )}

      {bridgeSwitchLoading && createPortal(
        <div className="sensor-data-page-overlay" role="status" aria-live="polite" aria-busy="true">
          <Loading />
        </div>,
        document.body
      )}

    </div>
  );
}
